import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
// Note: html2canvas/dom-to-image have CSS rendering issues; using Playwright server-side instead
import { Tendv2ContentItem, tendApi, OverlayTemplateConfig, OverlayTemplateResponse, videoRenderApi, carouselApi, CarouselSlide, SlideCreateRequest, PlacedDesignElement, DesignElementLibraryItem, MAX_DESIGN_ELEMENTS_PER_OVERLAY, getPlatformCharLimits, PlatformCharLimits } from '../../api/tendApi';
import LogoSelector, { LogoSelectorHandle } from '../brand/LogoSelector';
import { Logo } from '../../api/logoApi';
import SmartPublishButton, { normalizePlatform } from './SmartPublishButton';
import TendCardMetricsRow from './TendCardMetricsRow';
import PublishedMediaViewer from './PublishedMediaViewer';
import { usePublishStatus } from '../../contexts/PublishStatusContext';
import { useBrandPostMetrics } from '../../contexts/PostMetricsContext';
import DraggablePreviewPanel from './DraggablePreviewPanel';
import { CHANNEL_INFO } from '../../utils/editorialCadence';
import {
  describePublishFailure, publishFailureHeadState, publishFailureCodeTag, publishFailureDetailLine, PUBLISH_FAILURE_STYLE,
} from '../../utils/publishFailure';
import { placementsFor } from '../../utils/placements';
import PlacementControl from './PlacementControl';
import RedditDestinationControl from './RedditDestinationControl';
import TendModal from '../brand/TendModal';
import { useAuth } from '../../AuthContext';
import TemplateManagementModal from './TemplateManagementModal';
import ColorPickerPopover from './ColorPickerPopover';
import ElementFrame from './overlay-controls/ElementFrame';
import ElementToolbar, { TextToolbarValue } from './overlay-controls/ElementToolbar';
import { useOverlaySelection } from './overlay-controls/useOverlaySelection';
import { useElementMode } from './overlay-controls/useElementMode';
import { useArrowNudge } from './overlay-controls/useArrowNudge';
import SafeZoneLayer from './overlay-controls/SafeZoneLayer';
import { resolveSafeZone } from '../../utils/safeZones';
import { effectiveGap, SetElementKind, stackBoxStyle, stackColumnStyle, stackKindsForRow, stackMargins } from '../../utils/overlayStack';
import { useBrandPalette } from '../../contexts/BrandPaletteContext';
import AnchoredStripPopover from './overlay-controls/AnchoredStripPopover';
import TacticEditModal from './TacticEditModal';
import FeedbackPanel from './FeedbackPanel';
import { useFeedbackCounts, feedbackCacheKey } from '../../contexts/FeedbackCountsContext';
import TextWidthHandles from './overlay-controls/TextWidthHandles';
import CornerScaleHandle from './overlay-controls/CornerScaleHandle';
import DesignElementsPicker from './design-elements/DesignElementsPicker';
import PlaceableDesignElement from './design-elements/PlaceableDesignElement';
import { splitDesignElementsByDepth } from '../../utils/designElementLayers';
import { clampPlacement, placedElementCenterPx, deriveAnchoredOffsets } from './design-elements/designElementPosition';
import { useVideoRecorder } from './useVideoRecorder';
import { useMediaSelection } from './useMediaSelection';
import { croppedAxisFor, patchSlide, resolveSlideMedia, withPhotoOnEverySlide } from './slideMedia';
import { LegState, legNote } from './publishLegState';
import { RecordingTeleprompter } from './RecordingTeleprompter';
import { useTakeFields } from '../../hooks/useTakeFields';
import { takeState } from '../../utils/takeState';
import { announcePoolChange, onPoolChange } from '../../utils/mediaPool';
import TendMediaControl from './TendMediaControl';
import MediaGalleryModal from '../media/MediaGalleryModal';
import { CornerControlsProvider, CornerControlId } from '../media/useCornerControls';
import { buildTextStyle, buildBorderStyle, backdropBlurCss, TextCase } from './overlay-controls/textStyleUtils';
import { sanitizeOverlayHtml, hasVisibleText, emptyIfBlank } from '../../utils/sanitizeOverlayHtml';
import { usePersistedText } from './usePersistedText';
import { usePersistedValue } from './usePersistedValue';
import { useFontLibrary } from '../../contexts/FontLibraryContext';
import { resolveElementFontFamily } from '../../utils/fontResolution';
import { OVERLAY_REFERENCE_WIDTH, OVERLAY_CONSTANTS, getScaledOverlayStyles } from '../../hooks/useOverlayScale';
import { PexelsMediaItem } from '../../api/pexelsApi';
import { PexelsMediaPicker } from './PexelsMediaPicker';
import { REMOTION_SERVICE_URL } from '../../config/api';
import { PlatformIcon } from './PlatformIcon';
import { ArrowsPointingInIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import {
  AspectBucket,
  BUCKET_CSS,
  asAspectBucket,
  resolveChannelAspect,
  BUCKET_DIMENSIONS,
} from '../../utils/channelAspect';
import { AspectGlyph, AspectRatioMenuPopover, AspectAnchorRect } from './AspectRatioMenu';
// Note: RemotionVideoPreview is available for future use when client-side Remotion preview is needed
// Currently using CSS overlay preview with Remotion backend rendering
// import RemotionVideoPreview from './RemotionVideoPreview';

// Word count as the server computes it (len(text.split()))
const countWords = (text: string): number => text.split(/\s+/).filter((w) => w.length > 0).length;
// Hashtags arrive as a JSON string or a list
const parseHashtags = (raw: unknown): string[] =>
  !raw ? [] : typeof raw === 'string' ? JSON.parse(raw) : (raw as string[]);

// Custom hook for debouncing
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

// Logo display mode union and runtime guard.
// `OverlayTemplateConfig.logoDisplayMode` is typed as plain `string`, so we
// narrow with this guard before feeding it into setLogoDisplayMode.
type LogoDisplayMode = 'icon_only' | 'name_only' | 'icon_and_name';
const isLogoDisplayMode = (v: unknown): v is LogoDisplayMode =>
  v === 'icon_only' || v === 'name_only' || v === 'icon_and_name';

// Per-text-element extended styling: emphasis, case, per-box border, vertical offset, width.
// Grouped into one state object per element to keep config flow tractable.
// Hoisted to module scope so React hook deps don't have to track it.
type TextElementStyle = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  // Drop shadow behind text. Defaults ON — publish always applied it before
  // this was a setting, so absent config keys must keep the legacy look.
  shadow: boolean;
  textCase: TextCase;
  borderEnabled: boolean;
  borderColor: string;
  borderThickness: number; // px at ref width 360
  borderRadius: number;    // px at ref width 360
  gap: number | null;      // visible space below to the next element, ref px; null = pair default
  width: number;           // percentage 0-100 of overlay padded area
  widthOffset: number;     // percentage 0-100, distance from anchor edge
  // Font Library family slug; null = inherit up the cascade
  // (template → brand default → universal default → Inter).
  fontFamily: string | null;
  letterSpacing: number;      // integer percent, 0 = default
  lineHeight: number | null;  // unitless multiplier, null = Auto (font default)
};
const DEFAULT_TEXT_ELEMENT_STYLE: TextElementStyle = {
  bold: false, italic: false, underline: false, strikethrough: false, shadow: true, textCase: 'none',
  borderEnabled: false, borderColor: '#ffffff', borderThickness: 0, borderRadius: 8,
  gap: null, width: 100, widthOffset: 0, fontFamily: null,
  letterSpacing: 0, lineHeight: null,
};
// Title defaults to bold (matches the legacy implicit semibold). Subheader/body
// default to regular weight. User can toggle either way.
const defaultBoldFor = (prefix: 'title' | 'subheader' | 'body') => prefix === 'title';

const hydrateTextElementStyle = (config: Partial<OverlayTemplateConfig>, prefix: 'title' | 'subheader' | 'body'): TextElementStyle => ({
  bold: (config as any)[`${prefix}Bold`] ?? defaultBoldFor(prefix),
  italic: (config as any)[`${prefix}Italic`] ?? false,
  underline: (config as any)[`${prefix}Underline`] ?? false,
  strikethrough: (config as any)[`${prefix}Strikethrough`] ?? false,
  shadow: (config as any)[`${prefix}Shadow`] ?? true,
  textCase: (config as any)[`${prefix}TextCase`] ?? 'none',
  borderEnabled: (config as any)[`${prefix}BorderEnabled`] ?? false,
  borderColor: (config as any)[`${prefix}BorderColor`] ?? '#ffffff',
  borderThickness: (config as any)[`${prefix}BorderThickness`] ?? 0,
  borderRadius: (config as any)[`${prefix}BorderRadius`] ?? 8,
  gap: (config as any)[`${prefix}Gap`] ?? null,
  width: (config as any)[`${prefix}Width`] ?? 100,
  widthOffset: (config as any)[`${prefix}WidthOffset`] ?? 0,
  fontFamily: (config as any)[`${prefix}FontFamily`] ?? null,
  letterSpacing: (config as any)[`${prefix}LetterSpacing`] ?? 0,
  lineHeight: (config as any)[`${prefix}LineHeight`] ?? null,
});

// Platform display configuration
// The canvas is always a fixed-aspect box now, driven by the content's stamped
// aspect_ratio (brand/universal resolved fallback) — the old per-platform
// 'natural' sizing is gone. 'natural'/'video' stay in the type only so
// historical comparisons keep compiling (they are statically dead).
type ImageMode = 'fixed' | 'natural' | 'video';

interface PlatformConfig {
  color: string;
  imageMode: ImageMode;
  aspectRatio?: string; // CSS aspect-ratio value (e.g., '1/1', '4/5')
}

const PLATFORM_COLORS: Record<string, string> = {
  twitter: '#1DA1F2',
  bluesky: '#0085FF',
  instagram: '#E4405F',
  threads: '#000000',
  pinterest: '#BD081C',
  linkedin: '#0A66C2',
  facebook: '#1877F2',
  reddit: '#FF4500',
  quora: '#B92B27',
  newsletter: '#6B7280',
  tiktok: '#000000',
  youtube_shorts: '#FF0000',
  snapchat: '#FFFC00',
};

// Platform character limits — for the POST TEXT (the body/caption/description
// field the primary text publishes into). Post Title limits are separate
// (PLATFORM_TITLE_LIMITS below); YouTube's description allows 5,000 — the
// old 100 here was the TITLE limit misapplied, blocking legitimate posts.
const PLATFORM_CHAR_LIMITS: Record<string, number> = {
  bluesky: 300,
  twitter: 280,
  threads: 500,
  instagram: 2200,
  facebook: 63206,
  linkedin: 3000,
  pinterest: 500,
  reddit: 40000,
  tiktok: 2200,
  youtube_shorts: 5000,
  snapchat: 250,
  quora: 100000,
  newsletter: 100000,
};

// The served copy of that table (+ the short-link length) — canonical source
// is the backend's PLATFORM_CHAR_LIMITS, the same numbers the queue holds on.
// One fetch per session, shared by every card; the static dict above is the
// offline fallback. The published post carries base/slug, not the article
// URL, so the counter must count the short link's length, not source_url's.
const STATIC_CHAR_LIMITS: PlatformCharLimits = {
  limits: PLATFORM_CHAR_LIMITS,
  default_limit: 10000,
  short_link_length: null,
};
let _servedCharLimits: PlatformCharLimits = STATIC_CHAR_LIMITS;
const _charLimitListeners = new Set<(v: PlatformCharLimits) => void>();
let _charLimitsRequested = false;

function usePlatformCharLimits(): PlatformCharLimits {
  const [value, setValue] = useState<PlatformCharLimits>(_servedCharLimits);
  useEffect(() => {
    _charLimitListeners.add(setValue);
    if (!_charLimitsRequested) {
      _charLimitsRequested = true;
      getPlatformCharLimits(STATIC_CHAR_LIMITS).then((served) => {
        _servedCharLimits = served;
        _charLimitListeners.forEach((fn) => fn(served));
      });
    } else if (value !== _servedCharLimits) {
      setValue(_servedCharLimits);
    }
    return () => { _charLimitListeners.delete(setValue); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return value;
}

// Post Title limits — what the Ayrshare adapter truncates titles to at
// publish (youTubeOptions/pinterestOptions [:100], redditOptions [:300]).
// Over-limit is a warning, not a block: the backend truncates safely.
const PLATFORM_TITLE_LIMITS: Record<string, number> = {
  youtube_shorts: 100,
  pinterest: 100,
  reddit: 300,
};

// Per-channel card layout, mirroring how each platform renders a published
// post: `flip` puts the primary text ABOVE the media (text-first platforms);
// `clampLines` is the idle line clamp on primary text (matched to each
// platform's real feed truncation — e.g. linkedin's 3-line "...see more");
// `titlePos` places the independently-editable Post Title for the channels
// that publish one.
type TitlePos = 'none' | 'top' | 'below_media';
interface PlatformLayout { clampLines: number; flip: boolean; titlePos: TitlePos }
const PLATFORM_LAYOUT: Record<string, PlatformLayout> = {
  tiktok:         { clampLines: 2, flip: false, titlePos: 'none' },
  facebook:       { clampLines: 4, flip: true,  titlePos: 'none' },
  twitter:        { clampLines: 6, flip: true,  titlePos: 'none' },
  bluesky:        { clampLines: 8, flip: true,  titlePos: 'none' },
  threads:        { clampLines: 4, flip: true,  titlePos: 'none' },
  instagram:      { clampLines: 2, flip: false, titlePos: 'none' },
  youtube_shorts: { clampLines: 2, flip: false, titlePos: 'below_media' },
  pinterest:      { clampLines: 2, flip: false, titlePos: 'below_media' },
  linkedin:       { clampLines: 3, flip: true,  titlePos: 'none' },
  reddit:         { clampLines: 4, flip: false, titlePos: 'top' },
  snapchat:       { clampLines: 2, flip: false, titlePos: 'none' },
  quora:          { clampLines: 3, flip: true,  titlePos: 'top' }, // title, text, media
  newsletter:     { clampLines: 8, flip: false, titlePos: 'none' },
};
const DEFAULT_LAYOUT: PlatformLayout = { clampLines: 4, flip: false, titlePos: 'none' };

// First-comment routing is verified on these channels only (LinkedIn's
// spec line bakes the URL into the body; Instagram unresolved) — the card
// shows the toggle just where publish can honor it.
const FIRST_COMMENT_CHANNELS = new Set(['facebook', 'twitter']);

// Toggle component with size variants
const Toggle = ({
  checked,
  onChange,
  size = 'md',
  disabled = false,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  size?: 'sm' | 'md';
  /** The record of a published post: the state shows, nothing flips. */
  disabled?: boolean;
}) => {
  const sizes = {
    sm: { track: 'w-7 h-4', thumb: 'w-3 h-3', translate: 'translate-x-3' },
    md: { track: 'w-9 h-5', thumb: 'w-4 h-4', translate: 'translate-x-4' }
  };
  const s = sizes[size];

  return (
    <button
      type="button"
      aria-disabled={disabled || undefined}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); if (!disabled) onChange(!checked); }}
      className={`relative ${s.track} rounded-full transition-colors ${checked ? 'bg-blue-500' : 'bg-gray-300'
        } ${disabled ? 'cursor-default opacity-70' : ''}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 ${s.thumb} bg-white rounded-full shadow transition-transform ${checked ? s.translate : ''
          }`}
      />
    </button>
  );
};

// (LinkCallout retired 2026-08-10: link + hashtags now share one compact
// control row in the card body with dropdown editors — see the
// "Link + Tags control row" block in the card render.)

// Photo Attribution Callout component (compact callout pattern)
const PhotoAttributionCallout = ({
  attribution,
  enabled,
  onToggle,
  onAttributionChange,
}: {
  attribution: string;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  onAttributionChange?: (text: string) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onAttributionChange) {
      onAttributionChange(e.target.value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      setIsEditing(false);
      inputRef.current?.blur();
    }
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  return (
    <div className="relative border border-purple-200 rounded-lg overflow-hidden bg-purple-50/50">
      <div className="flex items-center justify-between p-2">
        <div className="flex-1 min-w-0 flex items-center gap-2">
          {/* Pexels camera icon */}
          <svg className="w-4 h-4 text-purple-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={attribution}
              onChange={handleInput}
              onBlur={() => setIsEditing(false)}
              onKeyDown={handleKeyDown}
              className="flex-1 text-sm bg-white border border-purple-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          ) : (
            <span
              className="text-sm text-purple-700 truncate cursor-default"
              title={attribution}
            >
              {attribution}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 ml-2">
          {!isEditing && (
            <span className="text-[10px] text-purple-400">{attribution.length}</span>
          )}
          {!isEditing && onAttributionChange && (
            <button
              onClick={() => setIsEditing(true)}
              className="p-1 text-purple-400 hover:text-purple-600 hover:bg-purple-100 rounded transition-colors"
              title="Edit attribution"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          )}
          <Toggle checked={enabled} onChange={onToggle} size="sm" />
        </div>
      </div>
    </div>
  );
};

// Editable Hashtag Pill component
const HashtagPill = ({
  tag,
  onEdit,
  onDelete,
  autoFocus = false,
}: {
  tag: string;
  onEdit: (newTag: string) => void;
  onDelete: () => void;
  autoFocus?: boolean;
}) => {
  const [isEditing, setIsEditing] = useState(autoFocus);
  const [editValue, setEditValue] = useState(tag);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Sync editValue when tag prop changes
  useEffect(() => {
    setEditValue(tag);
  }, [tag]);

  const handleSave = () => {
    setIsEditing(false);
    // Strip leading #, spaces, and special characters (keep only alphanumeric and underscores)
    const cleaned = editValue.trim().replace(/^#+/, '').replace(/[^a-zA-Z0-9_]/g, '');
    if (cleaned) {
      onEdit(cleaned);
    } else {
      onDelete();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(tag);
      setIsEditing(false);
    }
  };

  const displayTag = tag.startsWith('#') ? tag : `#${tag}`;

  return (
    <div className={`group inline-flex items-center rounded-full px-2 py-0.5 gap-1 ${
      isEditing
        ? 'bg-blue-100 border-2 border-blue-400'
        : 'bg-blue-50 border border-blue-200'
    }`}>
      {isEditing ? (
        <>
          <span className="text-xs text-blue-600">#</span>
          <input
            ref={inputRef}
            type="text"
            value={editValue.replace(/^#+/, '')}
            onChange={(e) => setEditValue(e.target.value.replace(/^#+/, ''))}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className="text-xs text-blue-600 bg-transparent border-none outline-none focus:outline-none focus:ring-0"
            style={{ width: `${Math.max((editValue.replace(/^#+/, '').length || 1) * 0.6 + 2, 4)}em` }}
          />
        </>
      ) : (
        <>
          <span
            onClick={() => setIsEditing(true)}
            className="text-xs text-blue-600 cursor-pointer"
          >
            {displayTag}
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="text-blue-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Remove hashtag"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
};

// Hashtags List component with embedded toggle and editable pills
const HashtagsList = ({
  hashtags,
  enabled,
  onToggle,
  onHashtagsChange,
  hideToggle = false,
}: {
  hashtags: string[];
  enabled: boolean;
  onToggle: (v: boolean) => void;
  onHashtagsChange?: (newHashtags: string[]) => void;
  hideToggle?: boolean;
}) => {
  const [newTagIndex, setNewTagIndex] = useState<number | null>(null);

  const handleEdit = (index: number, newTag: string) => {
    if (!onHashtagsChange) return;
    // Check for duplicates (case-insensitive, excluding current index)
    const isDuplicate = hashtags.some((tag, i) =>
      i !== index && tag.toLowerCase() === newTag.toLowerCase()
    );
    if (isDuplicate) {
      // If duplicate, just delete this one
      onHashtagsChange(hashtags.filter((_, i) => i !== index));
    } else {
      const updated = [...hashtags];
      updated[index] = newTag;
      onHashtagsChange(updated);
    }
    setNewTagIndex(null);
  };

  const handleDelete = (index: number) => {
    if (!onHashtagsChange) return;
    onHashtagsChange(hashtags.filter((_, i) => i !== index));
    setNewTagIndex(null);
  };

  const handleAdd = () => {
    if (!onHashtagsChange) return;
    const newIndex = hashtags.length;
    onHashtagsChange([...hashtags, '']);
    setNewTagIndex(newIndex);
  };

  const isEmpty = hashtags.length === 0;

  return (
    <div className="relative tend-card-pill-frost rounded-lg overflow-hidden">
      {/* Toggle in top-right corner (hidden when the combined Link/Tags
          control row owns the toggle) */}
      {!hideToggle && (
        <div className="absolute top-1.5 right-1.5 z-10">
          <Toggle checked={enabled} onChange={onToggle} size="sm" />
        </div>
      )}

      <div className={`p-2 ${hideToggle ? '' : 'pr-10'}`}>
        {isEmpty ? (
          /* Empty state: show label and add button */
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Hashtags</span>
            {onHashtagsChange && (
              <button
                onClick={handleAdd}
                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500 hover:text-gray-700 transition-colors"
                title="Add hashtag"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            )}
          </div>
        ) : (
          /* Non-empty state: show pills and add button */
          <div className="flex flex-wrap items-center gap-1.5">
            {hashtags.map((tag, idx) => (
              <HashtagPill
                key={`${idx}-${tag}`}
                tag={tag}
                onEdit={(newTag) => handleEdit(idx, newTag)}
                onDelete={() => handleDelete(idx)}
                autoFocus={idx === newTagIndex}
              />
            ))}
            {onHashtagsChange && (
              <button
                onClick={handleAdd}
                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500 hover:text-gray-700 transition-colors"
                title="Add hashtag"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// (OverlayTemplate interface removed with template swipe-to-switch — the
//  fetched templates live untyped in overlayTemplatesRef for slide sync.)

// Stacked Carousel Control Bar component
interface CarouselControlBarProps {
  slides: CarouselSlide[];
  currentIndex: number;
  onNavigate: (index: number) => void;
  onDelete: (slideId: number) => void;
  onAdd: () => void;
  onReorder: (slideIds: number[]) => void;
}

const CarouselControlBar: React.FC<CarouselControlBarProps> = ({
  slides,
  currentIndex,
  onNavigate,
  onDelete,
  onAdd,
  onReorder,
}) => {
  // Visual order during drag - items shift in real-time
  const [visualOrder, setVisualOrder] = useState<number[]>(() => slides.map(s => s.id));
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const longHoldTimerRef = useRef<NodeJS.Timeout | null>(null);
  const didInitiateDragRef = useRef(false);
  const LONG_HOLD_MS = 400;

  // Sync visualOrder when slides change (but not during drag)
  React.useEffect(() => {
    if (draggedId === null) {
      setVisualOrder(slides.map(s => s.id));
    }
  }, [slides, draggedId]);

  const clearLongHold = () => {
    if (longHoldTimerRef.current) {
      clearTimeout(longHoldTimerRef.current);
      longHoldTimerRef.current = null;
    }
  };

  const handlePointerDown = (e: React.PointerEvent, slideId: number) => {
    didInitiateDragRef.current = false;

    longHoldTimerRef.current = setTimeout(() => {
      didInitiateDragRef.current = true;
      setDraggedId(slideId);
      if (navigator.vibrate) navigator.vibrate(50);
    }, LONG_HOLD_MS);
  };

  const handlePointerUp = (slideId: number) => {
    clearLongHold();

    // If we were dragging, commit the reorder
    if (draggedId !== null) {
      const originalOrder = slides.map(s => s.id);
      if (JSON.stringify(visualOrder) !== JSON.stringify(originalOrder)) {
        onReorder(visualOrder);
      }
      setDraggedId(null);
      return;
    }

    // If we didn't drag, navigate
    if (!didInitiateDragRef.current) {
      const index = slides.findIndex(s => s.id === slideId);
      if (index !== -1) onNavigate(index);
    }
  };

  const handlePointerEnter = (targetId: number) => {
    if (draggedId === null || draggedId === targetId) return;

    // Shift items: remove dragged from current position, insert at target position
    setVisualOrder(prev => {
      const newOrder = prev.filter(id => id !== draggedId);
      const targetIdx = prev.indexOf(targetId);
      // Insert dragged item at target position
      newOrder.splice(targetIdx, 0, draggedId);
      return newOrder;
    });
  };

  // Global pointer up
  React.useEffect(() => {
    const handleGlobalPointerUp = () => {
      if (draggedId !== null) {
        const originalOrder = slides.map(s => s.id);
        if (JSON.stringify(visualOrder) !== JSON.stringify(originalOrder)) {
          onReorder(visualOrder);
        }
        setDraggedId(null);
      }
      clearLongHold();
    };

    window.addEventListener('pointerup', handleGlobalPointerUp);
    return () => window.removeEventListener('pointerup', handleGlobalPointerUp);
  }, [draggedId, visualOrder, slides, onReorder]);

  // Map visual order to slides for rendering
  const orderedItems = visualOrder
    .map(id => slides.find(s => s.id === id))
    .filter(Boolean) as CarouselSlide[];

  // Beyond 10 slides the numbered 28px pills overflow the card — switch to
  // compact dots with a position counter (platforms like TikTok allow up to 35).
  const compact = orderedItems.length > 10;

  return (
    <div className="flex items-center justify-between gap-1.5 mt-3 px-2 py-1.5 bg-gray-800 rounded-lg">
      {/* Slide numbers/dots - click to navigate, long-hold to drag reorder.
          flex-1 min-w-0 + overflow keep the pills from EVER pushing the
          delete/add buttons out of the bar (they vanished at 9-10 slides). */}
      <div className={`flex items-center gap-0.5 touch-none select-none flex-1 min-w-0 ${compact ? 'flex-wrap gap-y-1.5' : 'overflow-x-auto'}`}>
        {orderedItems.map((slide, visualIdx) => {
          const isDragged = draggedId === slide.id;
          // Find original index to determine if this is the current slide
          const originalIndex = slides.findIndex(s => s.id === slide.id);
          const isCurrentSlide = originalIndex === currentIndex;

          return (
            <button
              key={slide.id}
              onPointerDown={(e) => handlePointerDown(e, slide.id)}
              onPointerUp={() => handlePointerUp(slide.id)}
              onPointerEnter={() => handlePointerEnter(slide.id)}
              onPointerCancel={() => { clearLongHold(); setDraggedId(null); setVisualOrder(slides.map(s => s.id)); }}
              title={`Slide ${visualIdx + 1}`}
              className={compact
                ? `w-2.5 h-2.5 rounded-full cursor-pointer transition-all duration-200 ease-out flex-shrink-0
                   ${isCurrentSlide && !isDragged
                     ? 'bg-white scale-125'
                     : isDragged
                       ? 'bg-blue-500 scale-150 shadow-lg ring-2 ring-blue-300'
                       : 'bg-gray-600 hover:bg-gray-400'}`
                : `w-6 h-6 text-[11px] font-medium rounded cursor-pointer flex-shrink-0
                   transition-all duration-200 ease-out
                   ${isCurrentSlide && !isDragged
                     ? 'bg-white text-gray-900'
                     : isDragged
                       ? 'bg-blue-500 text-white scale-110 shadow-lg ring-2 ring-blue-300 -translate-y-1'
                       : 'bg-gray-600 text-white hover:bg-gray-500'}`}
              style={{ touchAction: 'none' }}
            >
              {compact ? '' : visualIdx + 1}
            </button>
          );
        })}
        {compact && (
          <span className="ml-1.5 text-xs text-gray-400 whitespace-nowrap">
            {currentIndex + 1}/{slides.length}
          </span>
        )}
      </div>

      {/* Action buttons — never squeezed by the pill row */}
      <div className="flex gap-1 flex-shrink-0">
        {/* Delete current slide */}
        <button
          onClick={() => slides.length > 1 && onDelete(slides[currentIndex].id)}
          className={`p-1.5 rounded transition-colors ${slides.length > 1 ? 'text-gray-400 hover:text-red-400' : 'text-gray-600 cursor-not-allowed'}`}
          title={slides.length > 1 ? "Delete slide" : "Can't delete last slide"}
          disabled={slides.length <= 1}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
        {/* Add slide */}
        <button
          onClick={onAdd}
          className="p-1.5 text-gray-400 hover:text-green-400 rounded transition-colors"
          title="Add slide"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Stacked Carousel View component - 3D deck visual with adjacent slides peeking
interface StackedCarouselViewProps {
  slides: CarouselSlide[];
  currentIndex: number;
  onNavigate: (index: number) => void;
  onDeleteSlide: (slideId: number) => void;
  onAddSlide: () => void;
  onReorderSlides: (slideIds: number[]) => void;
  // Overlay config (passed from parent)
  showOverlay: boolean;
  overlayConfig: {
    showLogo: boolean;
    showTitle: boolean;
    showSubheader: boolean;
    showCategory: boolean;
    showGradient: boolean;
    gradientDirection: 'up' | 'down' | 'full';
    gradientColor: string;
    gradientOpacity: number;
    borderEnabled: boolean;
    borderColor: string;
    borderThickness: number;
  };
  // Current slide content
  title: string;
  subheader: string;
  category?: string;
  logoElement?: React.ReactNode;
  // Styling
  platformConfig: { aspectRatio?: string; imageMode: string };
  overlayScale: number;
  titleFontSize: number;
  subheaderFontSize: number;
  logoPosition: string;
  titlePosition: string;
  subheaderPosition: string;
  categoryPosition: string;
  categoryBgColor: string;
  logoScale: number;
}

const StackedCarouselView: React.FC<StackedCarouselViewProps> = ({
  slides,
  currentIndex,
  onNavigate,
  onDeleteSlide,
  onAddSlide,
  onReorderSlides,
  showOverlay,
  overlayConfig,
  title,
  subheader,
  category,
  logoElement,
  platformConfig,
  overlayScale,
  titleFontSize,
  subheaderFontSize,
  logoPosition,
  titlePosition,
  subheaderPosition,
  categoryPosition,
  categoryBgColor,
  logoScale,
}) => {
  const prevSlide = currentIndex > 0 ? slides[currentIndex - 1] : null;
  const currentSlide = slides[currentIndex];
  const nextSlide = currentIndex < slides.length - 1 ? slides[currentIndex + 1] : null;

  // Get the image URL for a slide
  const getSlideImageUrl = (slide: CarouselSlide) => {
    return slide.media_url || slide.primary_image_url || (slide.media_urls?.[0]) || '';
  };

  return (
    <div className="relative">
      {/* Stacked cards container - with padding for side peeks */}
      <div className="relative flex items-center justify-center px-6">
        {/* Left peek card - previous slide */}
        {prevSlide && (
          <div
            onClick={() => onNavigate(currentIndex - 1)}
            className="absolute left-0 top-0 bottom-0 w-8 cursor-pointer z-0
                       overflow-hidden rounded-l-lg
                       transition-all duration-300 ease-out hover:w-10"
          >
            <div className="absolute inset-0 bg-gray-800 rounded-l-lg overflow-hidden">
              {getSlideImageUrl(prevSlide) && (
                <img
                  src={getSlideImageUrl(prevSlide)}
                  alt={`Slide ${currentIndex}`}
                  className="absolute right-0 top-0 h-full w-auto min-w-[200%] object-cover opacity-70"
                />
              )}
              {/* Subtle dark overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30" />
              {/* Arrow indicator */}
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-4 h-4 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </div>
            </div>
          </div>
        )}

        {/* Current slide (full size) */}
        <div
          className="relative w-full rounded-lg overflow-hidden shadow-xl z-10
                     transition-transform duration-300 ease-out border border-gray-200"
          style={platformConfig.aspectRatio ? { aspectRatio: platformConfig.aspectRatio } : undefined}
        >
          {/* Slide image */}
          {currentSlide && getSlideImageUrl(currentSlide) && (
            <img
              src={getSlideImageUrl(currentSlide)}
              alt={`Slide ${currentIndex + 1}`}
              className={`w-full select-none ${platformConfig.imageMode === 'fixed' ? 'h-full object-cover' : 'h-auto'}`}
              draggable={false}
            />
          )}

          {/* Overlay elements */}
          {showOverlay && (
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              {/* Border layer */}
              {overlayConfig.borderEnabled && (
                <div
                  className="absolute inset-0"
                  style={{
                    border: `${overlayConfig.borderThickness}px solid ${overlayConfig.borderColor}`,
                    borderRadius: 'inherit',
                  }}
                />
              )}
              {/* Gradient */}
              {overlayConfig.showGradient && (
                <div
                  className="absolute inset-0"
                  style={{
                    background: overlayConfig.gradientDirection === 'full'
                      ? `${overlayConfig.gradientColor}${Math.round(overlayConfig.gradientOpacity * 2.55).toString(16).padStart(2, '0')}`
                      : overlayConfig.gradientDirection === 'down'
                      ? `linear-gradient(to bottom, ${overlayConfig.gradientColor}${Math.round(overlayConfig.gradientOpacity * 2.55).toString(16).padStart(2, '0')} 0%, transparent 100%)`
                      : `linear-gradient(to top, ${overlayConfig.gradientColor}${Math.round(overlayConfig.gradientOpacity * 2.55).toString(16).padStart(2, '0')} 0%, transparent 100%)`,
                  }}
                />
              )}
              {/* Logo */}
              {overlayConfig.showLogo && logoElement && (
                <div
                  className={`absolute ${logoPosition === 'top-left' ? 'top-2 left-2' :
                    logoPosition === 'top-right' ? 'top-2 right-2' :
                    logoPosition === 'bottom-left' ? 'bottom-2 left-2' : 'bottom-2 right-2'}`}
                  style={{ transform: `scale(${logoScale})` }}
                >
                  {logoElement}
                </div>
              )}
              {/* Title */}
              {overlayConfig.showTitle && title && (
                <div
                  className={`absolute px-3 ${titlePosition === 'top' ? 'top-3' : titlePosition === 'center' ? 'top-1/2 -translate-y-1/2' : 'bottom-12'} left-0 right-0`}
                >
                  <p
                    className="text-white font-bold leading-tight drop-shadow-lg"
                    style={{ fontSize: `${titleFontSize * overlayScale}px` }}
                  >
                    {title}
                  </p>
                </div>
              )}
              {/* Subheader */}
              {overlayConfig.showSubheader && subheader && (
                <div
                  className={`absolute px-3 ${subheaderPosition === 'below-title' ? 'bottom-6' : 'bottom-3'} left-0 right-0`}
                >
                  <p
                    className="text-white/90 leading-tight drop-shadow-md"
                    style={{ fontSize: `${subheaderFontSize * overlayScale}px` }}
                  >
                    {subheader}
                  </p>
                </div>
              )}
              {/* Category pill */}
              {overlayConfig.showCategory && category && (
                <div
                  className={`absolute ${categoryPosition === 'top-left' ? 'top-3 left-3' :
                    categoryPosition === 'top-right' ? 'top-3 right-3' :
                    categoryPosition === 'bottom-left' ? 'bottom-3 left-3' : 'bottom-3 right-3'}`}
                >
                  <span
                    className="px-2 py-1 text-white text-xs font-medium rounded-full"
                    style={{ backgroundColor: categoryBgColor }}
                  >
                    {category}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Slide number indicator */}
          <div className="absolute top-2 right-2 z-20 bg-black/60 text-white text-xs px-2 py-1 rounded-full select-none">
            {currentIndex + 1}/{slides.length}
          </div>
        </div>

        {/* Right peek card - next slide */}
        {nextSlide && (
          <div
            onClick={() => onNavigate(currentIndex + 1)}
            className="absolute right-0 top-0 bottom-0 w-8 cursor-pointer z-0
                       overflow-hidden rounded-r-lg
                       transition-all duration-300 ease-out hover:w-10"
          >
            <div className="absolute inset-0 bg-gray-800 rounded-r-lg overflow-hidden">
              {getSlideImageUrl(nextSlide) && (
                <img
                  src={getSlideImageUrl(nextSlide)}
                  alt={`Slide ${currentIndex + 2}`}
                  className="absolute left-0 top-0 h-full w-auto min-w-[200%] object-cover opacity-70"
                />
              )}
              {/* Subtle dark overlay */}
              <div className="absolute inset-0 bg-gradient-to-l from-black/60 to-black/30" />
              {/* Arrow indicator */}
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-4 h-4 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom control bar */}
      <CarouselControlBar
        slides={slides}
        currentIndex={currentIndex}
        onNavigate={onNavigate}
        onDelete={onDeleteSlide}
        onAdd={onAddSlide}
        onReorder={onReorderSlides}
      />
    </div>
  );
};

interface TendV2ContentCardProps {
  item: Tendv2ContentItem;
  brandId?: number;
  brandName?: string;
  brandSlug?: string;
  contentId?: number | string;
  contentTitle?: string;
  brandDefaultLogo?: Logo | null;
  brandLogoDisplayMode?: 'icon_only' | 'name_only' | 'icon_and_name';
  onCopy?: (text: string) => void;
  // Curator attribution props (for curated content)
  is_curated?: boolean;
  curatorBrandId?: number;
  curatorBrandName?: string;
  curatorBrandLogo?: Logo | null;
  // Drag/drop reordering props
  dragEnabled?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  // Archive callbacks (SuperAdmin only)
  onArchive?: (contentId: number) => void;
  onUnarchive?: (contentId: number) => void;
  /** When set, the chat button delegates instead of floating its own
      panel — TendedItemModal renders the panel as a side column. */
  onFeedbackClick?: () => void;
  /** The host's "panel is open" state, so the chat button lights up the
      same way it does when the card owns the panel. */
  feedbackActive?: boolean;
}

const TendV2ContentCard: React.FC<TendV2ContentCardProps> = ({
  item,
  brandId,
  brandName,
  brandSlug,
  contentId,
  contentTitle,
  brandDefaultLogo,
  brandLogoDisplayMode = 'icon_and_name',
  is_curated,
  curatorBrandId,
  curatorBrandName,
  curatorBrandLogo,
  dragEnabled = false,
  dragHandleProps,
  onArchive,
  onUnarchive,
  onFeedbackClick,
  feedbackActive = false,
}) => {
  // Get user authentication context
  const { user } = useAuth();
  const isSuperAdmin = user?.is_super_admin ?? false;
  // Font Library manifest (registered fonts + brand/universal defaults);
  // null-object without a provider — everything degrades to Inter.
  const { manifest: fontManifest } = useFontLibrary();
  // Brand membership — any associated user can attempt the design-element
  // upload; the backend gates with 403 for insufficient roles. We deliberately
  // don't filter by role here so the button isn't hidden by frontend type
  // mismatches (string vs int ids, role-string casing, etc.).
  const canEditCurrentBrand = useMemo(() => {
    if (isSuperAdmin) return true;
    if (!user?.brands || brandId == null) return false;
    return user.brands.some((b) => {
      // Compare loosely: backend serializes brand id as string ("12"); local
      // brandId may be a number. Match either way.
      return String(b.id) === String(brandId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, user?.brands, brandId]);

  // Per-content aspect: stamped value → brand/universal maps (captured from
  // the overlay-settings fetch below) → code defaults.
  const [aspectState, setAspectState] = useState<AspectBucket | null>(
    asAspectBucket(item.aspect_ratio)
  );
  const [brandAspectMap, setBrandAspectMap] = useState<Record<string, string> | null>(null);
  const [universalAspectMap, setUniversalAspectMap] = useState<Record<string, string> | null>(null);
  const effectiveBucket: AspectBucket =
    aspectState ?? resolveChannelAspect(brandAspectMap, universalAspectMap, item.platform);
  const [aspectMenuAnchor, setAspectMenuAnchor] = useState<AspectAnchorRect | null>(null);

  // Platform config: color per platform; the canvas aspect comes from the
  // bucket. The wide PlatformConfig annotation is load-bearing — it keeps the
  // statically-dead 'natural'/'video' comparisons below compiling.
  const platformConfig: PlatformConfig = {
    color: PLATFORM_COLORS[item.platform.toLowerCase()] ?? '#6B7280',
    imageMode: 'fixed',
    aspectRatio: BUCKET_CSS[effectiveBucket],
  };

  // Build unique image list from media_urls (deduplicated, preserving original order)
  const initialImages = useMemo(() => {
    const seen = new Set<string>();
    return (item.media_urls || []).filter(url => {
      if (seen.has(url)) {
        return false;
      }
      seen.add(url);
      return true;
    });
  }, [item.media_urls]);

  // Local state for images (can be modified via delete)
  const [localImages, setLocalImages] = useState<string[]>(initialImages);

  const [isCopied, setCopied] = useState(false);
  // Feedback — doc-style comments; replaced the old share-link button
  // (copy-link now lives in the panel footer, new ?item= format).
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackAnchor, setFeedbackAnchor] = useState<HTMLElement | null>(null);
  const [feedbackEntry, setFeedbackEntry] = useState<{ count: number; resolved: boolean } | null>(null);
  const feedbackCounts = useFeedbackCounts();
  const [hoveredImageIndex, setHoveredImageIndex] = useState<number | null>(null);
  // Queue state lifted from QueueControl (null = loading): the whole card
  // header turns purple when queued — that IS the queued indicator.
  const [isItemQueued, setIsItemQueued] = useState<boolean | null>(null);
  // Take (phase 1): the item's take request, live across every view of it
  // (the publish menu toggles it; the recorder's Save marks it recorded).
  const takeItemId = typeof item.id === 'string' ? parseInt(item.id, 10) : item.id;
  const [takeFields, setTakeFields] = useTakeFields(takeItemId, item);
  const itemTakeState = takeState(takeFields);

  // Which slide's audio is live (null = all muted, the default). Per-slide
  // instead of card-wide so unmuting never turns on every carousel video.
  const [unmutedIndex, setUnmutedIndex] = useState<number | null>(null);
  // Videos start PAUSED in production — a wall of cards must not play at
  // you. The Play puck starts playback WITH sound for its slide.
  const [isPlaying, setIsPlaying] = useState(false);

  // Registry of the carousel's <video> elements, keyed `${index}:${kind}`
  // (kind: solo | fit-mode bg | fit-mode fg). Ref callbacks register here
  // and apply the CURRENT av state on attach — replacing the old inline
  // `el.muted = true` refs, which re-ran on every render and silently
  // re-muted the card whenever anything re-rendered (hover, scroll, ...).
  const videoRefs = useRef(new Map<string, HTMLVideoElement>());
  const avStateRef = useRef({ selected: 0, playing: false, unmuted: null as number | null });

  // Frame zero is usually a video's worst frame (mid-motion, mid-blink,
  // fade-in). While a video sits paused before its FIRST play, show the ~1s
  // frame as the still instead. A server-rendered poster image (the poster
  // attribute, when it actually loads) already covers the paused state —
  // but poster URLs are a key CONVENTION ('{video}.poster.jpg', our-bucket
  // videos only) and may 404 before the backfill reaches them, so probe the
  // poster and fall back to the seek when it's missing.
  const POSTER_SEEK_S = 1.0;
  const applyPosterSeek = useCallback((el: HTMLVideoElement) => {
    const seek = () => {
      if (el.dataset.played || !el.paused || el.currentTime > 0.05) return;
      const t = el.duration && isFinite(el.duration)
        ? Math.min(POSTER_SEEK_S, el.duration / 2)
        : POSTER_SEEK_S;
      el.dataset.posterSeek = '1';
      try { el.currentTime = t; } catch { /* not seekable yet */ }
    };
    const seekWhenReady = () => {
      if (el.readyState >= 1) seek();
      else el.addEventListener('loadedmetadata', seek, { once: true });
    };
    if (el.poster) {
      const probe = new Image();
      probe.onerror = seekWhenReady; // poster missing → seek fallback
      probe.src = el.poster;
    } else {
      seekWhenReady();
    }
  }, []);

  const applyAVState = useCallback((el: HTMLVideoElement, index: number, kind: 'solo' | 'bg' | 'fg') => {
    const { selected, playing, unmuted } = avStateRef.current;
    // The blurred fit-mode background never carries audio (its fg twin does)
    el.muted = !(index === unmuted && kind !== 'bg');
    // Only the visible slide plays; off-screen slides stay mounted (instant
    // scroll-snap switching, warm buffers) but paused.
    if (index === selected && playing) {
      if (!el.dataset.played) {
        // First play: rewind from the poster-seek frame to the real start
        el.dataset.played = '1';
        if (el.dataset.posterSeek) {
          delete el.dataset.posterSeek;
          try { el.currentTime = 0; } catch { /* fine — play from here */ }
        }
      }
      el.play().catch(() => { /* autoplay policy or teardown race */ });
    } else if (!el.paused) {
      el.pause();
    }
  }, []);

  const registerVideo = useCallback((index: number, kind: 'solo' | 'bg' | 'fg') =>
    (el: HTMLVideoElement | null) => {
      const key = `${index}:${kind}`;
      if (el) {
        videoRefs.current.set(key, el);
        applyAVState(el, index, kind);
        applyPosterSeek(el);
      } else {
        videoRefs.current.delete(key);
      }
    }, [applyAVState, applyPosterSeek]);

  // Card hover state for drag handle visibility
  const [isCardHovered, setIsCardHovered] = useState(false);

  // Video render state (for Remotion)
  const [isRenderingVideo, setIsRenderingVideo] = useState(false);
  const [renderedVideoUrl, setRenderedVideoUrl] = useState<string | null>(null);
  const [videoRenderError, setVideoRenderError] = useState<string | null>(null);
  const [showVideoPreview, setShowVideoPreview] = useState(false);

  // Media upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pexels media picker state
  const [showPexelsPanel, setShowPexelsPanel] = useState(false);

  // In-situ video recorder — phase, refs (canvas + hidden source video),
  // imperative handles. The card mounts the canvas in the active slot when
  // phase !== 'idle', and the hidden source <video> always (so iOS Safari
  // keeps producing frames).
  const recorder = useVideoRecorder();
  const isRecordingActive = recorder.phase !== 'idle';
  // A clip mid-recording or awaiting Save is only a local blob — a refresh
  // or navigation would throw it away silently. Standard leave-page prompt
  // while that's true.
  useEffect(() => {
    if (recorder.phase !== 'recording' && recorder.phase !== 'review') return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [recorder.phase]);

  // Teleprompter (PIP) — opens via button or auto on tab switch while the
  // user is in preview/recording. Closed by default; only mounted while open
  // so we don't request a PIP window until needed.
  const [teleprompterOpen, setTeleprompterOpen] = useState(false);
  // Stops the visibilitychange handler from re-opening the PIP if the user
  // explicitly closed it during this preview/recording session. Resets when
  // the recorder leaves the preview/recording phases.
  const teleprompterDismissedRef = useRef(false);
  // When the teleprompter is open, the recording stage (image carousel +
  // overlay + camera canvas + slide nav + controls) is portaled INTO the
  // PIP's right-side container instead of being rendered in-place. Same
  // React subtree, different DOM home — so any change made here lives in
  // one place. State (not a plain ref) for the in-place host so the portal
  // re-renders once the host element mounts.
  const [inPlaceStageHost, setInPlaceStageHost] = useState<HTMLDivElement | null>(null);
  const [pipStageContainer, setPipStageContainer] = useState<HTMLElement | null>(null);
  const stagePortalTarget = pipStageContainer || inPlaceStageHost;

  // Reset the "user dismissed" flag whenever the recorder returns to idle/review
  // so the next preview session can auto-open again. Also force-close any open
  // PIP that's left dangling — without an active canvas stream it's just an
  // empty window.
  useEffect(() => {
    if (recorder.phase === 'idle' || recorder.phase === 'review') {
      teleprompterDismissedRef.current = false;
      if (recorder.phase === 'idle' && teleprompterOpen) {
        setTeleprompterOpen(false);
      }
    }
  }, [recorder.phase, teleprompterOpen]);

  // Auto-open the teleprompter when the user tabs away during preview/recording.
  // Per spec: educates users that the feature exists ("primarily users would
  // do this from the preview phase before they start recording"). Honors a
  // session-scoped dismissal flag so closing the PIP doesn't immediately
  // re-open it on the next tab switch.
  useEffect(() => {
    const onVisibility = () => {
      if (!document.hidden) return;
      if (teleprompterOpen || teleprompterDismissedRef.current) return;
      const p = recorder.phase;
      if (p === 'preview' || p === 'recording') {
        setTeleprompterOpen(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [recorder.phase, teleprompterOpen]);

  const closeTeleprompter = useCallback(() => {
    teleprompterDismissedRef.current = true;
    setTeleprompterOpen(false);
  }, []);

  // --- Published state --------------------------------------------------
  // Once an item is published, the media area flips from the editor to the
  // actual published media and the analytics band appears under the header.
  // All lookups key on the TENDV2 item id (item.id) — the card's contentId
  // prop is the source article id and must not be used here. The async
  // publish-status call rides SmartPublishButton's own 50ms batch (same
  // key, same context) so this costs no extra requests.
  const publishBrandId = curatorBrandId || brandId;
  const itemIdNum = typeof item.id === 'string' ? parseInt(item.id, 10) : item.id;
  const { getPublishStatus, refreshPublishStatus } = usePublishStatus();
  const [asyncPublished, setAsyncPublished] = useState(false);
  // Fresh copy of the item fetched on the in-session publish flip. The
  // published face must show the media that actually went out, but the
  // `item` prop is from the page-load fetch — stale when media was
  // recorded/swapped in-session (the golf-image flash). Refetched from the
  // same items listing the page uses, so display semantics are identical.
  const [publishedItemFresh, setPublishedItemFresh] = useState<Tendv2ContentItem | null>(null);
  const [publishedAt, setPublishedAt] = useState<string | null>(
    (item as any).published_at || null
  );
  useEffect(() => {
    if (!publishBrandId || !itemIdNum) return;
    let alive = true;
    getPublishStatus(publishBrandId, itemIdNum).then((status) => {
      if (!alive || !status) return;
      const leg = status.platforms?.[normalizePlatform(item.platform)];
      setAsyncPublished(!!(status.published && leg?.status === 'success'));
      setAsyncLegState((leg?.leg_state as LegState | undefined) ?? null);
      if (status.published_at) setPublishedAt(status.published_at);
    });
    return () => { alive = false; };
  }, [publishBrandId, itemIdNum, item.platform, getPublishStatus]);
  // Sync fast path — the state='p' listing injects publish_status, so the
  // Published page never flashes the editor for a frame. Note a
  // repost-requeued item stays in the published view (button-face
  // semantics); if reposts must be re-editable, gate on !isItemQueued too.
  const showPublishedView = (item as any).publish_status === 'success' || asyncPublished;
  // The network's confirmation of the leg: the Published listing injects
  // publish_leg_state; the per-item status fetch refreshes it. A post the
  // network accepted but has not confirmed reads "Sent · awaiting …" on the
  // published face instead of Published with no link.
  const [asyncLegState, setAsyncLegState] = useState<LegState | null>(null);
  const publishLegState: LegState | null =
    asyncLegState ?? ((item as any).publish_leg_state as LegState | null | undefined) ?? null;
  const publishLegNote = showPublishedView
    ? legNote(publishLegState, CHANNEL_INFO[item.platform]?.name || item.platform)
    : null;

  // QA peek: published cards default to the media that actually went out,
  // but a toggle above the media flips back to the live overlay
  // composition so the two can be compared side-by-side-in-time.
  const [publishedOverlayPeek, setPublishedOverlayPeek] = useState(false);
  useEffect(() => { setPublishedOverlayPeek(false); }, [item.id]);
  // The editor stage (overlay composition) renders when unpublished OR
  // when the QA peek asks for it — the three stage gates key off this.
  const showEditorStage = !showPublishedView || publishedOverlayPeek;

  // Lifetime metrics for the analytics band — one bulk fetch per brand via
  // PostMetricsContext, keyed by tendv2 item id.
  const { byContentId: postMetricsByContentId, refresh: refreshPostMetrics } =
    useBrandPostMetrics(publishBrandId);
  const metricsRow = itemIdNum ? postMetricsByContentId[itemIdNum] : undefined;
  // Row visible for anything that has ever been published: a metrics row
  // survives a repost-requeue, and a just-published item shows pending
  // dashes until the posts fetch catches up.
  const showMetricsRow = !!metricsRow || showPublishedView;

  // A publish can land while the recorder/teleprompter is up (queue fire,
  // publish-now from the menu) — the portal unmount already cleans up, this
  // makes the teardown explicit.
  useEffect(() => {
    if (!showPublishedView) return;
    if (recorder.phase !== 'idle') recorder.cancel();
    if (teleprompterOpen) closeTeleprompter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPublishedView, recorder.phase, teleprompterOpen, closeTeleprompter]);

  // Pexels photo attribution (map of URL -> attribution text) and its
  // switch — persisted fields: only an edit made HERE writes; a server
  // change arriving through the prop is adopted (usePersistedValue).
  const serverPexelsAttributions = useMemo<Record<string, string>>(
    () => item.pexels_attributions || {},
    [item.pexels_attributions]
  );
  const savePexelsAttributions = useCallback((value: Record<string, string>) => {
    (item as any).pexels_attributions = value;
    return tendApi.updateTendV2Content(item.id, { pexels_attributions: value });
  }, [item]);
  const pexelsAttributionsField = usePersistedValue<Record<string, string>>({
    serverValue: serverPexelsAttributions,
    serverVersion: item,
    recordKey: item.id,
    save: savePexelsAttributions,
  });
  const pexelsAttributions = pexelsAttributionsField.value;
  const { edit: setPexelsAttributions, getValue: getPexelsAttributions } = pexelsAttributionsField;
  const savePhotoAttributionEnabled = useCallback((value: boolean) => {
    (item as any).pexels_attribution_enabled = value;
    return tendApi.updateTendV2Content(item.id, { pexels_attribution_enabled: value });
  }, [item]);
  const photoAttributionEnabledField = usePersistedValue<boolean>({
    serverValue: item.pexels_attribution_enabled ?? false,
    serverVersion: item,
    recordKey: item.id,
    save: savePhotoAttributionEnabled,
    delay: 300,
  });
  const photoAttributionEnabled = photoAttributionEnabledField.value;
  const setPhotoAttributionEnabled = photoAttributionEnabledField.edit;

  // Image selection state lives in useMediaSelection (below — it needs the
  // carousel refs declared next).

  // Carousel mode state (for content_mode === 'carousel')
  // NOTE: These must be declared before the localImages sync effect below
  const [slides, setSlides] = useState<CarouselSlide[]>([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isCarouselMode, setIsCarouselMode] = useState(false);
  // Ref to track current carousel mode for async callbacks (avoids stale closures)
  const isCarouselModeRef = useRef(false);
  const slidesRef = useRef<CarouselSlide[]>([]);
  const currentSlideIndexRef = useRef(0);
  const [isDraggingSlide, setIsDraggingSlide] = useState<number | null>(null);
  const [isLoadingSlides, setIsLoadingSlides] = useState(false);
  // A slide's image selection is the URL on the slide (media_url), read by
  // resolveSlideMedia on every hydration — never a remembered position: the
  // pool is shared by every slide and grows whenever any slide adds.
  // Refs to track which slide an edit belongs to (prevents debounce race condition)
  const editingTitleSlideIdRef = useRef<number | null>(null);
  const editingSubheaderSlideIdRef = useRef<number | null>(null);
  const editingBodySlideIdRef = useRef<number | null>(null);
  const editingScriptSlideIdRef = useRef<number | null>(null);
  // Track previous slide index to detect actual navigation (vs. slides array updates)
  const prevSlideIndexRef = useRef<number>(0);
  // Track if we're in the middle of initial slide load
  const isInitialSlideLoadRef = useRef<boolean>(true);
  // Track if we're in the middle of syncing slide state from a navigation.
  // The sync effect sets this true before its setX calls so the auto-save
  // effect can skip — otherwise sync-driven state changes would re-fire the
  // debounced save and clear the slide's overlay_template_id (because
  // auto-save assumes any state change is a user customization).
  const isSyncingSlideRef = useRef<boolean>(false);
  // Ref mirror of overlayTemplates so the slide sync effect can resolve template configs
  // without a dependency on the state variable (which is declared later in the file)
  const overlayTemplatesRef = useRef<Array<{ id: number } & Record<string, any>>>([]);
  // Track if we're in the middle of a reorder operation (skip sync during reorder)
  const isReorderingRef = useRef<boolean>(false);
  // Track pending overlay config changes (slideId -> config) to flush before navigation
  // Also tracks template_id for template selections
  const pendingOverlayConfigRef = useRef<{ slideId: number; config: OverlayTemplateConfig; templateId?: number | null } | null>(null);
  // Ref to debounce timeout so we can cancel it when flushing
  const overlayConfigTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Keep refs in sync with state (for async callbacks that would otherwise have stale closures)
  useEffect(() => {
    isCarouselModeRef.current = isCarouselMode;
  }, [isCarouselMode]);
  useEffect(() => {
    slidesRef.current = slides;
  }, [slides]);
  useEffect(() => {
    currentSlideIndexRef.current = currentSlideIndex;
  }, [currentSlideIndex]);

  // Sync localImages when item.media_urls changes
  // Skip in carousel mode - slides manage their own images
  useEffect(() => {
    if (isCarouselMode) return;

    const seen = new Set<string>();
    const deduped = (item.media_urls || []).filter(url => {
      if (seen.has(url)) return false;
      seen.add(url);
      return true;
    });
    // A refetch that carries the same list (a strip poll, a neighbour's
    // queue click re-fetching the strip) must not hand the carousel a new
    // array: that re-ran the anchor + scroll-restore effects and could move
    // a card off a clip recorded in-session (Take phase 1, 2026-09-28).
    setLocalImages((prev) =>
      prev.length === deduped.length && prev.every((url, i) => url === deduped[i]) ? prev : deduped
    );
  }, [item.media_urls, isCarouselMode, item.id]);

  // A sibling tend of the same original added media (upload / recording /
  // Pexels): it is in the shared pool now, so it joins this card's strip
  // immediately — appended, never a new payload (see utils/mediaPool.ts).
  // In carousel mode every slide's pool gains it, the current slide's strip too.
  // This card's own additions arrive here as well (a second mounted copy of
  // the same item learns this way); what is already held is left alone.
  const poolOriginalId = Number((item as any).source_content_id ?? contentId) || null;
  useEffect(() => onPoolChange(poolOriginalId, (url) => {
    setLocalImages((prev) => (prev.includes(url) ? prev : [...prev, url]));
    setSlides((prev) => prev.map((s) =>
      (s.media_urls || []).includes(url) ? s : { ...s, media_urls: [...(s.media_urls || []), url] }
    ));
  }), [poolOriginalId, item.id]);

  // The selected media and the ONLY way to change it (useMediaSelection).
  // A dot click persists through this callback: to the current slide in
  // carousel mode, to the content in single mode. Upload / delete / picker
  // save their own richer payloads and just report the URL the server now
  // holds (markPersisted). Nothing derives the selection from the DOM.
  const persistMediaSelection = useCallback(async (url: string) => {
    if (isCarouselModeRef.current) {
      const slide = slidesRef.current[currentSlideIndexRef.current];
      if (!slide) return;
      if (slide.media_url === url && slide.primary_image_url === url) return;
      // The slide holds its pick at once, so a navigation away and back
      // during the save still shows it (the hydration reads the slide).
      setSlides(prev => prev.map(s => (s.id === slide.id ? { ...s, primary_image_url: url, media_url: url } : s)));
      try {
        await carouselApi.updateSlide(slide.id, { primary_image_url: url, media_url: url });
      } catch (err) {
        console.error('Failed to save slide image selection:', err);
      }
      return;
    }
    try {
      await tendApi.updateTendV2Content(item.id, { primary_image_url: url });
    } catch (err) {
      console.error('Failed to save image selection:', err);
    }
  }, [item.id]);
  const { selectedIndex: selectedImageIndex, selectMedia, markPersisted } = useMediaSelection({
    itemId: item.id,
    primaryUrl: item.primary_image_url,
    images: localImages,
    anchor: !isCarouselMode,
    onPersist: persistMediaSelection,
  });

  // Derive current attribution from selected image (must be after selectedImageIndex)
  const currentImageUrl = localImages[selectedImageIndex] || item.primary_image_url || '';
  const editedPhotoAttribution = pexelsAttributions[currentImageUrl] || '';

  // Handler to update attribution for current image
  const handleAttributionChange = useCallback((newAttribution: string) => {
    if (!currentImageUrl) return;
    setPexelsAttributions({
      ...getPexelsAttributions(),
      [currentImageUrl]: newAttribution,
    });
  }, [currentImageUrl, setPexelsAttributions, getPexelsAttributions]);
  const overlayContainerRef = useRef<HTMLDivElement | null>(null);
  // The media strip is a translated track (`left: -N × 100%`), i.e. a pure
  // projection of selectedImageIndex: nothing scrolls, so a portal swap
  // (teleprompter PIP), a keyed DOM move (a Tended-page reorder) or a
  // remount cannot desync what is shown from what is selected, and there is
  // no scroll offset for anything to read back (Buggie Boo 3ec9a8a4…8cd9).
  const [imageHeights, setImageHeights] = useState<Record<number, number>>({});

  // Overlay proportional scaling state (matches backend CARD_REFERENCE_WIDTH = 360)
  const [overlayScale, setOverlayScale] = useState(1);

  // Keep overlayScale synced to the container via a CALLBACK ref, not a
  // mount-once effect: the overlay container is conditionally rendered and
  // remounts (media arriving, carousel/single switches, recording portal
  // swaps), and an effect-attached ResizeObserver keeps watching the old
  // detached node — freezing the scale at a stale width until something
  // re-runs the effect. The card's overlay then wraps/sizes differently
  // from the server render. offsetWidth (not getBoundingClientRect) so
  // ancestor transform animations can't skew the measurement either.
  const overlayResizeObserverRef = useRef<ResizeObserver | null>(null);
  const setOverlayContainer = useCallback((node: HTMLDivElement | null) => {
    overlayContainerRef.current = node;
    overlayResizeObserverRef.current?.disconnect();
    overlayResizeObserverRef.current = null;
    if (!node) return;
    const updateScale = () => {
      const width = node.offsetWidth;
      if (width > 0) setOverlayScale(width / OVERLAY_REFERENCE_WIDTH);
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(node);
    overlayResizeObserverRef.current = observer;
  }, []);

  // Natural dimensions of every media the strip has loaded, by URL. The
  // selected media's dims are LOOKED UP from the selection on every render —
  // never remembered from "the load that happened while it was selected":
  // with the shared pool every slide walks the same list, so the next
  // slide's photo is usually already mounted as a neighbour and never loads
  // again, and the cropped axis (hence the Fill slider's axis) came from the
  // previous slide's photo (staging 4204, 2026-10-12).
  const [naturalDimsByUrl, setNaturalDimsByUrl] = useState<Record<string, { w: number; h: number }>>({});
  const recordNaturalDims = useCallback((url: string | null, w: number, h: number) => {
    if (!url || !w || !h) return;
    setNaturalDimsByUrl(prev => (prev[url] && prev[url].w === w && prev[url].h === h ? prev : { ...prev, [url]: { w, h } }));
  }, []);
  const selectedMediaNaturalDims = useMemo<{ w: number; h: number } | null>(() => {
    const url = localImages[selectedImageIndex];
    return (url && naturalDimsByUrl[url]) || null;
  }, [localImages, selectedImageIndex, naturalDimsByUrl]);

  // Track image height when it loads; its natural size joins the by-URL map
  // whether or not this slot is the selected one.
  const handleImageLoad = useCallback((index: number, e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageHeights(prev => ({ ...prev, [index]: img.offsetHeight }));
    recordNaturalDims(img.getAttribute('src'), img.naturalWidth, img.naturalHeight);
  }, [recordNaturalDims]);

  // Capture natural dimensions of a video (for cropped-axis detection in fill mode)
  const handleVideoMetadata = useCallback((_index: number, e: React.SyntheticEvent<HTMLVideoElement>) => {
    const v = e.currentTarget;
    recordNaturalDims(v.getAttribute('src'), v.videoWidth, v.videoHeight);
  }, [recordNaturalDims]);

  // Determine which axis is being cropped in fill mode (object-cover).
  // Returns 'x' if image is wider than slot (cropped horizontally), 'y' if taller (cropped vertically),
  // or null if aspect ratios match closely (no meaningful crop).
  const fillCroppedAxis = useMemo<'x' | 'y' | null>(
    () => croppedAxisFor(selectedMediaNaturalDims, platformConfig.aspectRatio),
    [selectedMediaNaturalDims, platformConfig.aspectRatio],
  );

  // Sync mute + playback across the registered carousel videos. One pass:
  // only the visible slide plays (isPlaying-gated); only unmutedIndex is
  // audible. localImages is a dep so key-remounts after URL swaps re-sync.
  useEffect(() => {
    avStateRef.current = { selected: selectedImageIndex, playing: isPlaying, unmuted: unmutedIndex };
    videoRefs.current.forEach((el, key) => {
      const [idxStr, kind] = key.split(':');
      applyAVState(el, parseInt(idxStr, 10), kind as 'solo' | 'bg' | 'fg');
    });
  }, [selectedImageIndex, isPlaying, unmutedIndex, localImages, applyAVState]);

  // Leaving the unmuted slide re-mutes it (audio stops via the pause above;
  // this keeps the icon truthful when you come back).
  useEffect(() => {
    setUnmutedIndex(prev => (prev !== null && prev !== selectedImageIndex ? null : prev));
  }, [selectedImageIndex]);

  // Current image to display (for single-image fallback and overlay)
  const primaryImageUrl = localImages[selectedImageIndex] || item.primary_image_url;

  // Handle image deletion
  const handleDeleteImage = useCallback(async (indexToDelete: number) => {
    if (localImages.length <= 1) return; // Don't delete the last image

    const newImages = localImages.filter((_, idx) => idx !== indexToDelete);
    setLocalImages(newImages);

    // Determine new selected index
    let newIndex = selectedImageIndex;
    if (indexToDelete <= selectedImageIndex) {
      newIndex = Math.max(0, selectedImageIndex - 1);
    }
    if (newIndex >= newImages.length) {
      newIndex = newImages.length - 1;
    }
    selectMedia(newIndex, { images: newImages });

    const newPrimaryUrl = newImages[newIndex];

    // In carousel mode, persist to the current SLIDE, not the parent content.
    // Otherwise the deletion gets reverted on navigation when the sync effect
    // re-reads slide.media_urls.
    if (isCarouselMode && slides[currentSlideIndex]) {
      const slide = slides[currentSlideIndex];
      setSlides(prev => prev.map((s, i) =>
        i === currentSlideIndex
          ? { ...s, media_urls: newImages, primary_image_url: newPrimaryUrl, media_url: newPrimaryUrl }
          : s
      ));
      try {
        await carouselApi.updateSlide(slide.id, {
          media_urls: newImages,
          primary_image_url: newPrimaryUrl,
          media_url: newPrimaryUrl,
        });
      } catch (error) {
        console.error('Error deleting image from slide:', error);
      }
      return;
    }

    // Single mode: save to content
    markPersisted(newPrimaryUrl);
    await tendApi.updateTendV2Content(item.id, {
      media_urls: newImages,
      primary_image_url: newPrimaryUrl
    });
  }, [localImages, selectedImageIndex, item.id, isCarouselMode, slides, currentSlideIndex, selectMedia, markPersisted, poolOriginalId]);

  // Handle media upload
  const handleMediaUpload = useCallback(async (file: File, source: 'upload' | 'recording' = 'upload') => {
    setIsUploading(true);
    setUploadError(null);
    try {
      const result = await tendApi.uploadMediaToContent(item.id, file, source);
      if (!result.success || !result.media_urls) {
        const errorMsg = result.error || 'Upload failed';
        console.error('Upload failed:', errorMsg);
        setUploadError(errorMsg);
        setTimeout(() => setUploadError(null), 5000);
        return;
      }
      // The recorder's Save: the server stamped take_recorded_at — the
      // notch reads "recorded" from here on (Take phase 1).
      if (source === 'recording' && result.take_recorded_at) {
        setTakeFields({ ...takeFields, take_recorded_at: result.take_recorded_at });
      }

      // The upload endpoint hosts the file in the content's media storage
      // and returns the content's full media_urls list. The newly added URL
      // is whatever's in result.media_urls but not in the previous list.
      // In single mode that's enough; in carousel mode we still need to
      // append the new URL onto the current slide's own media_urls and save
      // that to the slide so it survives navigation.
      const previousContentUrls = new Set(item.media_urls || []);
      const newlyAddedUrls = result.media_urls.filter(url => !previousContentUrls.has(url));
      const newUrl = newlyAddedUrls[newlyAddedUrls.length - 1] ?? result.media_urls[result.media_urls.length - 1];
      // The file is in the original's pool now: tell the sibling cards on screen.
      if (newUrl) announcePoolChange(poolOriginalId, newUrl, item.id);

      if (isCarouselMode && slides[currentSlideIndex] && newUrl) {
        const slide = slides[currentSlideIndex];
        const slideMediaUrls = slide.media_urls || [];
        const newSlideMediaUrls = [...slideMediaUrls, newUrl];
        const newIndex = newSlideMediaUrls.length - 1;

        setLocalImages(newSlideMediaUrls);
        selectMedia(newIndex, { images: newSlideMediaUrls });
        // The file is the post's now: every other slide's list gains it too,
        // so their Media counter and gallery agree without a reload.
        setSlides(prev => withPhotoOnEverySlide(prev.map((s, i) =>
          i === currentSlideIndex
            ? { ...s, media_urls: newSlideMediaUrls, primary_image_url: newUrl, media_url: newUrl }
            : s
        ), newUrl));

        try {
          await carouselApi.updateSlide(slide.id, {
            media_urls: newSlideMediaUrls,
            primary_image_url: newUrl,
            media_url: newUrl,
          });
        } catch (error) {
          console.error('Error saving uploaded media to slide:', error);
        }
        return;
      }

      // Single mode: localImages mirrors content.media_urls — the pool as the
      // server reads it back (own list first, then what sibling tends added),
      // so the new file is found by URL, never assumed to be last.
      setLocalImages(result.media_urls);
      const byUrl = newUrl ? result.media_urls.indexOf(newUrl) : -1;
      const newIndex = byUrl >= 0 ? byUrl : result.media_urls.length - 1;
      selectMedia(newIndex, { images: result.media_urls });
      // Persist the new media as primary RIGHT NOW — closing the card (or a
      // background card instance racing us) must not lose the choice. The
      // hook learns the server now holds it, so a prop echo is not a change.
      if (newUrl) {
        markPersisted(newUrl);
        try {
          await tendApi.updateTendV2Content(item.id, { primary_image_url: newUrl });
        } catch (error) {
          console.error('Error saving uploaded media as primary:', error);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('Upload failed. Please try again.');
      setTimeout(() => setUploadError(null), 5000);
    } finally {
      setIsUploading(false);
    }
  }, [item.id, item.media_urls, isCarouselMode, slides, currentSlideIndex, takeFields, setTakeFields, selectMedia, markPersisted]);

  // Handle file selection from input
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleMediaUpload(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [handleMediaUpload]);

  // Detect if this is carousel content (by content_mode or tactic name)
  const isCarouselTactic = item.content_mode === 'carousel';

  // Load slides when content is in carousel mode
  useEffect(() => {
    const loadSlides = async () => {
      // Load slides if either content_mode is 'carousel' OR tactic indicates carousel
      if (isCarouselTactic) {
        setIsLoadingSlides(true);
        try {
          const loadedSlides = await carouselApi.listSlides(item.id);
          if (loadedSlides.length > 0) {
            // Mark as initial load so sync effect runs once
            isInitialSlideLoadRef.current = true;
            prevSlideIndexRef.current = 0;
            setCurrentSlideIndex(0);
            setSlides(loadedSlides);
            setIsCarouselMode(true);
          }
        } catch (error) {
          console.error('Failed to load carousel slides:', error);
        } finally {
          setIsLoadingSlides(false);
        }
      }
    };
    loadSlides();
  }, [item.id, isCarouselTactic]);

  // Sync ALL state when navigating slides in carousel mode
  // IMPORTANT: Only runs on ACTUAL navigation (slide index change) or initial load,
  // NOT when slides array updates from saving (which would overwrite user changes)
  useEffect(() => {
    if (!isCarouselMode || !slides[currentSlideIndex]) return;

    // Skip during reorder operations (prevents image/content from changing)
    if (isReorderingRef.current) return;

    // Check if this is actual navigation or just slides array update from saving
    const isNavigation = prevSlideIndexRef.current !== currentSlideIndex;
    const isInitialLoad = isInitialSlideLoadRef.current;

    // Only sync state on actual navigation or initial load
    if (!isNavigation && !isInitialLoad) {
      return;
    }

    // Update refs
    prevSlideIndexRef.current = currentSlideIndex;
    isInitialSlideLoadRef.current = false;

    // Mark that we're loading state from a slide. The auto-save effect will
    // skip while this is true, preventing the debounced save from clearing
    // the slide's overlay_template_id when sync programmatically updates state
    // to match what's already saved on the slide. Reset after the next task
    // so the auto-save effect (which runs after this render commits) sees it
    // true, then becomes false for any subsequent user-driven changes.
    isSyncingSlideRef.current = true;
    setTimeout(() => { isSyncingSlideRef.current = false; }, 600);
    // A hydration is starting: any earlier pointerdown (scroll, navigation
    // click) was NOT an overlay edit. Disarm so the auto-save adopts the
    // synced state as baseline; a real edit re-arms via pointerdown.
    overlayUserTouchedRef.current = false;

    const slide = slides[currentSlideIndex];

    // ===== IMAGES (FULL POOL) — before anything can return =====
    // The slide's list (the shared pool) and ITS pick, found by URL. This
    // sits above the template branch below on purpose: a slide whose
    // template is not in the loaded list returns early there (text only),
    // and the card kept the previous slide's photo (item 5256, 2026-10-12).
    // The strip is a projection of the selection, so this is the whole
    // navigation: no deferred scroll, nothing to clamp.
    const slideMedia = resolveSlideMedia(slide);
    setLocalImages(slideMedia.pool);
    selectMedia(slideMedia.index, { images: slideMedia.pool });

    // Resolve overlay config: prefer inline config, then template, then skip
    let config: Partial<OverlayTemplateConfig> | null = slide.overlay_config || null;
    if (!config && slide.overlay_template_id) {
      if (overlayTemplatesRef.current.length > 0) {
        const match = overlayTemplatesRef.current.find(t => t.id === slide.overlay_template_id);
        if (match) config = match as Partial<OverlayTemplateConfig>;
      }
      // Templates not loaded yet — skip overlay state updates entirely.
      // The template init callback will apply the config once templates arrive.
      if (!config) {
        // Still sync text, images, and template tracking — just not overlay visuals
        const newTitle = slide.title || slide.custom_title || slide.generated_title || '';
        const newSubheader = slide.subheader || slide.custom_subheader || slide.generated_subheader || '';
        const newBody = (slide as any).generated_body || slide.slide_text || '';
        const newScript = (slide as any).script || '';
        setEditedTitle(newTitle);
        setEditedSubheader(newSubheader);
        setEditedBody(newBody);
        setEditedScript(newScript);
        if (titleRef.current) titleRef.current.innerHTML = newTitle;
        if (subheaderRef.current) subheaderRef.current.innerHTML = newSubheader;
        if (bodyRef.current) bodyRef.current.innerHTML = newBody;
        if (scriptRef.current) scriptRef.current.innerText = newScript;
        setActiveTemplateId(slide.overlay_template_id ?? null);
        prevSlideIndexRef.current = currentSlideIndex;
        isInitialSlideLoadRef.current = false;
        return;
      }
    }
    if (!config) config = {};

    // ===== TEXT =====
    // Backend returns `title` and `subheader` as effective values (custom || generated)
    const newTitle = slide.title || slide.custom_title || slide.generated_title || '';
    const newSubheader = slide.subheader || slide.custom_subheader || slide.generated_subheader || '';
    // Body text: prefer the new generated_body field, fall back to legacy slide_text
    // (existing slides created before this migration still only have slide_text).
    const newBody = (slide as any).generated_body || slide.slide_text || '';
    const newScript = (slide as any).script || '';
    setEditedTitle(newTitle);
    setEditedSubheader(newSubheader);
    setEditedBody(newBody);
    setEditedScript(newScript);

    // CRITICAL: Update contentEditable DOM directly (React state doesn't update DOM).
    // Use innerHTML so saved rich-text emphasis (per-content highlight bold/italic/etc.)
    // re-renders correctly on slide switch.
    if (titleRef.current) titleRef.current.innerHTML = newTitle;
    if (subheaderRef.current) subheaderRef.current.innerHTML = newSubheader;
    if (bodyRef.current) bodyRef.current.innerHTML = newBody;
    if (scriptRef.current) scriptRef.current.innerText = newScript;

    // (Images were hydrated above, before the template branch.)

    // ===== VISIBILITY TOGGLES =====
    setShowOverlayLogo(config.showOverlayLogo ?? true);
    setShowOverlayTitle(config.showOverlayTitle ?? true);
    setShowOverlaySubheader(config.showOverlaySubheader ?? false);
    setShowOverlayCategory(config.showOverlayCategory ?? false);
    setShowOverlayGradient(config.showOverlayGradient ?? true);
    setShowOverlayBody(config.showOverlayBody ?? false);

    // ===== GRADIENT =====
    setGradientDirection(config.gradientDirection || 'up');
    setGradientColor(config.gradientColor || '#000000');
    setGradientOpacity(config.gradientOpacity ?? 70);

    // ===== BORDER =====
    setBorderEnabled(config.borderEnabled ?? false);
    setBorderColor(config.borderColor || '#ffffff');
    setBorderThickness(config.borderThickness ?? 4);

    // ===== POSITIONS =====
    setLogoPosition(config.logoPosition || 'bottom-center');
    setTitlePosition(config.titlePosition || 'bottom-center');
    setSubheaderPosition(config.subheaderPosition || 'bottom-center');
    setBodyPosition(config.bodyPosition || 'bottom-center');
    setCategoryPosition(config.categoryPosition || 'bottom-center');

    // ===== FONT SIZES & SCALES =====
    setTitleFontSize(config.titleFontSize ?? 14);
    setSubheaderFontSize(config.subheaderFontSize ?? 12);
    setBodyFontSize(config.bodyFontSize ?? 12);
    setLogoScale(config.logoScale ?? 1.0);
    setCategoryScale(config.categoryScale ?? 1.0);
    setLogoGap(config.logoGap ?? null);
    setCategoryGap(config.categoryGap ?? null);

    // ===== TEXT BACKGROUNDS =====
    setTitleBgEnabled(config.titleBgEnabled ?? false);
    setTitleBgColor(config.titleBgColor || '#000000');
    setTitleBgOpacity(config.titleBgOpacity ?? 80);
    setTitleTextColor(config.titleTextColor || '#ffffff');
    setSubheaderBgEnabled(config.subheaderBgEnabled ?? false);
    setSubheaderBgColor(config.subheaderBgColor || '#000000');
    setSubheaderBgOpacity(config.subheaderBgOpacity ?? 80);
    setSubheaderTextColor(config.subheaderTextColor || '#ffffff');
    setBodyBgEnabled(config.bodyBgEnabled ?? false);
    setBodyBgColor(config.bodyBgColor || '#000000');
    setBodyBgOpacity(config.bodyBgOpacity ?? 80);
    setBodyTextColor(config.bodyTextColor || '#ffffff');

    // ===== EXTENDED PER-ELEMENT STYLING =====
    setTitleStyle(hydrateTextElementStyle(config, 'title'));
    setSubheaderStyle(hydrateTextElementStyle(config, 'subheader'));
    setBodyStyle(hydrateTextElementStyle(config, 'body'));

    // ===== DESIGN ELEMENTS =====
    setDesignElements(Array.isArray(config.designElements) ? config.designElements : []);

    // ===== LOGO (restore from config if available) =====
    // Slide-own config holds the effective logo; template-sourced config
    // only seeds when the content has no explicit selection of its own.
    const configFromTemplate = !slide.overlay_config && !!slide.overlay_template_id;
    if (!configFromTemplate || (!effectiveLogoUrlRef.current && !item.overlay_logo_display_mode)) {
      if (config.logoUrl) {
        setSelectedLogo({ url: config.logoUrl, logoKey: config.logoKey, displayName: '' } as Logo);
      }
      if (isLogoDisplayMode(config.logoDisplayMode)) {
        setLogoDisplayMode(config.logoDisplayMode);
      }
    }

    // ===== IMAGE FIT/FILL =====
    setImageFitMode((slide.image_fit_mode as 'fill' | 'fit') || (config.imageFitMode as 'fill' | 'fit') || 'fill');
    setImageFitAnchor((slide.image_fit_anchor as 'top' | 'center' | 'bottom') || (config.imageFitAnchor as 'top' | 'center' | 'bottom') || 'center');
    setImageFitWidth(slide.image_fit_width || (config.imageFitWidth as number) || 100);
    setImageFillPositionX(
      slide.image_fill_position_x ?? (config.imageFillPositionX as number) ?? 50
    );
    setImageFillPositionY(
      slide.image_fill_position_y ?? (config.imageFillPositionY as number) ?? 50
    );

    // ===== CATEGORY =====
    // Config styling wins; a config without it resets to the brand layer
    setCategoryBgColor(config.categoryBgColor || brandCategoryDefaultsRef.current.color);
    setCategoryBorderRadius(
      typeof config.categoryBorderRadius === 'number'
        ? config.categoryBorderRadius
        : brandCategoryDefaultsRef.current.radius
    );
    categoryConfigOverrideRef.current = {
      color: !!config.categoryBgColor,
      radius: typeof config.categoryBorderRadius === 'number',
    };
    setCategoryFontFamily(config.categoryFontFamily ?? null);

    // ===== TEMPLATE TRACKING =====
    // Sync activeTemplateId from slide's overlay_template_id (same as single mode behavior)
    // This ensures the UI reflects whether this slide is using a template or custom config
    setActiveTemplateId(slide.overlay_template_id ?? null);

    // ===== RESET EDIT FLAGS =====
    // Prevent the title/subheader/body/image/font changes from triggering saves (they're from navigation, not user edit)
    titleEditedByUser.current = false;
    subheaderEditedByUser.current = false;
    bodyEditedByUser.current = false;
    scriptEditedByUser.current = false;
    editingTitleSlideIdRef.current = null;
    editingSubheaderSlideIdRef.current = null;
    editingBodySlideIdRef.current = null;
    editingScriptSlideIdRef.current = null;
    isFirstFontSizeChange.current = true;
  }, [currentSlideIndex, slides, isCarouselMode, selectMedia]);

  // Flush pending overlay config changes immediately (used before navigation)
  const flushPendingOverlayConfig = useCallback(async () => {
    // Cancel any pending debounce timeout
    if (overlayConfigTimeoutRef.current) {
      clearTimeout(overlayConfigTimeoutRef.current);
      overlayConfigTimeoutRef.current = null;
    }

    // Save any pending config immediately
    if (pendingOverlayConfigRef.current) {
      const { slideId, config, templateId } = pendingOverlayConfigRef.current;
      pendingOverlayConfigRef.current = null;

      try {
        // Same semantics as the debounced save: a pending user edit means
        // custom config, so the template reference clears explicitly.
        // (Previously templateId was never populated by the debounce path, so
        // this sent undefined — the server KEPT the template id while the
        // local slides array cleared it, leaving UI and server diverged.)
        await carouselApi.updateSlide(slideId, {
          overlay_config: config,
          overlay_template_id: templateId ?? null
        });
        // Update local slides array (convert null to undefined for TypeScript)
        setSlides(prev => prev.map(s =>
          s.id === slideId ? { ...s, overlay_config: config, overlay_template_id: templateId ?? undefined } : s
        ));
        if ((templateId ?? null) === null) {
          setActiveTemplateId(null);
        }
      } catch (err) {
        console.error('Failed to flush slide overlay config:', err);
      }
    }
  }, []);

  // Fit/Fill changes on the current slide, not yet on the local slide. The
  // slider saves on every tick; writing `slides` on every tick re-rendered
  // the whole card mid-drag (staging 2026-10-12: the image stopped following
  // the slider). So the change is remembered here and lands on the local
  // slide once, when the user leaves the slide — which is when the
  // slide-switch hydration reads it back.
  const pendingFitRef = useRef<{ slideId: number; patch: Partial<CarouselSlide> } | null>(null);
  const commitPendingFit = useCallback(() => {
    const pending = pendingFitRef.current;
    if (!pending) return;
    pendingFitRef.current = null;
    setSlides(prev => patchSlide(prev, pending.slideId, pending.patch));
  }, []);

  // Navigate to a slide in carousel mode
  const navigateToSlide = useCallback(async (index: number) => {
    if (index >= 0 && index < slides.length && index !== currentSlideIndex) {
      // Flush any pending overlay config changes for current slide before navigating
      await flushPendingOverlayConfig();
      // The slide keeps its Fit/Fill changes before we leave it.
      commitPendingFit();
      // Set sync guard BEFORE state change so the auto-save effect (which fires
      // after setCurrentSlideIndex) sees the guard and skips. Without this, the
      // auto-save fires with the old slide's config and saves it to the new slide.
      isSyncingSlideRef.current = true;
      setCurrentSlideIndex(index);
    }
  }, [slides.length, currentSlideIndex, flushPendingOverlayConfig, commitPendingFit]);

  // Add a new slide to the carousel
  const handleAddSlide = useCallback(async () => {
    try {
      const newSlide = await carouselApi.createSlide(item.id, {
        slide_number: slides.length + 1,
        // Inherit media from parent content
        media_urls: localImages,
        primary_image_url: localImages[0] || item.primary_image_url || undefined,
      });
      commitPendingFit();
      setSlides(prev => [...prev, newSlide]);
      setCurrentSlideIndex(slides.length); // Navigate to new slide
    } catch (error) {
      console.error('Failed to add slide:', error);
    }
  }, [item.id, slides.length, localImages, item.primary_image_url, commitPendingFit]);

  // Delete the current slide
  const handleDeleteSlide = useCallback(async (slideId: number) => {
    if (slides.length <= 1) return; // Don't delete last slide
    try {
      await carouselApi.deleteSlide(slideId);
      const slideIndex = slides.findIndex(s => s.id === slideId);
      commitPendingFit();
      setSlides(prev => prev.filter(s => s.id !== slideId));
      // Navigate to previous slide or stay at current position
      if (slideIndex <= currentSlideIndex && currentSlideIndex > 0) {
        setCurrentSlideIndex(prev => prev - 1);
      }
    } catch (error) {
      console.error('Failed to delete slide:', error);
    }
  }, [slides, currentSlideIndex, commitPendingFit]);

  // Reorder slides
  const handleReorderSlides = useCallback(async (newSlideIds: number[]) => {
    try {
      // Mark that we're reordering (prevents sync effect from running)
      isReorderingRef.current = true;

      // Find the currently viewed slide's ID before reordering
      const currentSlideId = slides[currentSlideIndex]?.id;

      await carouselApi.reorderSlides(item.id, newSlideIds);

      // Reorder local state to match
      const reorderedSlides = newSlideIds.map(id => slides.find(s => s.id === id)!).filter(Boolean);
      setSlides(reorderedSlides);

      // Update currentSlideIndex to follow the slide we were viewing
      if (currentSlideId) {
        const newIndex = newSlideIds.indexOf(currentSlideId);
        if (newIndex !== -1 && newIndex !== currentSlideIndex) {
          // Also update prevSlideIndexRef so sync effect doesn't think it's navigation
          prevSlideIndexRef.current = newIndex;
          setCurrentSlideIndex(newIndex);
        }
      }

      // Clear reordering flag after state updates are processed
      setTimeout(() => {
        isReorderingRef.current = false;
      }, 0);
    } catch (error) {
      console.error('Failed to reorder slides:', error);
      isReorderingRef.current = false;
    }
  }, [item.id, slides, currentSlideIndex]);

  // Handle Pexels media selection
  const handlePexelsMediaSelect = useCallback(async (media: PexelsMediaItem) => {
    // In carousel mode, add to current slide's media_urls pool
    if (isCarouselMode && slides[currentSlideIndex]) {
      const slide = slides[currentSlideIndex];
      const currentSlideImages = slide.media_urls || [];
      const newImages = [...currentSlideImages, media.fullUrl];
      const newIndex = newImages.length - 1;

      // Update local state immediately (so navigation preserves change)
      setLocalImages(newImages);
      selectMedia(newIndex, { images: newImages });

      // Update slide in local state — and every other slide's list gains the
      // photo (the pool is the post's), so their counter and gallery agree.
      setSlides(prev => withPhotoOnEverySlide(prev.map((s, i) =>
        i === currentSlideIndex ? {
          ...s,
          media_urls: newImages,
          primary_image_url: media.fullUrl,
          media_url: media.fullUrl,
        } : s
      ), media.fullUrl));

      // Close the panel with a small delay to allow cleanup
      setTimeout(() => {
        setShowPexelsPanel(false);
      }, 50);

      // Save to backend
      try {
        await carouselApi.updateSlide(slide.id, {
          media_urls: newImages,
          primary_image_url: media.fullUrl,
          media_url: media.fullUrl,
        });
        announcePoolChange(poolOriginalId, media.fullUrl, item.id);
      } catch (error) {
        console.error('Error saving Pexels media to slide:', error);
      }
      return;
    }

    // Single mode: add to content's media_urls
    const newImages = [...localImages, media.fullUrl];
    setLocalImages(newImages);

    // Merge the new attribution into the map (keyed by URL)
    const newAttributions = {
      ...pexelsAttributions,
      [media.fullUrl]: media.attribution
    };
    setPexelsAttributions(newAttributions);
    // Auto-disable attribution by default (user can enable in post area)
    setPhotoAttributionEnabled(false);

    // Navigate to the newly added image
    const newIndex = newImages.length - 1;
    selectMedia(newIndex, { images: newImages });

    // Close the panel with a small delay to allow cleanup
    setTimeout(() => {
      setShowPexelsPanel(false);
    }, 50);

    // Save to backend for single mode
    markPersisted(newImages[newIndex]);
    try {
      await tendApi.updateTendV2Content(item.id, {
        media_urls: newImages,
        primary_image_url: newImages[newIndex],
        pexels_attributions: newAttributions,
        pexels_attribution_enabled: false,
      });
      announcePoolChange(poolOriginalId, media.fullUrl, item.id);
    } catch (error) {
      console.error('Error saving Pexels media:', error);
    }
  }, [localImages, item.id, pexelsAttributions, isCarouselMode, slides, currentSlideIndex, selectMedia, markPersisted, poolOriginalId]);

  // Check if URL is a video (handles URLs with query params like ?Expires=...)
  const isVideoUrl = useCallback((url: string) => {
    return /\.(mp4|mov|webm)($|\?)/i.test(url);
  }, []);

  // Poster URL by key convention: our S3 media pipeline writes a
  // ffmpeg-picked clean still at '{video_key}.poster.jpg' next to every
  // video it stores. Only OUR bucket objects can have sibling posters —
  // third-party hosts (Pexels, Vizard CDN) get undefined and fall back to
  // the paused seek-to-1s heuristic (applyPosterSeek probes for 404s).
  const posterUrlFor = useCallback((url: string): string | undefined => {
    return /\.s3\.[^/]+\.amazonaws\.com\/(tend|content)-media\//.test(url)
      ? `${url}.poster.jpg`
      : undefined;
  }, []);

  // Editable primary text — the text is STATE, the editable is its
  // projection (Buggie Boo 3f89a8a4…3521: the copy text reverted on
  // production because a card that had NOT received the edit — the same
  // post in the strip under the item modal, another tab, a teammate's
  // browser — compared its text against the refreshed prop and wrote its
  // stale text back on every strip poll). usePersistedText owns the
  // debounce, the blur/unmount flush, the last-saved compare and the
  // stale-refetch guard; only an edit typed HERE ever saves.
  const textRef = useRef<HTMLDivElement>(null);
  const savePrimaryText = useCallback((value: string) => {
    // Mirror onto the item so a parent holding this object doesn't reseed
    // us with the old text on a remount (same as body / category).
    (item as any).primary_text = value;
    (item as any).character_count = value.length;
    (item as any).word_count = countWords(value);
    return tendApi.updateTendV2Content(item.id, { primary_text: value });
  }, [item]);
  const textField = usePersistedText({
    serverValue: item.primary_text ?? '',
    serverVersion: item,
    recordKey: item.id,
    save: savePrimaryText,
    onExternalChange: (value) => {
      // The editable is mounted only while editing (the read view renders
      // from state); keep the node under the caret in step.
      if (textRef.current && textRef.current.textContent !== value) {
        textRef.current.textContent = value;
      }
    },
  });
  const editedText = textField.value;
  const { edit: editText, flush: flushText } = textField;
  const localWordCount = countWords(editedText);

  // Post Title — independently editable (title-bearing channels only, per
  // PLATFORM_LAYOUT.titlePos). Seeded server-side from generated_title at
  // creation; edits here write post_title ONLY, never the overlay title,
  // and overlay-title edits never write back. That divergence is the
  // contract — do not "sync" these fields.
  const postTitleRef = useRef<HTMLDivElement | null>(null);
  const savePostTitle = useCallback((value: string) => {
    (item as any).post_title = value;
    return tendApi.updateTendV2Content(item.id, { post_title: value });
  }, [item]);
  const postTitleField = usePersistedText({
    serverValue: item.post_title ?? '',
    serverVersion: item,
    recordKey: item.id,
    save: savePostTitle,
    onExternalChange: (value) => {
      if (postTitleRef.current && postTitleRef.current.textContent !== value) {
        postTitleRef.current.textContent = value;
      }
    },
  });
  const editedPostTitle = postTitleField.value;
  const { edit: editPostTitle, flush: flushPostTitle, getValue: getPostTitleValue } = postTitleField;
  // The editable is rendered childless and seeded from STATE on attach —
  // never from the item prop as children (React would rewrite the text
  // node under the caret on the next prop refresh). Stable identity: React
  // calls it on attach/detach only.
  const attachPostTitleRef = useCallback((el: HTMLDivElement | null) => {
    postTitleRef.current = el;
    if (el && el.textContent !== getPostTitleValue()) {
      el.textContent = getPostTitleValue();
    }
  }, [getPostTitleValue]);
  // Title counter visibility: shown while the post title is focused, or
  // whenever over the channel's title limit (PLATFORM_TITLE_LIMITS).
  const [isPostTitleFocused, setIsPostTitleFocused] = useState(false);

  // Primary-text clamp: idle shows a read-only view clamped to the
  // channel's line count (PLATFORM_LAYOUT.clampLines) with a social-style
  // "more" affordance; clicking swaps in the contentEditable for full-text
  // editing, blur collapses back. The swap (vs clamping the editable)
  // avoids caret weirdness at the clip boundary and keeps the imperative
  // textRef rehydration contract intact.
  const [isEditingText, setIsEditingText] = useState(false);
  const [textOverflows, setTextOverflows] = useState(false);
  const clampRef = useRef<HTMLDivElement>(null);
  // Which editor is open under the combined Link/Tags control row (one at a
  // time; pencil toggles it).
  const [openTailEditor, setOpenTailEditor] = useState<'link' | 'tags' | null>(null);

  // Caption mode: check if AI captions exist (defined early for use in state init)
  const hasAICaptions = !!(item.generated_title || item.generated_subheader);

  // Track BOTH source and generated titles/subheaders separately
  // This prevents toggling from overwriting user edits
  const [editedSourceTitle, setEditedSourceTitle] = useState(item.source_title || contentTitle || '');
  const [editedGeneratedTitle, setEditedGeneratedTitle] = useState(item.generated_title || '');
  const [editedSourceSubheader, setEditedSourceSubheader] = useState(item.source_subhead || '');
  const [editedGeneratedSubheader, setEditedGeneratedSubheader] = useState(item.generated_subheader || '');

  // Current display title/subheader based on caption mode
  const [editedTitle, setEditedTitle] = useState(
    hasAICaptions ? (item.generated_title || '') : (item.source_title || contentTitle || '')
  );
  const [editedSubheader, setEditedSubheader] = useState(
    hasAICaptions ? (item.generated_subheader || '') : (item.source_subhead || '')
  );
  const titleRef = useRef<HTMLDivElement>(null);
  const subheaderRef = useRef<HTMLDivElement>(null);
  const isFirstTitleRender = useRef(true);
  const isFirstSubheaderRender = useRef(true);

  // Body — third overlay text element. The text content lives in editedBody;
  // styling lives in body* state above.
  const [editedBody, setEditedBody] = useState(item.generated_body || '');
  const bodyRef = useRef<HTMLDivElement>(null);
  const isFirstBodyRender = useRef(true);
  const [isBodyFocused, setIsBodyFocused] = useState(false);
  // Tracks the body string that's been persisted to backend (single mode).
  // Compared against debounced edits to decide whether to fire a PATCH.
  // Insulates the save logic from `item.generated_body` being stale on the
  // parent (parent doesn't refetch after our save).
  const lastSavedBodyRef = useRef<string>(item.generated_body || '');

  // Script — spoken script for the recording teleprompter (single-post scope;
  // carousels read/write the current slide's script). Edited inline in the
  // Details section AND from inside the PIP teleprompter window.
  const [editedScript, setEditedScript] = useState<string>(item.script || '');
  const scriptRef = useRef<HTMLDivElement>(null);
  const isFirstScriptRender = useRef(true);
  const lastSavedScriptRef = useRef<string>(item.script || '');

  // Editable URL state
  const saveSourceUrl = useCallback((value: string) => {
    (item as any).source_url = value;
    return tendApi.updateTendV2Content(item.id, { source_url: value });
  }, [item]);
  const urlField = usePersistedText({
    serverValue: item.source_url || '',
    serverVersion: item,
    recordKey: item.id,
    save: saveSourceUrl,
  });
  const editedUrl = urlField.value;
  const { edit: setEditedUrl, flush: flushUrl } = urlField;

  // Editable hashtags — a persisted list (order matters)
  const serverHashtags = useMemo<string[]>(() => parseHashtags(item.hashtags), [item.hashtags]);
  const saveHashtags = useCallback((value: string[]) => {
    (item as any).hashtags = value;
    return tendApi.updateTendV2Content(item.id, { hashtags: value });
  }, [item]);
  const hashtagsField = usePersistedValue<string[]>({
    serverValue: serverHashtags,
    serverVersion: item,
    recordKey: item.id,
    save: saveHashtags,
  });
  const editedHashtags = hashtagsField.value;
  const setEditedHashtags = hashtagsField.edit;

  // Editable overlay category — the text is STATE, the pill is its projection
  // (Buggie Boo 3ec9a8a4…fc56). usePersistedText owns the debounce, the
  // blur/unmount flush and the stale-prop guard; a remount (the pill moved to
  // another row) re-seeds from state, never from the item prop.
  const categoryRef = useRef<HTMLDivElement | null>(null);
  const [isCategoryFocused, setIsCategoryFocused] = useState(false);
  const saveCategory = useCallback((value: string) => {
    // Mirror onto the item so a parent holding this object doesn't reset us
    // on remount (same as body / overlay config).
    (item as any).source_category = value;
    return tendApi.updateTendV2Content(item.id, { source_category: value });
  }, [item]);
  const categoryField = usePersistedText({
    serverValue: item.source_category || '',
    recordKey: item.id,
    save: saveCategory,
    onExternalChange: (value) => {
      if (categoryRef.current && categoryRef.current.textContent !== value) {
        categoryRef.current.textContent = value;
      }
      setIsCategoryFocused(false);
    },
  });
  const editedCategory = categoryField.value;
  const { edit: editCategory, flush: flushCategory, getValue: getCategoryValue } = categoryField;
  const [isSubheaderFocused, setIsSubheaderFocused] = useState(false);

  // Title font size controls (persisted)
  const [titleFontSize, setTitleFontSize] = useState(item.title_font_size || 14);
  const [subheaderFontSize, setSubheaderFontSize] = useState(12);
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const isFirstFontSizeChange = useRef(true);

  // Memoized scaled styles for overlay elements (matches backend CARD_REFERENCE_WIDTH = 360)
  const scaledOverlayStyles = useMemo(() =>
    getScaledOverlayStyles(overlayScale, titleFontSize, subheaderFontSize),
    [overlayScale, titleFontSize, subheaderFontSize]
  );

  // Link / Tags switches (default on) — persisted fields: only a click HERE
  // writes; a server change is adopted.
  const saveLinkEnabled = useCallback((value: boolean) => {
    (item as any).tactic_link_enabled = value;
    return tendApi.updateTendV2Content(item.id, { tactic_link_enabled: value });
  }, [item]);
  const linkEnabledField = usePersistedValue<boolean>({
    serverValue: item.tactic_link_enabled ?? true,
    serverVersion: item,
    recordKey: item.id,
    save: saveLinkEnabled,
    delay: 300,
  });
  const linkEnabled = linkEnabledField.value;
  const setLinkEnabled = linkEnabledField.edit;
  const saveHashtagsEnabled = useCallback((value: boolean) => {
    (item as any).tactic_hashtags_enabled = value;
    return tendApi.updateTendV2Content(item.id, { tactic_hashtags_enabled: value });
  }, [item]);
  const hashtagsEnabledField = usePersistedValue<boolean>({
    serverValue: item.tactic_hashtags_enabled ?? true,
    serverVersion: item,
    recordKey: item.id,
    save: saveHashtagsEnabled,
    delay: 300,
  });
  const hashtagsEnabled = hashtagsEnabledField.value;
  const setHashtagsEnabled = hashtagsEnabledField.edit;

  // Channel surface (snapchat: story/saved_story/spotlight; null = channel
  // default). Saved immediately on change — selects are discrete.
  const [contentPlacement, setContentPlacement] = useState<string | null>(item.placement ?? null);
  useEffect(() => { setContentPlacement(item.placement ?? null); }, [item.id, item.placement]);
  const handlePlacementChange = useCallback((value: string) => {
    const next = value || null;
    setContentPlacement(next);
    tendApi.updateTendV2Content(item.id, { placement: next });
  }, [item.id]);

  // Reddit destination + flair (reddit cards only): null destination =
  // the brand's connected profile. Saved immediately — picks are discrete.
  const [redditDestination, setRedditDestination] = useState<string | null>(item.reddit_destination ?? null);
  const [redditFlairId, setRedditFlairId] = useState<string | null>(item.reddit_flair_id ?? null);
  useEffect(() => {
    setRedditDestination(item.reddit_destination ?? null);
    setRedditFlairId(item.reddit_flair_id ?? null);
  }, [item.id, item.reddit_destination, item.reddit_flair_id]);
  const handleRedditDestinationChange = useCallback((destination: string | null) => {
    setRedditDestination(destination);
    tendApi.updateTendV2Content(item.id, { reddit_destination: destination });
  }, [item.id]);
  const handleRedditFlairChange = useCallback((flair: string | null) => {
    setRedditFlairId(flair);
    tendApi.updateTendV2Content(item.id, { reddit_flair_id: flair });
  }, [item.id]);

  // First-comment routing (facebook/twitter): the short link publishes as
  // the post's first comment instead of in the body. Only meaningful with
  // Link on — the row mutes (never hides) when it isn't.
  const saveFirstCommentEnabled = useCallback((value: boolean) => {
    (item as any).tactic_first_comment_enabled = value;
    return tendApi.updateTendV2Content(item.id, { tactic_first_comment_enabled: value });
  }, [item]);
  const firstCommentEnabledField = usePersistedValue<boolean>({
    serverValue: item.tactic_first_comment_enabled ?? false,
    serverVersion: item,
    recordKey: item.id,
    save: saveFirstCommentEnabled,
    delay: 300,
  });
  const firstCommentEnabled = firstCommentEnabledField.value;
  const setFirstCommentEnabled = firstCommentEnabledField.edit;

  // Caption mode toggle - when ON, show AI-generated text; when OFF, show source text
  // (hasAICaptions is defined earlier, near getEffectiveTitle/getEffectiveSubheader)
  const [captionModeEnabled, setCaptionModeEnabled] = useState(
    item.caption_mode_enabled ?? hasAICaptions  // Use dedicated DB column, fall back to auto-detect
  );

  // New visual toggle states for overlay elements
  const [showOverlayLogo, setShowOverlayLogo] = useState(true);
  const [showOverlayTitle, setShowOverlayTitle] = useState(true);
  const [showOverlaySubheader, setShowOverlaySubheader] = useState(hasAICaptions);  // Default ON if AI captions exist
  const [showOverlayCategory, setShowOverlayCategory] = useState(false);    // Default OFF
  const [showOverlayGradient, setShowOverlayGradient] = useState(true);
  // Which corner component is open — Media and Settings today, Elements and
  // Layout to come — one at a time. The settings puck's old boolean is
  // derived from it so its call sites stay as they were.
  const [openCorner, setOpenCorner] = useState<CornerControlId | null>(null);
  // The Content Media Gallery (opened from the Media corner's Preview)
  const [galleryOpen, setGalleryOpen] = useState(false);
  const cardRootRef = useRef<HTMLDivElement>(null);   // the gallery panel sits beside the card
  const showOverlayToggles = openCorner === 'settings';
  const setShowOverlayToggles = useCallback((on: boolean) => {
    setOpenCorner((prev) => (on ? 'settings' : prev === 'settings' ? null : prev));
  }, []);

  // Image fit/fill mode
  const [imageFitMode, setImageFitMode] = useState<'fill' | 'fit'>(item.image_fit_mode as 'fill' | 'fit' || 'fill');
  const [imageFitAnchor, setImageFitAnchor] = useState<'top' | 'center' | 'bottom'>(item.image_fit_anchor as 'top' | 'center' | 'bottom' || 'center');
  const [imageFitWidth, setImageFitWidth] = useState(item.image_fit_width || 100);
  const [imageFillPositionX, setImageFillPositionX] = useState<number>(item.image_fill_position_x ?? 50);
  const [imageFillPositionY, setImageFillPositionY] = useState<number>(item.image_fill_position_y ?? 50);
  const [showFitControls, setShowFitControls] = useState(false);

  // Extended overlay controls
  const [gradientDirection, setGradientDirection] = useState<'up' | 'down' | 'full'>('up');
  const [gradientColor, setGradientColor] = useState('#000000');
  const [gradientOpacity, setGradientOpacity] = useState(70);  // 0-100
  const [titleBgEnabled, setTitleBgEnabled] = useState(false);
  const [titleBgColor, setTitleBgColor] = useState('#000000');
  const [titleBgOpacity, setTitleBgOpacity] = useState(80);  // 0-100
  const [titleTextColor, setTitleTextColor] = useState('#ffffff');
  const [subheaderBgEnabled, setSubheaderBgEnabled] = useState(false);
  const [subheaderBgColor, setSubheaderBgColor] = useState('#000000');
  const [subheaderBgOpacity, setSubheaderBgOpacity] = useState(80);  // 0-100
  const [subheaderTextColor, setSubheaderTextColor] = useState('#ffffff');
  // Body — third overlay text element (renders slide.generated_body or content.generated_body).
  // Defaults are off + bottom-center; the actual text content lives in editedBody below.
  const [showOverlayBody, setShowOverlayBody] = useState(false);
  const [bodyFontSize, setBodyFontSize] = useState(12);
  const [bodyTextColor, setBodyTextColor] = useState('#ffffff');
  const [bodyBgEnabled, setBodyBgEnabled] = useState(false);
  const [bodyBgColor, setBodyBgColor] = useState('#000000');
  const [bodyBgOpacity, setBodyBgOpacity] = useState(80);  // 0-100
  const [borderEnabled, setBorderEnabled] = useState(false);
  const [borderColor, setBorderColor] = useState('#ffffff');
  const [borderThickness, setBorderThickness] = useState(4);

  const [titleStyle, setTitleStyle] = useState<TextElementStyle>({ ...DEFAULT_TEXT_ELEMENT_STYLE, bold: true });
  const [subheaderStyle, setSubheaderStyle] = useState<TextElementStyle>(DEFAULT_TEXT_ELEMENT_STYLE);
  const [bodyStyle, setBodyStyle] = useState<TextElementStyle>(DEFAULT_TEXT_ELEMENT_STYLE);

  // Design elements (canvas-style placed PNG/SVG assets, top-most overlay layer).
  const [designElements, setDesignElements] = useState<PlacedDesignElement[]>([]);
  const [showDesignElementsPicker, setShowDesignElementsPicker] = useState(false);
  // One selection for set elements and design elements alike: click selects,
  // click-away / Escape deselects, hover does nothing (useOverlaySelection).
  const selection = useOverlaySelection(overlayContainerRef, true);
  const { select: selectElement, clear: clearSelection, isSelected: isElementSelected } = selection;
  const selectedDesignElementId = selection.selected?.kind === 'design' ? selection.selected.id : null;
  // Design elements: two layers by depth (utils/designElementLayers.ts). A
  // BEHIND element draws its selection chrome in designChromeHost, a layer
  // above the set elements, so its handles and pill are never under a title.
  const [designChromeHost, setDesignChromeHost] = useState<HTMLDivElement | null>(null);
  const designLayers = splitDesignElementsByDepth(designElements);
  const renderDesignElement = (p: PlacedDesignElement) => (
    <PlaceableDesignElement
      key={p.instanceId}
      placement={p}
      containerRef={overlayContainerRef}
      chromeHost={designChromeHost}
      isSelected={selectedDesignElementId === p.instanceId}
      editable={true}
      onSelect={() => selection.select({ kind: 'design', id: p.instanceId })}
      onChange={(next) => {
        setDesignElements((prev) => prev.map((x) => (x.instanceId === p.instanceId ? next : x)));
      }}
      onDelete={() => {
        setDesignElements((prev) => prev.filter((x) => x.instanceId !== p.instanceId));
        if (selectedDesignElementId === p.instanceId) selection.clear();
      }}
    />
  );

  // Delete / Backspace removes the selected design element — but only when
  // the user isn't typing into a text field (input, textarea, contentEditable).
  useEffect(() => {
    if (!selectedDesignElementId) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const ae = document.activeElement as HTMLElement | null;
      const tag = ae?.tagName;
      const isEditable = !!ae && (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        ae.isContentEditable
      );
      if (isEditable) return;
      e.preventDefault();
      const id = selectedDesignElementId;
      setDesignElements((prev) => prev.filter((x) => x.instanceId !== id));
      clearSelection();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selectedDesignElementId, clearSelection]);

  // Independent overlay positioning state for each element
  const [logoPosition, setLogoPosition] = useState('bottom-center');
  const [titlePosition, setTitlePosition] = useState('bottom-center');
  const [subheaderPosition, setSubheaderPosition] = useState('bottom-center');
  const [bodyPosition, setBodyPosition] = useState('bottom-center');
  const [categoryPosition, setCategoryPosition] = useState('bottom-center');
  const [categoryBgColor, setCategoryBgColor] = useState('#2563eb');
  const [categoryBorderRadius, setCategoryBorderRadius] = useState(50); // 0=square, 100=pill
  // True once a content/template config (or a user edit) set category
  // styling — the async brand-settings fetch must not clobber it. Category
  // resolution everywhere: content config > template > brand > default.
  const categoryConfigOverrideRef = useRef<{ color: boolean; radius: boolean }>({ color: false, radius: false });
  // Brand-level category styling (the fallback layer) — kept in a ref so
  // hydrating a config WITHOUT category styling can reset to brand values.
  const brandCategoryDefaultsRef = useRef<{ color: string; radius: number }>({ color: '#2563eb', radius: 50 });
  // Font Library family slug for the category badge; null = inherit cascade
  const [categoryFontFamily, setCategoryFontFamily] = useState<string | null>(null);
  const [showGradientControls, setShowGradientControls] = useState(false);
  const [showBorderControls, setShowBorderControls] = useState(false);
  // Element mode (Overlay controls hide on element selection, 2026-10-27):
  // a selected element closes whatever control is open and the overlay
  // controls stay gone until it is deselected; nothing reopens by itself.
  // The recorder keeps its own rules while a session runs (Fit/Fill stays).
  const elementMode = useElementMode(selection.selected, () => {
    setOpenCorner(null);            // Media or Settings, whichever is open
    setShowFitControls(false);
    setAspectMenuAnchor(null);
    setShowGradientControls(false);
    setShowBorderControls(false);
  });
  const gradientBtnRef = useRef<HTMLButtonElement>(null);
  const borderBtnRef = useRef<HTMLButtonElement>(null);

  // Track if any color picker popover is open (prevents toolbar from closing)
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);


  // Logo selection state
  const [selectedLogo, setSelectedLogo] = useState<Logo | null>(
    item.overlay_logo_url
      ? { url: item.overlay_logo_url, logoKey: item.overlay_logo_key || 'icon_white', displayName: '' } as Logo
      : brandDefaultLogo || null
  );
  const [logoScale, setLogoScale] = useState(1.0);
  const [categoryScale, setCategoryScale] = useState(1.0);
  // Gap below the logo / pill (ref px, null = pair default); text kinds carry theirs in their style.
  const [logoGap, setLogoGap] = useState<number | null>(null);
  const [categoryGap, setCategoryGap] = useState<number | null>(null);
  // Per-slide logo display mode (icon_only / name_only / icon_and_name).
  // Seeded from the brand-level prop but tracked locally so each slide can
  // override it independently. Persisted in overlay_config.logoDisplayMode.
  const [logoDisplayMode, setLogoDisplayMode] = useState<LogoDisplayMode>(
    (item.overlay_logo_display_mode as LogoDisplayMode) || brandLogoDisplayMode
  );
  const logoSelectorRef = useRef<LogoSelectorHandle>(null);
  // Tracks whether the user explicitly selected a logo (vs auto-seeded brand default).
  // When null, the render/publish path uses display mode fallback (icon/name circle).
  const effectiveLogoUrlRef = useRef<string | null>(item.overlay_logo_url || null);

  // Overlay template state (the list itself lives in overlayTemplatesRef —
  // the slide sync effect is its only reader since swipe-to-switch left)
  const [activeTemplateId, setActiveTemplateId] = useState<number | null>(null);
  const [defaultTemplateId, setDefaultTemplateId] = useState<number | null>(null);

  // Template management modal state
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Flag to prevent auto-save on initial load (only save after user makes changes)
  const [hasInitializedOverlay, setHasInitializedOverlay] = useState(false);
  // Tracks the overlay config snapshot we already persisted (or just loaded
  // from the backend). Auto-save compares against this to suppress the
  // immediate post-init "save" that would otherwise wipe overlay_template_id
  // even though the user hasn't actually changed anything yet.
  const lastSavedOverlayConfigRef = useRef<string | null>(null);

  // The user hasn't interacted with this card yet. Until they do, ANY config
  // drift is async brand data settling (settings fetch → categoryBgColor,
  // brand default logo → logoUrl, font manifest…), NOT an edit — auto-saving
  // it would PATCH overlay_config and wipe overlay_template_id on every card,
  // every page view (the 45-card PATCH wave that starved the gateway).
  const overlayUserTouchedRef = useRef(false);

  // Ref to track when we're applying a template (to prevent auto-save from clearing template reference)
  const isApplyingTemplateRef = useRef(false);

  // Overlay preview state
  const [isRenderingPreview, setIsRenderingPreview] = useState(false);
  const [showPreviewPopover, setShowPreviewPopover] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(item.rendered_overlay_url || null);
  const previewButtonRef = useRef<HTMLButtonElement>(null);
  // Source-media rect captured when a preview is requested, so the panel
  // can match its width (1:1 comparison) and spawn alongside it.
  const [previewAnchor, setPreviewAnchor] = useState<{ width: number; top: number; left: number; right: number } | null>(null);

  // Seed selectedLogo from the brand default once it loads, but only if the
  // user hasn't already picked something. Without this guard, brand data
  // arriving after the user picks a per-slide logo would silently revert it.
  const hasPersistedInitialLogo = useRef(!!item.overlay_logo_url);
  useEffect(() => {
    if (brandDefaultLogo && !selectedLogo) {
      setSelectedLogo(brandDefaultLogo);
    }
  }, [brandDefaultLogo, selectedLogo]);

  // Persist the initial logo to dedicated DB columns once the overlay is
  // initialized, so the publish path knows which logo the user sees.
  // Gated behind hasInitializedOverlay to avoid firing for every card on page load.
  useEffect(() => {
    if (hasInitializedOverlay && brandDefaultLogo && !hasPersistedInitialLogo.current) {
      hasPersistedInitialLogo.current = true;
      const logoToSave = selectedLogo || brandDefaultLogo;
      effectiveLogoUrlRef.current = logoToSave.url;
      tendApi.updateTendV2Content(item.id, {
        overlay_logo_url: logoToSave.url,
        overlay_logo_key: logoToSave.logoKey || 'icon_white',
        overlay_logo_display_mode: logoDisplayMode,
      });
    }
  }, [hasInitializedOverlay, brandDefaultLogo, selectedLogo, logoDisplayMode]);

  const [detailsOpen, setDetailsOpen] = useState(false);

  // View Prompt (CS/superadmin): the exact LLM input that generated this
  // content, for tactic traceability. Fetched on click; the gateway
  // hard-gates the path server-side.
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [tacticEditOpen, setTacticEditOpen] = useState(false);
  // Items handed off through the MCP server carry a synthetic tactic id
  // ("mcp:<render format>", or "mcp:external" with no format) — a label for
  // the queue and analytics, not a tactic file anyone can open.
  const isAgentHandOff = (item.tactic_id || '').startsWith('mcp:');
  const [promptData, setPromptData] = useState<
    Awaited<ReturnType<typeof tendApi.getContentInputPrompt>> | 'loading' | null
  >(null);
  const openPromptModal = useCallback(async () => {
    setPromptModalOpen(true);
    setPromptData('loading');
    const result = await tendApi.getContentInputPrompt(item.id);
    setPromptData(result);
  }, [item.id]);

  // Failed publish legs for THIS item — fetched once when the details
  // strip opens (null = not yet fetched), shown as a red block so the
  // platform's error sits next to the content it rejected.
  const [publishFailures, setPublishFailures] = useState<
    Array<{
      history_id: number | null; platforms: string[]; error_message: string | null; created_at: string | null;
      state?: 'retrying' | 'paused' | 'recovered' | 'failed';
      attempts?: number | null; max_attempts?: number | null;
      reason?: string | null; kind?: string | null; recovered_at?: string | null;
      recovered_via?: 'manual' | 'queue' | string | null;
      code?: number | string | null; details?: string | null;
    }> | null
  >(null);
  useEffect(() => { setPublishFailures(null); }, [item.id]);
  useEffect(() => {
    if (!detailsOpen || publishFailures !== null) return;
    let cancelled = false;
    tendApi.getContentPublishFailures(item.id).then((res) => {
      if (!cancelled) setPublishFailures(res.failures || []);
    });
    return () => { cancelled = true; };
  }, [detailsOpen, publishFailures, item.id]);

  // Calculate full post character count (including enabled hashtags and link)
  const charLimits = usePlatformCharLimits();
  const platformKey = item.platform.toLowerCase();
  const platformLimit = charLimits.limits[platformKey] || charLimits.default_limit;

  // Mirrors the publisher's recipe (backend services/post_text.py) so this
  // number is the one the queue holds on: text + " #tags" + "\n\n" credit +
  // "\n\n" link — the link as its SHORT form (base/slug), and only where the
  // post body carries it (not first-comment routed, never on Instagram or a
  // Pinterest pin, which have their own link fields).
  const fullPostCharCount = useMemo(() => {
    let count = editedText?.length || 0;

    if (hashtagsEnabled && editedHashtags && editedHashtags.length > 0) {
      const hashtagsStr = editedHashtags.map(tag =>
        tag.startsWith('#') ? tag : `#${tag}`
      ).join(' ');
      count += 1 + hashtagsStr.length; // +1 for space separator
    }

    // Pexels credit — appended to the post text when the toggle is on
    if (photoAttributionEnabled && editedPhotoAttribution) {
      count += 2 + editedPhotoAttribution.length; // +2 for "\n\n"
    }

    const linkInBody = linkEnabled && !!editedUrl && !firstCommentEnabled
      && platformKey !== 'instagram' && platformKey !== 'pinterest';
    if (linkInBody) {
      const linkLength = charLimits.short_link_length ?? editedUrl.length;
      count += 2 + linkLength; // +2 for "\n\n"
    }

    return count;
  }, [editedText, hashtagsEnabled, editedHashtags, linkEnabled, editedUrl, firstCommentEnabled,
      photoAttributionEnabled, editedPhotoAttribution, platformKey, charLimits.short_link_length]);

  const isOverLimit = fullPostCharCount > platformLimit;

  // Debounce the overlay text fields (500ms delay); the copy text, post
  // title, URL, tags and switches debounce inside their persisted fields.
  const debouncedTitle = useDebounce(editedTitle, 500);
  const debouncedSubheader = useDebounce(editedSubheader, 500);
  const debouncedBody = useDebounce(editedBody, 500);
  const debouncedScript = useDebounce(editedScript, 500);
  // One writer each for the single-mode title / subheader. The compare is
  // against what we LAST SAVED to the mode's field (falling back to the prop
  // before any save), so typing back to the original is still a save. The
  // item prop is deliberately NOT mutated here: the title/subheader sync
  // effect keys on those fields and rewrites the editable's innerHTML, which
  // would move the caret under a user who is still typing.
  const lastSavedTitleRef = useRef<{ generated_title?: string; source_title?: string }>({});
  const persistTitle = useCallback((value: string) => {
    const field = captionModeEnabled ? 'generated_title' : 'source_title';
    const known = lastSavedTitleRef.current[field] ?? item[field] ?? '';
    if (value === known) return;
    lastSavedTitleRef.current[field] = value;
    tendApi.updateTendV2Content(item.id, field === 'generated_title' ? { generated_title: value } : { source_title: value })
      .catch((err: unknown) => console.error('Failed to save overlay title:', err));
  }, [captionModeEnabled, item]);
  const lastSavedSubheaderRef = useRef<{ generated_subheader?: string; source_subhead?: string }>({});
  const persistSubheader = useCallback((value: string) => {
    const field = captionModeEnabled ? 'generated_subheader' : 'source_subhead';
    const known = lastSavedSubheaderRef.current[field] ?? item[field] ?? '';
    if (value === known) return;
    lastSavedSubheaderRef.current[field] = value;
    tendApi.updateTendV2Content(item.id, field === 'generated_subheader' ? { generated_subheader: value } : { source_subhead: value })
      .catch((err: unknown) => console.error('Failed to save overlay subheader:', err));
  }, [captionModeEnabled, item]);
  const debouncedTitleFontSize = useDebounce(titleFontSize, 300);

  // Save overlay title to backend when debounced title changes
  useEffect(() => {
    // Skip on first render
    if (isFirstTitleRender.current) {
      isFirstTitleRender.current = false;
      return;
    }

    // Only save if user actually edited (not programmatic toggle)
    if (!titleEditedByUser.current) {
      return;
    }
    titleEditedByUser.current = false;  // Reset flag

    // In carousel mode, save to the slide that was being edited (not current slide)
    // This prevents race condition when user navigates quickly after editing
    if (isCarouselMode && editingTitleSlideIdRef.current) {
      const slideId = editingTitleSlideIdRef.current;
      editingTitleSlideIdRef.current = null;  // Clear after capturing
      carouselApi.updateSlide(slideId, { custom_title: debouncedTitle }).then(() => {
        setSlides(prev => prev.map(s =>
          s.id === slideId ? { ...s, custom_title: debouncedTitle, title: debouncedTitle } : s
        ));
      }).catch(err => console.error('Failed to save slide title:', err));
      return;
    }

    // Single mode — one writer, compared against what we last saved.
    persistTitle(debouncedTitle);
  }, [debouncedTitle, persistTitle, isCarouselMode]);

  // Save overlay subheader to backend when debounced value changes
  useEffect(() => {
    if (isFirstSubheaderRender.current) {
      isFirstSubheaderRender.current = false;
      return;
    }

    // Only save if user actually edited (not programmatic toggle)
    if (!subheaderEditedByUser.current) {
      return;
    }
    subheaderEditedByUser.current = false;  // Reset flag

    // In carousel mode, save to the slide that was being edited (not current slide)
    // This prevents race condition when user navigates quickly after editing
    if (isCarouselMode && editingSubheaderSlideIdRef.current) {
      const slideId = editingSubheaderSlideIdRef.current;
      editingSubheaderSlideIdRef.current = null;  // Clear after capturing
      carouselApi.updateSlide(slideId, { custom_subheader: debouncedSubheader }).then(() => {
        setSlides(prev => prev.map(s =>
          s.id === slideId ? { ...s, custom_subheader: debouncedSubheader, subheader: debouncedSubheader } : s
        ));
      }).catch(err => console.error('Failed to save slide subheader:', err));
      return;
    }

    // Single mode — one writer, compared against what we last saved.
    persistSubheader(debouncedSubheader);
  }, [debouncedSubheader, persistSubheader, isCarouselMode]);

  // Save overlay body to backend when debounced value changes.
  // Carousel mode keeps the original flag-based path (gated by
  // bodyEditedByUser so slide-sync navigation doesn't trigger spurious saves).
  // Single mode uses lastSavedBodyRef instead — bypasses any subtle flag
  // resets and just compares against the last value we PATCHed.
  useEffect(() => {
    if (isFirstBodyRender.current) {
      isFirstBodyRender.current = false;
      lastSavedBodyRef.current = debouncedBody;
      return;
    }

    // Carousel mode: same path as before.
    if (isCarouselMode) {
      if (!bodyEditedByUser.current) return;
      bodyEditedByUser.current = false;
      if (editingBodySlideIdRef.current) {
        const slideId = editingBodySlideIdRef.current;
        editingBodySlideIdRef.current = null;
        carouselApi.updateSlide(slideId, { generated_body: debouncedBody }).then(() => {
          setSlides(prev => prev.map(s =>
            s.id === slideId ? { ...s, generated_body: debouncedBody } : s
          ));
        }).catch(err => console.error('Failed to save slide body:', err));
      }
      return;
    }

    // Single mode — fire on any debounced change vs last persisted value.
    if (debouncedBody !== lastSavedBodyRef.current) {
      const next = debouncedBody;
      lastSavedBodyRef.current = next;
      // Keep the item reference in sync so a parent holding a cached items
      // array doesn't reset us on remount before its next refetch.
      (item as any).generated_body = next;
      tendApi.updateTendV2Content(item.id, { generated_body: next })
        .catch((err: unknown) => console.error('Failed to save content body:', err));
    }
  }, [debouncedBody, item, isCarouselMode]);

  // Save script when debounced value changes. Mirrors the body save path:
  // - Carousel mode: PATCH the slide that was being edited (captured at
  //   input time on editingScriptSlideIdRef) so quick navigation after an
  //   edit doesn't accidentally write the value into the next slide.
  // - Single mode: PATCH the content item directly; compare against the
  //   last persisted value so we don't fight stale parent props.
  useEffect(() => {
    if (isFirstScriptRender.current) {
      isFirstScriptRender.current = false;
      lastSavedScriptRef.current = debouncedScript;
      return;
    }
    if (!scriptEditedByUser.current) return;

    if (isCarouselMode) {
      scriptEditedByUser.current = false;
      if (editingScriptSlideIdRef.current) {
        const slideId = editingScriptSlideIdRef.current;
        editingScriptSlideIdRef.current = null;
        carouselApi.updateSlide(slideId, { script: debouncedScript }).then(() => {
          setSlides(prev => prev.map(s =>
            s.id === slideId ? { ...s, script: debouncedScript } : s
          ));
        }).catch(err => console.error('Failed to save slide script:', err));
      }
      return;
    }

    scriptEditedByUser.current = false;
    if (debouncedScript !== lastSavedScriptRef.current) {
      const next = debouncedScript;
      lastSavedScriptRef.current = next;
      (item as any).script = next;
      tendApi.updateTendV2Content(item.id, { script: next })
        .catch((err: unknown) => console.error('Failed to save content script:', err));
    }
  }, [debouncedScript, item, isCarouselMode]);

  // Copy text / post title input — the persisted field owns the save.
  const handleTextInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    editText(e.currentTarget.textContent || '');
  }, [editText]);

  const handlePostTitleInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    editPostTitle(e.currentTarget.textContent || '');
  }, [editPostTitle]);

  // Track if title/subheader/body changes are from user edit vs programmatic toggle
  const titleEditedByUser = useRef(false);
  const subheaderEditedByUser = useRef(false);
  const bodyEditedByUser = useRef(false);
  const scriptEditedByUser = useRef(false);

  // Handle title input - mark as user edit and update correct mode-specific state.
  // Captures sanitized innerHTML so per-content rich-text emphasis (CMD+B/I/U on
  // a selection) survives saves. Field-wide emphasis lives in titleStyle and is
  // applied via CSS, not stored inside the text itself.
  const handleTitleInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    const newTitle = emptyIfBlank(sanitizeOverlayHtml(e.currentTarget.innerHTML || ''));
    titleEditedByUser.current = true;
    if (isCarouselMode && slides[currentSlideIndex]) {
      editingTitleSlideIdRef.current = slides[currentSlideIndex].id;
    }
    setEditedTitle(newTitle);
    if (captionModeEnabled) {
      setEditedGeneratedTitle(newTitle);
    } else {
      setEditedSourceTitle(newTitle);
    }
  }, [captionModeEnabled, isCarouselMode, slides, currentSlideIndex]);

  // Handle subheader input - mark as user edit and update correct mode-specific state
  const handleSubheaderInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    const newSubheader = emptyIfBlank(sanitizeOverlayHtml(e.currentTarget.innerHTML || ''));
    subheaderEditedByUser.current = true;
    if (isCarouselMode && slides[currentSlideIndex]) {
      editingSubheaderSlideIdRef.current = slides[currentSlideIndex].id;
    }
    setEditedSubheader(newSubheader);
    if (captionModeEnabled) {
      setEditedGeneratedSubheader(newSubheader);
    } else {
      setEditedSourceSubheader(newSubheader);
    }
  }, [captionModeEnabled, isCarouselMode, slides, currentSlideIndex]);

  // Handle body input — same pattern as title/subheader. The body element is
  // a multi-line contentEditable holding generated_body content.
  const handleBodyInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    const newBody = emptyIfBlank(sanitizeOverlayHtml(e.currentTarget.innerHTML || ''));
    bodyEditedByUser.current = true;
    if (isCarouselMode && slides[currentSlideIndex]) {
      editingBodySlideIdRef.current = slides[currentSlideIndex].id;
    }
    setEditedBody(newBody);
  }, [isCarouselMode, slides, currentSlideIndex]);

  // Handle script input — multiline plain-text editor used both in the
  // Details section and inside the PIP teleprompter. The same state powers
  // both surfaces, so an edit in one place updates the other instantly.
  const handleScriptInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    // Plain text — scripts have no styling. innerText keeps line breaks
    // without injecting <div>/<br> noise that confuses the LLM downstream.
    const newScript = e.currentTarget.innerText || '';
    scriptEditedByUser.current = true;
    if (isCarouselMode && slides[currentSlideIndex]) {
      editingScriptSlideIdRef.current = slides[currentSlideIndex].id;
    }
    setEditedScript(newScript);
  }, [isCarouselMode, slides, currentSlideIndex]);

  // Bridge for the PIP teleprompter: same state path as handleScriptInput
  // but takes a plain string (the PIP's contentEditable lives in another
  // document, so we can't share a SyntheticEvent). Also pushes the value
  // back into the Details editor so both surfaces stay in sync.
  const handleScriptTextChange = useCallback((next: string) => {
    scriptEditedByUser.current = true;
    if (isCarouselMode && slides[currentSlideIndex]) {
      editingScriptSlideIdRef.current = slides[currentSlideIndex].id;
    }
    setEditedScript(next);
    if (scriptRef.current && scriptRef.current.innerText !== next) {
      scriptRef.current.innerText = next;
    }
  }, [isCarouselMode, slides, currentSlideIndex]);

  // Belt-and-suspenders: when user blurs the body, force an immediate save if
  // the content differs from what's persisted. The debounced effect should
  // already handle this, but the empty→non-empty transition on single posts
  // has been reported to occasionally not persist; an explicit blur save
  // closes that gap.
  const handleBodyBlur = useCallback(() => {
    setIsBodyFocused(false);
    if (isCarouselMode) return; // carousel save path is reliable; only patch single mode
    const current = emptyIfBlank(sanitizeOverlayHtml(bodyRef.current?.innerHTML || ''));
    if (current !== lastSavedBodyRef.current) {
      lastSavedBodyRef.current = current;
      setEditedBody(current);
      (item as any).generated_body = current;
      tendApi.updateTendV2Content(item.id, { generated_body: current })
        .catch(err => console.error('Failed to save content body on blur:', err));
    }
  }, [isCarouselMode, item]);

  // Bind CMD+Shift+X to strikethrough (browsers don't bind it natively).
  // CMD+B/I/U are handled by contentEditable's default execCommand behavior.
  const handleTextElementKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'x') {
      e.preventDefault();
      try { document.execCommand('strikeThrough'); } catch { /* noop */ }
    }
  }, []);

  // Handle category input — the DOM's text becomes state; the field saves on
  // its debounce, on blur and on unmount.
  // The pill's fallback, the same rule the renders use (category_text.py):
  // a category nobody ever set (null) shows the source section; a pill a
  // person CLEARED ('') stays cleared — placeholder here, no pill published.
  const categoryFallback = item.source_category == null ? (item.source_section || '') : '';
  const handleCategoryInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    editCategory(e.currentTarget.textContent || '');
  }, [editCategory]);
  const handleCategoryBlur = useCallback(() => {
    setIsCategoryFocused(false);
    flushCategory();
  }, [flushCategory]);
  // The pill remounts when it moves to another row (each row is its own
  // container), so the new node is seeded from STATE — with the source
  // section as the fallback the render also uses — never from the item
  // prop. Stable identity: React calls it on attach/detach only, so clearing
  // the text mid-edit cannot re-seed the fallback under the caret.
  const attachCategoryRef = useCallback((el: HTMLDivElement | null) => {
    categoryRef.current = el;
    if (el && !el.textContent) {
      el.textContent = getCategoryValue() || categoryFallback;
    }
  }, [getCategoryValue, categoryFallback]);

  // Blur + unmount flush for title / subheader / body (single mode). Same
  // lost-save window the category had: a bare debounce timer dies with the
  // card when the modal closes within 500 ms of the last keystroke. State,
  // not the DOM, is the source here — refs are already detached by the time
  // an unmount cleanup runs.
  const latestTextRef = useRef({ title: editedTitle, subheader: editedSubheader, body: editedBody });
  latestTextRef.current = { title: editedTitle, subheader: editedSubheader, body: editedBody };
  const flushTitle = useCallback(() => {
    if (isCarouselMode || !titleEditedByUser.current) return;
    titleEditedByUser.current = false;
    persistTitle(latestTextRef.current.title);
  }, [isCarouselMode, persistTitle]);
  const flushSubheader = useCallback(() => {
    if (isCarouselMode || !subheaderEditedByUser.current) return;
    subheaderEditedByUser.current = false;
    persistSubheader(latestTextRef.current.subheader);
  }, [isCarouselMode, persistSubheader]);
  const flushBody = useCallback(() => {
    if (isCarouselMode) return;
    const current = latestTextRef.current.body;
    if (current === lastSavedBodyRef.current) return;
    lastSavedBodyRef.current = current;
    (item as any).generated_body = current;
    tendApi.updateTendV2Content(item.id, { generated_body: current })
      .catch((err: unknown) => console.error('Failed to save content body on unmount:', err));
  }, [isCarouselMode, item]);
  const handleTitleBlur = useCallback(() => { setIsTitleFocused(false); flushTitle(); }, [flushTitle]);
  const handleSubheaderBlur = useCallback(() => { setIsSubheaderFocused(false); flushSubheader(); }, [flushSubheader]);
  const flushTextFieldsRef = useRef<() => void>(() => {});
  flushTextFieldsRef.current = () => { flushTitle(); flushSubheader(); flushBody(); };
  useEffect(() => () => { flushTextFieldsRef.current(); }, []);

  // Clamp overflow detection (idle view only): "more" shows only when the
  // clamped text is actually cut off.
  useLayoutEffect(() => {
    if (isEditingText || !clampRef.current) return;
    const el = clampRef.current;
    setTextOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [editedText, isEditingText]);

  // Entering edit: mount the (childless) contentEditable, hydrate it
  // imperatively from state, focus with the caret at the end.
  useLayoutEffect(() => {
    if (!isEditingText || !textRef.current) return;
    textRef.current.textContent = editedText;
    textRef.current.focus();
    const sel = window.getSelection();
    if (sel) {
      const range = document.createRange();
      range.selectNodeContents(textRef.current);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingText]);

  // Track previous item.id to detect when we switch to a different item
  const prevItemIdRef = useRef(item.id);

  // Sync mode-specific edited values when ITEM changes (new data from API)
  // Don't sync on captionModeEnabled changes - the toggle handler manages that
  useEffect(() => {
    // Check if this is a different item (always sync on item change)
    const isNewItem = prevItemIdRef.current !== item.id;
    prevItemIdRef.current = item.id;

    // Skip sync if user is actively editing (prevents race condition with saves)
    // But always sync if switching to a different item
    if (!isNewItem && (titleEditedByUser.current || subheaderEditedByUser.current)) {
      return;
    }

    // Update mode-specific states from API data
    setEditedSourceTitle(item.source_title || contentTitle || '');
    setEditedGeneratedTitle(item.generated_title || '');
    setEditedSourceSubheader(item.source_subhead || '');
    setEditedGeneratedSubheader(item.generated_subheader || '');

    // Update current display based on caption mode
    const newTitle = captionModeEnabled
      ? (item.generated_title || '')
      : (item.source_title || contentTitle || '');
    const newSubheader = captionModeEnabled
      ? (item.generated_subheader || '')
      : (item.source_subhead || '');

    setEditedTitle(newTitle);
    setEditedSubheader(newSubheader);

    // Force DOM update
    if (titleRef.current) {
      titleRef.current.innerHTML = newTitle;
    }
    if (subheaderRef.current) {
      subheaderRef.current.innerHTML = newSubheader;
    }
  }, [item.id, item.generated_title, item.source_title, item.generated_subheader, item.source_subhead, contentTitle]);

  // Sync title font size when item changes
  // Skip in carousel mode - font size comes from slide's overlay_config
  useEffect(() => {
    if (isCarouselMode) return;
    setTitleFontSize(item.title_font_size || 14);
    isFirstFontSizeChange.current = true;
  }, [item.id, item.title_font_size, isCarouselMode]);

  // Save title font size to backend when debounced value changes
  // Skip in carousel mode - font size is saved via overlay config auto-save
  useEffect(() => {
    if (isCarouselMode) return;
    if (isFirstFontSizeChange.current) {
      isFirstFontSizeChange.current = false;
      return;
    }
    if (debouncedTitleFontSize !== item.title_font_size) {
      tendApi.updateTendV2Content(item.id, { title_font_size: debouncedTitleFontSize });
    }
  }, [debouncedTitleFontSize, item.id, item.title_font_size, isCarouselMode]);

  // Selection + persistence live in useMediaSelection (anchor to the
  // server primary, one setter, saves only on a user action). Navigate with
  // wraparound: a dot click is a choice, so it persists.
  const navigateToImage = useCallback((targetIndex: number) => {
    if (localImages.length <= 1) return;
    let newIndex = targetIndex;
    if (targetIndex < 0) {
      newIndex = localImages.length - 1; // Wrap to last
    } else if (targetIndex >= localImages.length) {
      newIndex = 0; // Wrap to first
    }
    selectMedia(newIndex, { persist: true });
  }, [localImages.length, selectMedia]);

  // (Wheel-wraparound handler removed with swipe navigation — the media
  // strip is programmatic-only now; see the strip's comment.)

  // Format tactic name for display
  const formatTacticName = (tacticId: string) => {
    return tacticId
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleCopy = () => {
    // Use editedText for copy to get latest edits
    const textToCopy = editedText +
      (item.secondary_text ? '\n\n' + item.secondary_text : '') +
      (item.hashtags?.length ? '\n\n' + item.hashtags.map(t => t.startsWith('#') ? t : `#${t}`).join(' ') : '');

    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Batched count for the chat-icon badge — one bulk request per feed
  // render pass (PublishStatusContext discipline: never per-card fetches).
  useEffect(() => {
    if (brandId == null) return;
    let alive = true;
    feedbackCounts.getCount(brandId, 'tendv2_content', String(item.id))
      .then((e) => { if (alive) setFeedbackEntry(e); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, item.id]);

  const openFeedback = (e: React.MouseEvent<HTMLButtonElement>) => {
    // A host (TendedItemModal) may own the panel as a side column instead.
    if (onFeedbackClick) { onFeedbackClick(); return; }
    if (feedbackOpen) { setFeedbackOpen(false); return; }
    // Anchor = the card ROOT (header chrome's parent spans header +
    // content + details); the panel follows the viewport but clamps to
    // this element's top/bottom while the page scrolls.
    const chrome = (e.currentTarget as HTMLElement).closest('.tend-card-chrome');
    setFeedbackAnchor((chrome?.parentElement as HTMLElement) ?? (e.currentTarget as HTMLElement));
    setFeedbackOpen(true);
  };

  // Load brand overlay settings (including default template, category styling,
  // channel aspect maps for unstamped-content fallback + the Recommended star)
  useEffect(() => {
    if (!brandId) return;
    tendApi.getBrandOverlaySettings(brandId).then((settings) => {
      if (settings) {
        setDefaultTemplateId(settings.default_overlay_template_id);
        // Brand category styling is the FALLBACK layer — never clobber a
        // value hydrated from content/template config or edited by the user
        brandCategoryDefaultsRef.current = {
          color: settings.category_bg_color ?? '#2563eb',
          radius: settings.category_border_radius ?? 50,
        };
        if (!categoryConfigOverrideRef.current.radius) {
          setCategoryBorderRadius(brandCategoryDefaultsRef.current.radius);
        }
        if (!categoryConfigOverrideRef.current.color) {
          setCategoryBgColor(brandCategoryDefaultsRef.current.color);
        }
        setBrandAspectMap(settings.channel_aspect ?? {});
        setUniversalAspectMap(settings.universal_channel_aspect ?? null);
      }
    });
  }, [brandId]);

  // Load overlay templates from backend when brandId is available
  // and auto-apply the remembered template for this card (single mode only)
  // In carousel mode, each slide has its own overlay_config loaded by the carousel sync effect
  useEffect(() => {
    if (!brandId) return;
    tendApi.getOverlayTemplates(brandId).then((templates) => {
      if (templates.length > 0) {
        const mapped = templates.map((t) => ({
          id: t.id,
          brand_id: t.brand_id,
          name: t.name,
          description: t.description,
          ...t.config,
        }));
        overlayTemplatesRef.current = mapped;

        // Skip auto-applying content-level template/config in carousel mode.
        // Carousel slides have their own overlay_config loaded by carousel sync effect.
        // Use ref to get current value (async callback would have stale closure).
        // CRITICAL: still mark hasInitializedOverlay = true before returning, otherwise
        // the auto-save effect bails forever and overlay edits never persist.
        if (isCarouselModeRef.current) {
          // Apply template config for the current slide now that templates are loaded.
          // The initial slide sync ran before templates were available, so overlay states
          // fell to defaults. We need to re-apply from the resolved template.
          isSyncingSlideRef.current = true;
          // Hydration start — disarm the touched flag (see slide-sync effect)
          overlayUserTouchedRef.current = false;
          const currentSlides = slidesRef.current;
          const idx = currentSlideIndexRef.current;
          if (currentSlides.length > 0 && idx < currentSlides.length) {
            const slide = currentSlides[idx];
            if (slide.overlay_template_id) {
              const match = mapped.find(t => t.id === slide.overlay_template_id);
              if (match) {
                setShowOverlayLogo(match.showOverlayLogo);
                setShowOverlayTitle(match.showOverlayTitle);
                setShowOverlaySubheader(match.showOverlaySubheader ?? false);
                setShowOverlayBody(match.showOverlayBody ?? false);
                setShowOverlayCategory(match.showOverlayCategory);
                setShowOverlayGradient(match.showOverlayGradient);
                setLogoPosition(match.logoPosition);
                setTitlePosition(match.titlePosition);
                setSubheaderPosition(match.subheaderPosition);
                setBodyPosition(match.bodyPosition || 'bottom-center');
                setCategoryPosition(match.categoryPosition);
                setTitleFontSize(match.titleFontSize);
                setSubheaderFontSize(match.subheaderFontSize);
                setBodyFontSize(match.bodyFontSize ?? 12);
                setLogoScale(match.logoScale);
                setCategoryScale(match.categoryScale ?? 1.0);
                setLogoGap(match.logoGap ?? null);
                setCategoryGap(match.categoryGap ?? null);
                setGradientDirection(match.gradientDirection || 'up');
                setGradientColor(match.gradientColor || '#000000');
                setGradientOpacity(match.gradientOpacity ?? 70);
                setTitleBgEnabled(match.titleBgEnabled ?? false);
                setTitleBgColor(match.titleBgColor || '#000000');
                setTitleBgOpacity(match.titleBgOpacity ?? 80);
                setTitleTextColor(match.titleTextColor || '#ffffff');
                setSubheaderBgEnabled(match.subheaderBgEnabled ?? false);
                setSubheaderBgColor(match.subheaderBgColor || '#000000');
                setSubheaderBgOpacity(match.subheaderBgOpacity ?? 80);
                setSubheaderTextColor(match.subheaderTextColor || '#ffffff');
                setBodyBgEnabled(match.bodyBgEnabled ?? false);
                setBodyBgColor(match.bodyBgColor || '#000000');
                setBodyBgOpacity(match.bodyBgOpacity ?? 80);
                setBodyTextColor(match.bodyTextColor || '#ffffff');
                setBorderEnabled(match.borderEnabled ?? false);
                setBorderColor(match.borderColor || '#ffffff');
                setBorderThickness(match.borderThickness ?? 4);
                setCategoryBgColor(match.categoryBgColor || brandCategoryDefaultsRef.current.color);
                setCategoryBorderRadius(
                  typeof (match as any).categoryBorderRadius === 'number'
                    ? (match as any).categoryBorderRadius
                    : brandCategoryDefaultsRef.current.radius
                );
                categoryConfigOverrideRef.current = {
                  color: !!match.categoryBgColor,
                  radius: typeof (match as any).categoryBorderRadius === 'number',
                };
                setCategoryFontFamily((match as any).categoryFontFamily ?? null);
                setTitleStyle(hydrateTextElementStyle(match as any, 'title'));
                setSubheaderStyle(hydrateTextElementStyle(match as any, 'subheader'));
                setBodyStyle(hydrateTextElementStyle(match as any, 'body'));
                setDesignElements(Array.isArray((match as any).designElements) ? (match as any).designElements : []);
                // Template-carried logo (guarded by explicit content selection)
                if (!effectiveLogoUrlRef.current && !item.overlay_logo_display_mode) {
                  if ((match as any).logoUrl) {
                    setSelectedLogo({ url: (match as any).logoUrl, logoKey: (match as any).logoKey || 'icon_white', displayName: '' } as Logo);
                  }
                  if (isLogoDisplayMode((match as any).logoDisplayMode)) {
                    setLogoDisplayMode((match as any).logoDisplayMode);
                  }
                }
                setActiveTemplateId(slide.overlay_template_id);
              }
            }
          }
          setTimeout(() => { isSyncingSlideRef.current = false; }, 600);
          setHasInitializedOverlay(true);
          return;
        }

        // Auto-apply remembered template for this card
        const savedTemplateId = item.overlay_template_id;
        if (savedTemplateId) {
          const match = mapped.find(t => t.id === savedTemplateId);
          if (match) {
            setShowOverlayLogo(match.showOverlayLogo);
            setShowOverlayTitle(match.showOverlayTitle);
            setShowOverlaySubheader(match.showOverlaySubheader);
            setShowOverlayBody(match.showOverlayBody ?? false);
            setShowOverlayCategory(match.showOverlayCategory);
            setShowOverlayGradient(match.showOverlayGradient);
            setLogoPosition(match.logoPosition);
            setTitlePosition(match.titlePosition);
            setSubheaderPosition(match.subheaderPosition);
            setBodyPosition(match.bodyPosition || 'bottom-center');
            setCategoryPosition(match.categoryPosition);
            setTitleFontSize(match.titleFontSize);
            setSubheaderFontSize(match.subheaderFontSize);
            setBodyFontSize(match.bodyFontSize ?? 12);
            setLogoScale(match.logoScale);
            setCategoryScale(match.categoryScale ?? 1.0);
            setLogoGap(match.logoGap ?? null);
            setCategoryGap(match.categoryGap ?? null);
            setCategoryBgColor(match.categoryBgColor || brandCategoryDefaultsRef.current.color);
            setCategoryBorderRadius(
              typeof (match as any).categoryBorderRadius === 'number'
                ? (match as any).categoryBorderRadius
                : brandCategoryDefaultsRef.current.radius
            );
            categoryConfigOverrideRef.current = {
              color: !!match.categoryBgColor,
              radius: typeof (match as any).categoryBorderRadius === 'number',
            };
            // Load extended controls
            setGradientDirection(match.gradientDirection || 'up');
            setGradientColor(match.gradientColor || '#000000');
            setGradientOpacity(match.gradientOpacity ?? 70);
            setTitleBgEnabled(match.titleBgEnabled || false);
            setTitleBgColor(match.titleBgColor || '#000000');
            setTitleBgOpacity(match.titleBgOpacity ?? 80);
            setTitleTextColor(match.titleTextColor || '#ffffff');
            setSubheaderBgEnabled(match.subheaderBgEnabled || false);
            setSubheaderBgColor(match.subheaderBgColor || '#000000');
            setSubheaderBgOpacity(match.subheaderBgOpacity ?? 80);
            setSubheaderTextColor(match.subheaderTextColor || '#ffffff');
            setBodyBgEnabled(match.bodyBgEnabled || false);
            setBodyBgColor(match.bodyBgColor || '#000000');
            setBodyBgOpacity(match.bodyBgOpacity ?? 80);
            setBodyTextColor(match.bodyTextColor || '#ffffff');
            setBorderEnabled(match.borderEnabled || false);
            setBorderColor(match.borderColor || '#ffffff');
            setBorderThickness(match.borderThickness ?? 4);
            setCategoryFontFamily((match as any).categoryFontFamily ?? null);
            setTitleStyle(hydrateTextElementStyle(match as any, 'title'));
            setSubheaderStyle(hydrateTextElementStyle(match as any, 'subheader'));
            setBodyStyle(hydrateTextElementStyle(match as any, 'body'));
            setDesignElements(Array.isArray((match as any).designElements) ? (match as any).designElements : []);
            // Template-carried logo flows down unless the content already has
            // an explicit selection of its own (copy-on-apply model).
            if (!effectiveLogoUrlRef.current && !item.overlay_logo_display_mode) {
              if ((match as any).logoUrl) {
                setSelectedLogo({ url: (match as any).logoUrl, logoKey: (match as any).logoKey || 'icon_white', displayName: '' } as Logo);
              }
              if (isLogoDisplayMode((match as any).logoDisplayMode)) {
                setLogoDisplayMode((match as any).logoDisplayMode);
              }
            }
            setActiveTemplateId(match.id);
          }
        } else if (item.overlay_config) {
          // Load from "dummy template" (unsaved per-content config)
          const cfg = item.overlay_config as OverlayTemplateConfig;
          if (cfg.showOverlayLogo !== undefined) setShowOverlayLogo(cfg.showOverlayLogo);
          if (cfg.showOverlayTitle !== undefined) setShowOverlayTitle(cfg.showOverlayTitle);
          if (cfg.showOverlaySubheader !== undefined) setShowOverlaySubheader(cfg.showOverlaySubheader);
          if (cfg.showOverlayCategory !== undefined) setShowOverlayCategory(cfg.showOverlayCategory);
          if (cfg.showOverlayGradient !== undefined) setShowOverlayGradient(cfg.showOverlayGradient);
          if (cfg.showOverlayBody !== undefined) setShowOverlayBody(cfg.showOverlayBody);
          if (cfg.logoPosition) setLogoPosition(cfg.logoPosition);
          if (cfg.titlePosition) setTitlePosition(cfg.titlePosition);
          if (cfg.subheaderPosition) setSubheaderPosition(cfg.subheaderPosition);
          if (cfg.bodyPosition) setBodyPosition(cfg.bodyPosition);
          if (cfg.categoryPosition) setCategoryPosition(cfg.categoryPosition);
          if (cfg.titleFontSize !== undefined) setTitleFontSize(cfg.titleFontSize);
          if (cfg.subheaderFontSize !== undefined) setSubheaderFontSize(cfg.subheaderFontSize);
          if (cfg.bodyFontSize !== undefined) setBodyFontSize(cfg.bodyFontSize);
          if (cfg.logoScale !== undefined) setLogoScale(cfg.logoScale);
          if (cfg.categoryScale !== undefined) setCategoryScale(cfg.categoryScale);
          setLogoGap(cfg.logoGap ?? null);
          setCategoryGap(cfg.categoryGap ?? null);
          // Extended controls
          if (cfg.gradientDirection) setGradientDirection(cfg.gradientDirection);
          if (cfg.gradientColor) setGradientColor(cfg.gradientColor);
          if (cfg.gradientOpacity !== undefined) setGradientOpacity(cfg.gradientOpacity);
          if (cfg.titleBgEnabled !== undefined) setTitleBgEnabled(cfg.titleBgEnabled);
          if (cfg.titleBgColor) setTitleBgColor(cfg.titleBgColor);
          if (cfg.titleBgOpacity !== undefined) setTitleBgOpacity(cfg.titleBgOpacity);
          if (cfg.titleTextColor) setTitleTextColor(cfg.titleTextColor);
          if (cfg.subheaderBgEnabled !== undefined) setSubheaderBgEnabled(cfg.subheaderBgEnabled);
          if (cfg.subheaderBgColor) setSubheaderBgColor(cfg.subheaderBgColor);
          if (cfg.subheaderBgOpacity !== undefined) setSubheaderBgOpacity(cfg.subheaderBgOpacity);
          if (cfg.subheaderTextColor) setSubheaderTextColor(cfg.subheaderTextColor);
          if (cfg.bodyBgEnabled !== undefined) setBodyBgEnabled(cfg.bodyBgEnabled);
          if (cfg.bodyBgColor) setBodyBgColor(cfg.bodyBgColor);
          if (cfg.bodyBgOpacity !== undefined) setBodyBgOpacity(cfg.bodyBgOpacity);
          if (cfg.bodyTextColor) setBodyTextColor(cfg.bodyTextColor);
          if (cfg.borderEnabled !== undefined) setBorderEnabled(cfg.borderEnabled);
          if (cfg.borderColor) setBorderColor(cfg.borderColor);
          if (cfg.borderThickness !== undefined) setBorderThickness(cfg.borderThickness);
          // Extended per-element styling
          setTitleStyle(hydrateTextElementStyle(cfg, 'title'));
          setSubheaderStyle(hydrateTextElementStyle(cfg, 'subheader'));
          setBodyStyle(hydrateTextElementStyle(cfg, 'body'));
          setCategoryFontFamily(cfg.categoryFontFamily ?? null);
          // Category styling: per-content config overrides the brand default
          // (brand-settings fetch checks this ref before applying its values)
          if (cfg.categoryBgColor) {
            setCategoryBgColor(cfg.categoryBgColor);
            categoryConfigOverrideRef.current.color = true;
          }
          if (typeof cfg.categoryBorderRadius === 'number') {
            setCategoryBorderRadius(cfg.categoryBorderRadius);
            categoryConfigOverrideRef.current.radius = true;
          }
          // Design elements
          setDesignElements(Array.isArray(cfg.designElements) ? cfg.designElements : []);
          // Restore logo selection if saved (for curated content)
          if (cfg.logoUrl) {
            setSelectedLogo({
              id: 0, // ID not stored, using placeholder
              url: cfg.logoUrl,
              logoKey: cfg.logoKey || 'icon_white',
              displayName: 'Saved Logo',
              isDefault: false,
              displayOrder: 0,
            });
          }
          if (isLogoDisplayMode(cfg.logoDisplayMode)) {
            setLogoDisplayMode(cfg.logoDisplayMode);
          }
          // Restore caption mode if saved
          if (cfg.captionModeEnabled !== undefined) {
            setCaptionModeEnabled(cfg.captionModeEnabled);
            // Update displayed text based on restored caption mode
            const newTitle = cfg.captionModeEnabled
              ? (item.generated_title || '')
              : (item.source_title || contentTitle || '');
            const newSubheader = cfg.captionModeEnabled
              ? (item.generated_subheader || '')
              : (item.source_subhead || '');
            setEditedTitle(newTitle);
            setEditedSubheader(newSubheader);
          }
          // No active template - this is a "dummy template"
          setActiveTemplateId(null);
        }
      }
      // Hydration completed — disarm the touched flag so pre-init clicks
      // don't count as overlay edits (see slide-sync effect).
      overlayUserTouchedRef.current = false;
      // Mark overlay as initialized to enable auto-save
      setHasInitializedOverlay(true);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  // Single source of truth for the current overlay config snapshot.
  // Both the auto-save effect and ad-hoc consumers (handleRenderVideo,
  // preview button) read from this so we never serialize a stale or partial
  // config. Recomputed whenever any constituent piece of state changes.
  const currentOverlayConfig = useMemo<OverlayTemplateConfig>(() => ({
    showOverlayLogo,
    showOverlayTitle,
    showOverlaySubheader,
    showOverlayCategory,
    showOverlayGradient,
    showOverlayBody,
    logoPosition,
    titlePosition,
    subheaderPosition,
    bodyPosition,
    categoryPosition,
    titleFontSize,
    subheaderFontSize,
    bodyFontSize,
    logoScale,
    categoryScale,
    logoGap,
    categoryGap,
    categoryBgColor,
    // Extended controls
    gradientDirection,
    gradientColor,
    gradientOpacity,
    titleBgEnabled,
    titleBgColor,
    titleBgOpacity,
    titleTextColor,
    subheaderBgEnabled,
    subheaderBgColor,
    subheaderBgOpacity,
    subheaderTextColor,
    bodyBgEnabled,
    bodyBgColor,
    bodyBgOpacity,
    bodyTextColor,
    borderEnabled,
    borderColor,
    borderThickness,
    // Per-element extended styling (Title)
    titleBold: titleStyle.bold,
    titleItalic: titleStyle.italic,
    titleUnderline: titleStyle.underline,
    titleStrikethrough: titleStyle.strikethrough,
    titleShadow: titleStyle.shadow,
    titleTextCase: titleStyle.textCase,
    titleBorderEnabled: titleStyle.borderEnabled,
    titleBorderColor: titleStyle.borderColor,
    titleBorderThickness: titleStyle.borderThickness,
    titleBorderRadius: titleStyle.borderRadius,
    titleGap: titleStyle.gap ?? null,
    titleWidth: titleStyle.width,
    titleWidthOffset: titleStyle.widthOffset,
    // Per-element extended styling (Subheader)
    subheaderBold: subheaderStyle.bold,
    subheaderItalic: subheaderStyle.italic,
    subheaderUnderline: subheaderStyle.underline,
    subheaderStrikethrough: subheaderStyle.strikethrough,
    subheaderShadow: subheaderStyle.shadow,
    subheaderTextCase: subheaderStyle.textCase,
    subheaderBorderEnabled: subheaderStyle.borderEnabled,
    subheaderBorderColor: subheaderStyle.borderColor,
    subheaderBorderThickness: subheaderStyle.borderThickness,
    subheaderBorderRadius: subheaderStyle.borderRadius,
    subheaderGap: subheaderStyle.gap ?? null,
    subheaderWidth: subheaderStyle.width,
    subheaderWidthOffset: subheaderStyle.widthOffset,
    // Per-element extended styling (Body)
    bodyBold: bodyStyle.bold,
    bodyItalic: bodyStyle.italic,
    bodyUnderline: bodyStyle.underline,
    bodyStrikethrough: bodyStyle.strikethrough,
    bodyShadow: bodyStyle.shadow,
    bodyTextCase: bodyStyle.textCase,
    bodyBorderEnabled: bodyStyle.borderEnabled,
    bodyBorderColor: bodyStyle.borderColor,
    bodyBorderThickness: bodyStyle.borderThickness,
    bodyBorderRadius: bodyStyle.borderRadius,
    bodyGap: bodyStyle.gap ?? null,
    bodyWidth: bodyStyle.width,
    bodyWidthOffset: bodyStyle.widthOffset,
    // Font Library — `?? undefined` (never null/'') so JSON.stringify drops
    // unset keys and cascade inheritance survives round-trips.
    titleFontFamily: titleStyle.fontFamily ?? undefined,
    subheaderFontFamily: subheaderStyle.fontFamily ?? undefined,
    bodyFontFamily: bodyStyle.fontFamily ?? undefined,
    titleLetterSpacing: titleStyle.letterSpacing || undefined,
    subheaderLetterSpacing: subheaderStyle.letterSpacing || undefined,
    bodyLetterSpacing: bodyStyle.letterSpacing || undefined,
    titleLineHeight: titleStyle.lineHeight ?? undefined,
    subheaderLineHeight: subheaderStyle.lineHeight ?? undefined,
    bodyLineHeight: bodyStyle.lineHeight ?? undefined,
    categoryFontFamily: categoryFontFamily ?? undefined,
    // Category styling — per-content once edited here (brand default is the
    // fallback layer resolved at render time, never serialized as a config
    // value unless the user touched it on this card)
    categoryBorderRadius,
    // Image fit/fill mode
    imageFitMode,
    imageFitAnchor,
    imageFitWidth,
    imageFillPositionX,
    imageFillPositionY,
    // Logo selection (for curated content - may differ from brand default)
    logoUrl: selectedLogo?.url,
    logoKey: selectedLogo?.logoKey,
    logoDisplayMode,
    // Caption mode (AI-generated vs source text)
    captionModeEnabled,
    // Design elements (canvas-style PNG/SVG placements)
    designElements,
  }), [
    showOverlayLogo, showOverlayTitle, showOverlaySubheader, showOverlayCategory, showOverlayGradient, showOverlayBody,
    logoPosition, titlePosition, subheaderPosition, bodyPosition, categoryPosition,
    titleFontSize, subheaderFontSize, bodyFontSize, logoScale, categoryScale, logoGap, categoryGap, categoryBgColor, categoryBorderRadius,
    gradientDirection, gradientColor, gradientOpacity,
    titleBgEnabled, titleBgColor, titleBgOpacity, titleTextColor,
    subheaderBgEnabled, subheaderBgColor, subheaderBgOpacity, subheaderTextColor,
    bodyBgEnabled, bodyBgColor, bodyBgOpacity, bodyTextColor,
    borderEnabled, borderColor, borderThickness,
    titleStyle, subheaderStyle, bodyStyle, categoryFontFamily,
    imageFitMode, imageFitAnchor, imageFitWidth, imageFillPositionX, imageFillPositionY,
    selectedLogo, logoDisplayMode, captionModeEnabled,
    designElements,
  ]);

  // Effective per-element font stacks: config slug → brand/universal default
  // → Inter fallback. Applied to the editable text layers so the editor
  // matches the Playwright/Remotion renders.
  const effectiveFontFamilies = useMemo(() => ({
    title: resolveElementFontFamily(currentOverlayConfig, 'title', fontManifest),
    subheader: resolveElementFontFamily(currentOverlayConfig, 'subheader', fontManifest),
    body: resolveElementFontFamily(currentOverlayConfig, 'body', fontManifest),
    category: resolveElementFontFamily(currentOverlayConfig, 'category', fontManifest),
  }), [currentOverlayConfig, fontManifest]);

  // Clear cached preview when overlay config changes
  useEffect(() => {
    setPreviewUrl(null);
    setShowPreviewPopover(false);
  }, [
    showOverlayLogo, showOverlayTitle, showOverlaySubheader, showOverlayCategory, showOverlayGradient, showOverlayBody,
    logoPosition, titlePosition, subheaderPosition, bodyPosition, categoryPosition,
    titleFontSize, subheaderFontSize, bodyFontSize, logoScale, categoryScale, logoGap, categoryGap, categoryBgColor, selectedLogo, logoDisplayMode,
    gradientDirection, gradientColor, gradientOpacity,
    titleBgEnabled, titleBgColor, titleBgOpacity, titleTextColor,
    subheaderBgEnabled, subheaderBgColor, subheaderBgOpacity, subheaderTextColor,
    bodyBgEnabled, bodyBgColor, bodyBgOpacity, bodyTextColor,
    borderEnabled, borderColor, borderThickness
  ]);

  // Auto-save all overlay config changes with debounce
  // In carousel mode: saves to current slide's overlay_config
  // In single mode: saves to content's overlay_config
  // When a saved template is active and user makes changes, we clear the template reference
  useEffect(() => {
    // Skip until overlay is initialized (prevents save on first load)
    if (!hasInitializedOverlay) return;

    // Snapshot the canonical config from the shared memo so every save path
    // serializes the exact same fields. Serialized BEFORE any bail so
    // hydration renders can adopt it as the save baseline.
    const config = currentOverlayConfig;
    const serialized = JSON.stringify(config);

    // Hydration renders (template apply, initial slide load, slide sync)
    // ADOPT the current config as baseline instead of bailing before the
    // prime. The old early-returns left lastSavedOverlayConfigRef null
    // across sync completion (the un-flag timeout causes no re-run), so the
    // next async drift — brand logo / settings arriving — primed on drifted
    // state and the drift after THAT read as a user edit, PATCHing defaults
    // over the slide and wiping overlay_template_id. Falling through when
    // the user HAS touched preserves edits made inside the sync window
    // (touched is reset at each hydration start, so the baseline is fresh).
    const hydrating =
      isApplyingTemplateRef.current ||
      (isCarouselMode && isInitialSlideLoadRef.current) ||
      isSyncingSlideRef.current;
    if (hydrating && !overlayUserTouchedRef.current) {
      lastSavedOverlayConfigRef.current = serialized;
      return;
    }

    // Skip if the config matches what we last persisted/loaded — prevents the
    // post-init pass from wiping overlay_template_id when the user hasn't
    // actually edited anything.
    if (lastSavedOverlayConfigRef.current === null) {
      // First post-init run: prime the ref with the current config and skip.
      lastSavedOverlayConfigRef.current = serialized;
      return;
    }
    if (lastSavedOverlayConfigRef.current === serialized) return;

    // Config changed but the user hasn't touched the card: async brand data
    // settled after priming. Re-prime and skip — never persist drift.
    if (!overlayUserTouchedRef.current) {
      lastSavedOverlayConfigRef.current = serialized;
      return;
    }

    // In carousel mode, track pending changes for this specific slide
    // This ensures navigation flushes changes to the correct slide
    if (isCarouselMode && slides[currentSlideIndex]) {
      const slideId = slides[currentSlideIndex].id;

      // Store pending config immediately (so flush can access it)
      pendingOverlayConfigRef.current = { slideId, config, templateId: null };

      // Clear any existing timeout
      if (overlayConfigTimeoutRef.current) {
        clearTimeout(overlayConfigTimeoutRef.current);
      }

      // Set up debounced save
      overlayConfigTimeoutRef.current = setTimeout(() => {
        // Verify we're still on the same slide — if user navigated away,
        // the flush already saved and we should not overwrite the new slide's config
        if (isCarouselMode && slides[currentSlideIndex]?.id !== slideId) return;

        // Save custom config and clear template reference (same as single mode)
        // This ensures when user makes changes after selecting a template, we switch to custom mode
        carouselApi.updateSlide(slideId, {
          overlay_config: config,
          overlay_template_id: null  // Clear template reference - now using custom config
        }).then(() => {
          lastSavedOverlayConfigRef.current = serialized;
          // Clear pending ref since save completed
          if (pendingOverlayConfigRef.current?.slideId === slideId) {
            pendingOverlayConfigRef.current = null;
          }
          // Update local slides array so navigation back shows the saved data
          setSlides(prev => prev.map(s =>
            s.id === slideId ? { ...s, overlay_config: config, overlay_template_id: undefined } : s
          ));
          // Update local state to reflect we're now in custom mode
          if (activeTemplateId !== null) {
            setActiveTemplateId(null);
          }
        }).catch(err => {
          console.error('Failed to save slide overlay config:', err);
        });
        overlayConfigTimeoutRef.current = null;
      }, 500);

      return () => {
        // Don't clear pending ref on cleanup - flushPendingOverlayConfig needs it
        if (overlayConfigTimeoutRef.current) {
          clearTimeout(overlayConfigTimeoutRef.current);
          overlayConfigTimeoutRef.current = null;
        }
      };
    }

    // In single mode, save to content's overlay_config (no pending tracking needed)
    const timeoutId = setTimeout(() => {
      // Save custom config and clear template reference (backend enforces mutual exclusivity)
      // This ensures that when user makes changes after selecting a template, we switch to custom mode
      tendApi.updateTendV2Content(item.id, { overlay_config: config, overlay_template_id: null });
      lastSavedOverlayConfigRef.current = serialized;
      // Mirror the persisted values onto the item reference so a parent
      // holding stale items doesn't reset us on remount.
      (item as any).overlay_config = config;
      (item as any).overlay_template_id = null;
      // Update local state to reflect we're now in custom mode
      if (activeTemplateId !== null) {
        setActiveTemplateId(null);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
    // Note: slides and currentSlideIndex are intentionally NOT in deps.
    // We read them inside the effect to get the current slide ID,
    // but we don't want to re-run when navigating (that's what flush handles).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasInitializedOverlay, activeTemplateId,
    currentOverlayConfig,
    item.id, isCarouselMode
  ]);

  // (loadFromSlot removed with template swipe-to-switch — the template
  //  modal's onSelectTemplate is the only apply path now.)

  const setAsDefaultTemplate = useCallback(async (templateId: number) => {
    if (!brandId) return;
    const result = await tendApi.updateBrandOverlaySettings(brandId, {
      default_overlay_template_id: templateId
    });
    if (result) {
      setDefaultTemplateId(templateId);
    }
  }, [brandId]);

  // Live preview of category border radius while dragging — state only. The
  // PATCH happens in updateCategoryBorderRadius on slider release (a range
  // input fires onChange per tick; ~50 PATCHes per swipe without this split).
  const previewCategoryBorderRadius = useCallback((radius: number) => {
    setCategoryBorderRadius(radius);
  }, []);

  // Commit category border radius (0=square, 100=pill) — PER-CONTENT: state
  // feeds currentOverlayConfig and the overlay auto-save persists it into
  // this content's (or slide's) overlay_config. Brand-wide edits live in
  // the Design dropdown's Brand Colors CATEGORY slot only (the old brand
  // PATCH here made any card edit silently become the brand default).
  const updateCategoryBorderRadius = useCallback(async (radius: number) => {
    setCategoryBorderRadius(radius);
    categoryConfigOverrideRef.current.radius = true;
    overlayUserTouchedRef.current = true;
  }, []);

  // Live preview of category bg color while dragging — state only. The
  // commit happens in updateCategoryBgColor via the picker's
  // onChangeCommitted (one save per interaction instead of per drag tick).
  const previewCategoryBgColor = useCallback((color: string) => {
    setCategoryBgColor(color);
  }, []);

  // Commit category background color — per-content (see radius note above)
  const updateCategoryBgColor = useCallback(async (color: string) => {
    setCategoryBgColor(color);
    categoryConfigOverrideRef.current.color = true;
    overlayUserTouchedRef.current = true;
  }, []);

  // Handle video render request (Remotion)
  // If already rendered, show preview; otherwise render new
  const handleRenderVideo = useCallback(async (forceRerender = false) => {
    const videoUrl = localImages[selectedImageIndex];
    if (!videoUrl || !isVideoUrl(videoUrl)) return;

    const anchorRect = overlayContainerRef.current?.getBoundingClientRect();
    setPreviewAnchor(anchorRect && anchorRect.width > 40
      ? { width: anchorRect.width, top: anchorRect.top, left: anchorRect.left, right: anchorRect.right }
      : null);

    // If we have a rendered video and not forcing re-render, just show preview
    if (renderedVideoUrl && !forceRerender) {
      setShowVideoPreview(true);
      return;
    }

    setIsRenderingVideo(true);
    setVideoRenderError(null);

    try {
      // Check for existing completed render first (unless forcing re-render)
      if (!forceRerender) {
        const existingJobs = await videoRenderApi.listJobs(item.id);
        const completedJob = existingJobs.find(j => j.status === 'completed' && j.output_url);
        if (completedJob && completedJob.output_url) {
          setRenderedVideoUrl(completedJob.output_url);
          setShowVideoPreview(true);
          setIsRenderingVideo(false);
          return;
        }
      }

      const config = currentOverlayConfig;

      // Queue the video render job (duration auto-detected by Remotion)
      const response = await videoRenderApi.queueRender(item.id, {
        video_url: videoUrl,
        platform: item.platform,
        overlay_config: config,
        logo_url: selectedLogo?.url,
        logo_key: selectedLogo?.logoKey,
        logo_display_mode: logoDisplayMode,
        brand_name: brandName || '',
        brand_slug: brandSlug,
        title: editedTitle,
        subheader: editedSubheader,
        body: editedBody,
        category: editedCategory || categoryFallback,
        category_border_radius: categoryBorderRadius,
        category_bg_color: categoryBgColor,
        image_fit_mode: imageFitMode,
        image_fit_anchor: imageFitAnchor,
        image_fit_width: imageFitWidth,
        image_fill_position_x: imageFillPositionX,
        image_fill_position_y: imageFillPositionY,
      });

      if (response.success && response.job_id) {
        // Poll for completion
        const job = await videoRenderApi.pollJobCompletion(response.job_id);

        if (job && job.status === 'completed' && job.output_url) {
          setRenderedVideoUrl(job.output_url);
          setShowVideoPreview(true); // Show preview on completion
        } else if (job && job.status === 'failed') {
          setVideoRenderError(job.error_message || 'Video render failed');
        } else {
          setVideoRenderError('Video render timed out');
        }
      } else {
        setVideoRenderError(response.error || 'Failed to queue video render');
      }
    } catch (error) {
      console.error('Video render error:', error);
      setVideoRenderError(error instanceof Error ? error.message : 'Video render failed');
    } finally {
      setIsRenderingVideo(false);
    }
  }, [
    localImages, selectedImageIndex, isVideoUrl, currentOverlayConfig, item.id, item.platform,
    selectedLogo, brandName, editedTitle, editedSubheader, editedCategory,
    logoDisplayMode, categoryBorderRadius, categoryBgColor, renderedVideoUrl
  ]);

  // Overlay control update handlers — thin wrappers around setX.
  // Persistence is handled exclusively by the debounced auto-save effect so
  // that every overlay edit goes through one queue, eliminating the race
  // between this path and the effect that previously caused intermittent loss.
  const updateGradientDirection = useCallback((dir: 'up' | 'down' | 'full') => {
    setGradientDirection(dir);
  }, []);

  const updateGradientColor = useCallback((color: string) => {
    setGradientColor(color);
  }, []);

  const updateGradientOpacity = useCallback((opacity: number) => {
    setGradientOpacity(opacity);
  }, []);

  const updateShowGradient = useCallback((show: boolean) => {
    setShowOverlayGradient(show);
  }, []);

  const updateBorderEnabled = useCallback((enabled: boolean) => {
    setBorderEnabled(enabled);
  }, []);

  const updateBorderColor = useCallback((color: string) => {
    setBorderColor(color);
  }, []);

  const updateBorderThickness = useCallback((thickness: number) => {
    setBorderThickness(thickness);
  }, []);

  const updateTitleFontSize = useCallback((newSize: number) => {
    setTitleFontSize(Math.max(10, newSize));
  }, []);

  const updateSubheaderFontSize = useCallback((newSize: number) => {
    setSubheaderFontSize(Math.max(8, newSize));
  }, []);

  const updateBodyFontSize = useCallback((newSize: number) => {
    setBodyFontSize(Math.max(8, newSize));
  }, []);

  // Props for the shared ElementToolbar (text kinds): one element's state
  // in, a partial out, fanned to the card's setters.
  const textToolbarProps = (kind: 'title' | 'subheader' | 'body') => {
    const cfg = kind === 'title'
      ? {
          fontSize: titleFontSize, setFontSize: updateTitleFontSize,
          textColor: titleTextColor, setTextColor: setTitleTextColor,
          bgEnabled: titleBgEnabled, setBgEnabled: setTitleBgEnabled,
          bgColor: titleBgColor, setBgColor: setTitleBgColor,
          bgOpacity: titleBgOpacity, setBgOpacity: setTitleBgOpacity,
          position: titlePosition, setPosition: setTitlePosition,
          ref: titleRef, setIsFocused: setIsTitleFocused,
          style: titleStyle, setStyle: setTitleStyle,
        }
      : kind === 'subheader'
        ? {
            fontSize: subheaderFontSize, setFontSize: updateSubheaderFontSize,
            textColor: subheaderTextColor, setTextColor: setSubheaderTextColor,
            bgEnabled: subheaderBgEnabled, setBgEnabled: setSubheaderBgEnabled,
            bgColor: subheaderBgColor, setBgColor: setSubheaderBgColor,
            bgOpacity: subheaderBgOpacity, setBgOpacity: setSubheaderBgOpacity,
            position: subheaderPosition, setPosition: setSubheaderPosition,
            ref: subheaderRef, setIsFocused: setIsSubheaderFocused,
            style: subheaderStyle, setStyle: setSubheaderStyle,
          }
        : {
            fontSize: bodyFontSize, setFontSize: updateBodyFontSize,
            textColor: bodyTextColor, setTextColor: setBodyTextColor,
            bgEnabled: bodyBgEnabled, setBgEnabled: setBodyBgEnabled,
            bgColor: bodyBgColor, setBgColor: setBodyBgColor,
            bgOpacity: bodyBgOpacity, setBgOpacity: setBodyBgOpacity,
            position: bodyPosition, setPosition: setBodyPosition,
            ref: bodyRef, setIsFocused: setIsBodyFocused,
            style: bodyStyle, setStyle: setBodyStyle,
          };
    const value: TextToolbarValue = {
      textColor: cfg.textColor,
      fontFamily: cfg.style.fontFamily,
      fontSize: cfg.fontSize,
      bold: cfg.style.bold,
      italic: cfg.style.italic,
      underline: cfg.style.underline,
      strikethrough: cfg.style.strikethrough,
      shadow: cfg.style.shadow,
      textCase: cfg.style.textCase,
      letterSpacing: cfg.style.letterSpacing,
      lineHeight: cfg.style.lineHeight,
      bgEnabled: cfg.bgEnabled,
      bgColor: cfg.bgColor,
      bgOpacity: cfg.bgOpacity,
      borderEnabled: cfg.style.borderEnabled,
      borderColor: cfg.style.borderColor,
      borderThickness: cfg.style.borderThickness,
      borderRadius: cfg.style.borderRadius,
      gap: effectiveGapFor(kind),
      position: cfg.position,
    };
    const onChange = (patch: Partial<TextToolbarValue>) => {
      const { textColor, fontSize, bgEnabled, bgColor, bgOpacity, position, ...styleKeys } = patch;
      if (textColor !== undefined) cfg.setTextColor(textColor);
      if (fontSize !== undefined) cfg.setFontSize(fontSize);
      if (bgEnabled !== undefined) cfg.setBgEnabled(bgEnabled);
      if (bgColor !== undefined) cfg.setBgColor(bgColor);
      if (bgOpacity !== undefined) cfg.setBgOpacity(bgOpacity);
      if (styleKeys.gap !== undefined) styleKeys.gap = Math.max(0, Math.round(Number(styleKeys.gap) || 0));
      if (Object.keys(styleKeys).length > 0) cfg.setStyle({ ...cfg.style, ...styleKeys });
      if (position !== undefined) {
        // A new anchor remounts the element in another row: drop the caret so
        // the box does not come back half-edited. The selection itself stays.
        cfg.setPosition(position);
        cfg.setIsFocused(false);
        cfg.ref.current?.blur();
      }
    };
    return { value, onChange };
  };

  // ----- Safe zone (this channel + placement) -----
  const { universalSafeZones, setUniversalSafeZones } = useBrandPalette();
  const safeZone = resolveSafeZone(universalSafeZones, item.platform, contentPlacement);
  const safeZoneLabel = `${item.platform}${contentPlacement ? ' · ' + contentPlacement : ''}`;
  const safeBucket = asAspectBucket(item.aspect_ratio) ?? '1:1';
  // The stack: what is visible in which row, and each element's gap below it
  // (utils/overlayStack.ts — one column on the safe rectangle, nothing overlaps).
  const stackInput = {
    positions: { logo: logoPosition, title: titlePosition, subheader: subheaderPosition, body: bodyPosition, category: categoryPosition },
    visible: { logo: showOverlayLogo, title: showOverlayTitle, subheader: showOverlaySubheader, body: showOverlayBody, category: showOverlayCategory },
  };
  const stackGaps = { logo: logoGap, title: titleStyle.gap, subheader: subheaderStyle.gap, body: bodyStyle.gap, category: categoryGap };
  const effectiveGapFor = (kind: SetElementKind) => effectiveGap(kind, stackInput, stackGaps);
  // Per card and not remembered: on for this canvas only, off again once the
  // card loses selection (settings puck closed and no element selected).
  const [showSafeZones, setShowSafeZones] = useState(false);
  const toggleSafeZones = () => setShowSafeZones((v) => !v);
  useEffect(() => {
    if (!showOverlayToggles && !selection.selected) setShowSafeZones(false);
  }, [showOverlayToggles, selection.selected]);

  // ----- Arrow keys: design elements only (1 px, Shift 10 px). Set elements keep
  // the stack + vertical-offset model; their movement is being rethought. -----
  const nudge = useCallback((sel: { kind: string; id?: string }, n: { dx: number; dy: number }) => {
    if (sel.kind !== 'design') return;
    const c = overlayContainerRef.current?.getBoundingClientRect();
    if (!c) return;
    overlayUserTouchedRef.current = true;
    setDesignElements((prev) => prev.map((el) => {
      if (el.instanceId !== sel.id) return el;
      const { centerX, centerY } = placedElementCenterPx(el, c.width, c.height);
      return clampPlacement({ ...el, ...deriveAnchoredOffsets(centerX + n.dx, centerY + n.dy, c.width, c.height) }, c.width, c.height);
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useArrowNudge({ enabled: true, selected: selection.selected, rootRef: overlayContainerRef, onNudge: nudge });

  // Toggle template scope between global and brand-specific (SuperAdmin only)
  // (Template swipe-to-switch removed 2026-08-08 — the gesture zone sat
  //  under the z-30 hover controls so it rarely fired, and its touch
  //  handlers conflicted with content-row panning. Templates switch via
  //  the template modal.)

  // Per-channel published-render layout (replaces the statically-dead
  // isVideoPlatform imageMode branch)
  const layout = PLATFORM_LAYOUT[item.platform.toLowerCase()] ?? DEFAULT_LAYOUT;
  const showImageOverlay = primaryImageUrl && contentTitle && brandId;

  // Check if current media is a video (for video overlay rendering)
  const currentMediaIsVideo = primaryImageUrl && isVideoUrl(primaryImageUrl);
  const showVideoOverlay = currentMediaIsVideo && brandId;

  // ----- Ordered card slots: media / post title / primary text -----
  // The media canvas itself is PORTALED (createPortal in the content area
  // below) — its VISUAL position in the card is wherever this host div
  // sits. Per-channel reordering moves the HOST, never the createPortal
  // expression: moving the portal would remount the recorder/carousel
  // subtree and drop camera state mid-recording.
  const mediaSlot = (
    <React.Fragment key="media">
      {/* Published items show the media that actually went out — static,
          no editor. The QA toggle flips to the live overlay composition
          so published output and current overlay can be compared. */}
      {showPublishedView && (
        <div className="flex justify-center mb-2">
          <div className="inline-flex rounded-lg tend-card-pill-frost p-0.5 gap-0.5">
            {([['published', 'Published'], ['overlay', 'Overlay']] as const).map(([mode, label]) => {
              const active = (mode === 'overlay') === publishedOverlayPeek;
              return (
                <button
                  key={mode}
                  type="button"
                  className="text-xs font-medium px-2.5 py-1 rounded-md transition-colors"
                  style={{
                    background: active ? 'rgba(255,255,255,.85)' : 'transparent',
                    color: active ? '#1f2937' : 'rgba(55,65,81,.65)',
                  }}
                  onClick={() => setPublishedOverlayPeek(mode === 'overlay')}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {publishLegNote && (
        <div
          className="mb-2 px-2.5 py-1.5 rounded-lg text-xs font-medium"
          style={{ background: 'rgba(255,162,62,.16)', color: '#92400e' }}
          data-testid="publish-leg-note"
        >
          {publishLegNote}
        </div>
      )}
      {showPublishedView && !publishedOverlayPeek ? (
        <PublishedMediaViewer
          item={publishedItemFresh || item}
          slides={slides.length > 0 ? slides : undefined}
          aspectCss={platformConfig.aspectRatio}
        />
      ) : pipStageContainer ? (
        /* In-place host for the recording stage. When the PIP teleprompter
           is open, the stage JSX is portaled into the PIP instead of this
           host, and we render a small placeholder here so the layout
           doesn't collapse. The actual React subtree is the same in both
           cases — single source of truth. */
        <div className="mb-3 p-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 text-xs text-gray-500 text-center">
          Recording is open in the teleprompter window. Close it to return the editor here.
        </div>
      ) : (
        <div ref={setInPlaceStageHost} className="contents" />
      )}
    </React.Fragment>
  );
  const titleLimit = PLATFORM_TITLE_LIMITS[item.platform.toLowerCase()];
  const titleCharCount = editedPostTitle.length;
  const isTitleOverLimit = titleLimit != null && titleCharCount > titleLimit;
  const titleSlot = layout.titlePos !== 'none' ? (
    <div key="post-title" className="mb-3">
      {/* Post Title — writes post_title ONLY (never the overlay title).
          Read-only once published: the text that went out is the record. */}
      <div
        ref={attachPostTitleRef}
        contentEditable={!showPublishedView}
        suppressContentEditableWarning
        onInput={handlePostTitleInput}
        onFocus={() => setIsPostTitleFocused(true)}
        onBlur={() => { setIsPostTitleFocused(false); flushPostTitle(); }}
        data-placeholder="Add a post title…"
        className="text-sm font-semibold text-gray-900 leading-snug rounded p-1 -m-1 cursor-text
                   focus:outline-none focus:ring-2 focus:ring-blue-200 focus:bg-blue-50/30
                   transition-colors empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 empty:before:font-normal"
      />
      {/* Title counter — while editing, or persistent when over the limit.
          Over is a warning, not a block: publish truncates server-side. */}
      {titleLimit != null && !showPublishedView && (isPostTitleFocused || isTitleOverLimit) && (
        <div
          className="tend-micro mt-1 text-gray-400"
          style={isTitleOverLimit ? { color: 'var(--signal-rec-soft)' } : undefined}
        >
          TITLE {titleCharCount}/{titleLimit}
          {isTitleOverLimit && ' · TRUNCATED AT PUBLISH'}
        </div>
      )}
    </div>
  ) : null;
  const textSlot = (
    <div key="primary-text" className={layout.flip ? 'mb-3' : ''}>
      {!isEditingText ? (
        /* Read view: clamped to the channel's published line count. Click
           anywhere (or "more") to expand into the editor. */
        <div
          className={`relative rounded p-1 -m-1 ${showPublishedView ? '' : 'cursor-text'}`}
          onClick={() => { if (!showPublishedView) setIsEditingText(true); }}
        >
          <div
            ref={clampRef}
            className={`text-sm text-gray-800 whitespace-pre-wrap leading-relaxed ${textOverflows ? 'tend-clamp-fade' : ''}`}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: layout.clampLines,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              /* FIXED height (not max): every card reserves the channel's
                 full line count so media/rows align across a strip, and
                 "more" always anchors at line N regardless of text length.
                 1.625em = leading-relaxed on text-sm's own font size. */
              height: `${layout.clampLines * 1.625}em`,
            }}
          >
            {editedText || <span className="text-gray-400">Add text…</span>}
          </div>
          {textOverflows && (
            <span className="absolute bottom-0 right-0 text-xs font-bold lowercase text-gray-400 select-none leading-relaxed">
              more
            </span>
          )}
        </div>
      ) : (
        /* Edit view: the original contentEditable, mounted CHILDLESS — the
           entering-edit effect hydrates it from editedText (imperative
           rehydration contract). Explicit "less" (mirror of "more") or blur
           collapses; the debounced PATCH fires regardless of view. */
        <div>
          <div
            ref={textRef}
            contentEditable
            suppressContentEditableWarning
            onInput={handleTextInput}
            onBlur={() => { setIsEditingText(false); flushText(); }}
            className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed
                       focus:outline-none focus:ring-2 focus:ring-blue-200 focus:bg-blue-50/30
                       rounded p-1 -m-1 cursor-text transition-colors"
          />
          <div className="flex justify-end">
            <button
              type="button"
              /* mousedown (not click): fires before the editable's blur so
                 the collapse is a single deliberate transition */
              onMouseDown={(e) => { e.preventDefault(); setIsEditingText(false); }}
              className="text-xs font-bold lowercase text-gray-400 hover:text-gray-600 select-none mt-1.5"
            >
              less
            </button>
          </div>
        </div>
      )}
    </div>
  );
  // Published-render order per channel: reddit = title→media→text,
  // quora = title→text→media, shorts/pinterest = media→title→text,
  // text-first platforms flip text above media.
  const orderedSlots =
    layout.titlePos === 'top'
      ? (layout.flip ? [titleSlot, textSlot, mediaSlot] : [titleSlot, mediaSlot, textSlot])
      : layout.titlePos === 'below_media'
        ? [mediaSlot, titleSlot, textSlot]
        : layout.flip
          ? [textSlot, mediaSlot]
          : [mediaSlot, textSlot];

  return (
    <CornerControlsProvider openId={openCorner} onChange={setOpenCorner}>
    <div
      ref={cardRootRef}
      className="rounded-xl border border-gray-200 overflow-hidden flex flex-col shadow-sm relative group isolate"
      onMouseEnter={() => setIsCardHovered(true)}
      onMouseLeave={() => setIsCardHovered(false)}
      onPointerDownCapture={() => {
        overlayUserTouchedRef.current = true;
      }}
    >
      {/* Hidden file input for media uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,video/mp4,video/quicktime,video/webm"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Hidden source <video> for the in-situ recorder. The camera stream
          feeds this; the RAF draw loop in useVideoRecorder draws its frames
          onto the visible <canvas> mounted inside the active slot. Kept
          1px/transparent (not display:none) because iOS Safari freezes
          frame production on display:none videos. */}
      <video
        ref={recorder.liveVideoRef}
        className="absolute opacity-0 pointer-events-none"
        style={{ width: 1, height: 1, left: 0, top: 0 }}
        autoPlay
        muted
        playsInline
      />

      {/* Drag Handle — ink grip fully covering the platform tile; CSS group-hover for immediate reveal */}
      {dragEnabled && (
        <div
          {...dragHandleProps}
          className="tend-card-grip absolute left-3.5 top-2.5 z-30
            transition-opacity duration-150 opacity-0 group-hover:opacity-100"
        >
          <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="5" cy="4" r="1.5" />
            <circle cx="5" cy="8" r="1.5" />
            <circle cx="5" cy="12" r="1.5" />
            <circle cx="11" cy="4" r="1.5" />
            <circle cx="11" cy="8" r="1.5" />
            <circle cx="11" cy="12" r="1.5" />
          </svg>
        </div>
      )}

      {/* Archived badge */}
      {item.is_archived && (
        <div className="absolute top-[15px] right-12 z-20 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
          Archived
        </div>
      )}

      {/* Header — TEND ink chrome framing the white content (§8 surface
          inversion); scribe tint = the queued indicator */}
      <div className={`tend-card-chrome px-3.5 py-2.5 ${isItemQueued ? 'is-queued' : ''}`}>
        <div className="grid grid-cols-[1fr_auto_1fr] items-center">
          {/* Identity: platform icon only — cards live in channel buckets,
              the tinted tile is enough (name lives in the section header) */}
          <div
            className="flex items-center justify-center rounded-[10px] flex-shrink-0 justify-self-start"
            style={{
              width: 34,
              height: 34,
              background: `${(CHANNEL_INFO[item.platform]?.color || '#6B7280')}4D`,
            }}
            title={CHANNEL_INFO[item.platform]?.name || item.platform}
          >
            <PlatformIcon
              platform={item.platform}
              className="w-[18px] h-[18px] text-white"
            />
          </div>
          {/* Smart Publishing — centered between the tile and share */}
          <div className="justify-self-center">
            {(curatorBrandId || brandId) && (
              <SmartPublishButton
                brandId={curatorBrandId || brandId!}
                contentId={typeof item.id === 'string' ? parseInt(item.id, 10) : item.id}
                targetPlatform={item.platform}
                queued={isItemQueued}
                onQueuedChange={setIsItemQueued}
                takeRequested={!!takeFields.take_requested_at}
                onTakeChange={setTakeFields}
                disabled={isOverLimit}
                disabledReason={isOverLimit ? `Post exceeds ${item.platform} limit (${fullPostCharCount}/${platformLimit} chars)` : undefined}
                onPublishSuccess={() => {
                  // Give the backend a beat (the button refreshes its own
                  // status at ~1s), then flip the card live: published
                  // media swap + analytics band with pending dashes.
                  window.setTimeout(() => {
                    if (!publishBrandId || !itemIdNum) return;
                    refreshPublishStatus(publishBrandId, itemIdNum).then((status) => {
                      if (!status) return;
                      const leg = status.platforms?.[normalizePlatform(item.platform)];
                      setAsyncPublished(!!(status.published && leg?.status === 'success'));
                      if (status.published_at) setPublishedAt(status.published_at);
                    });
                    // Refetch the item so the published face shows what
                    // actually went out (publish-time render, in-session
                    // media swaps) instead of the stale page-load prop.
                    const sourceId = Number((item as any).source_content_id ?? contentId);
                    if (sourceId) {
                      tendApi.getTendV2ContentItems(sourceId, item.platform).then((items) => {
                        const fresh = items.find((it) => String(it.id) === String(item.id));
                        if (fresh) setPublishedItemFresh(fresh);
                      }).catch(() => {});
                    }
                    refreshPostMetrics();
                  }, 1500);
                }}
              />
            )}
          </div>
          {/* Right: the TAKE glyph (when a take is requested — Take phase 1)
              beside the feedback glyph. The glyph is the mark AND the
              one-click launcher (camera preview + teleprompter); recorded
              flips it to the live-green checked clapper. Glyph-only so it
              never fights the Queued pill for width (Shawn, 2026-09-28).
              Gone on the published face — a published item has no take. */}
          <div className="justify-self-end flex items-center gap-0.5">
            {itemTakeState !== 'none' && !showPublishedView && (
              <button
                type="button"
                className={`tend-card-action tend-take-glyph ${itemTakeState === 'recorded' ? 'is-recorded' : ''} ${!recorder.isSupported ? 'is-static' : ''}`}
                disabled={isRecordingActive}
                title={
                  !recorder.isSupported
                    ? (itemTakeState === 'recorded' ? 'Take recorded — queue it when ready' : 'Take requested — record it on a device with a camera')
                    : isRecordingActive
                    ? 'Recording in progress'
                    : itemTakeState === 'recorded'
                    ? 'Take recorded — record another take'
                    : 'Record this take (opens the camera and the teleprompter)'
                }
                aria-label={itemTakeState === 'recorded' ? 'Take recorded' : 'Take requested'}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!recorder.isSupported || isRecordingActive) return;
                  // One click: camera preview on the selected media, teleprompter
                  // open. beginPreview flips the phase synchronously, so the
                  // idle-closes-teleprompter effect never sees this open.
                  recorder.beginPreview();
                  teleprompterDismissedRef.current = false;
                  setTeleprompterOpen(true);
                }}
              >
                {itemTakeState === 'recorded' ? (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16v11a1 1 0 01-1 1H5a1 1 0 01-1-1V8zm0 0l2-4h3l-2 4m4-4h3l-2 4m4-4h3l-2 4" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M9 14l2 2 4-4" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16v11a1 1 0 01-1 1H5a1 1 0 01-1-1V8zm0 0l2-4h3l-2 4m4-4h3l-2 4m4-4h3l-2 4" />
                  </svg>
                )}
              </button>
            )}
            {brandSlug && brandId != null && (
              <button
                onClick={openFeedback}
                className={`tend-card-action relative ${feedbackOpen || feedbackActive ? 'is-ok' : ''}`}
                title="Feedback"
              >
                {(() => {
                  const live = feedbackCounts.counts[feedbackCacheKey(brandId, 'tendv2_content', String(item.id))];
                  const effective = live ?? feedbackEntry ?? { count: 0, resolved: false };
                  // Three states: empty outline / outline + ink count badge
                  // (same as channel tabs) / filled white = resolved.
                  return (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill={effective.resolved ? 'currentColor' : 'none'}
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        style={effective.resolved ? { color: '#fff' } : undefined}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      {!effective.resolved && effective.count > 0 && (
                        <span className="tend-chantab-badge">{effective.count > 99 ? '99+' : effective.count}</span>
                      )}
                    </>
                  );
                })()}
              </button>
            )}
            {feedbackOpen && feedbackAnchor && brandSlug && brandId != null && (
              <FeedbackPanel
                entityType="tendv2_content"
                entityKey={String(item.id)}
                brandId={brandId}
                layout="floating"
                anchorEl={feedbackAnchor}
                onClose={() => setFeedbackOpen(false)}
                shareUrl={`${window.location.origin}/${brandSlug}/tended?item=${item.id}`}
                onCountChange={(d) => {
                  setFeedbackEntry((e) => ({
                    count: Math.max(0, (e?.count ?? 0) + d),
                    resolved: d > 0 ? false : e?.resolved ?? false,
                  }));
                  feedbackCounts.bump(brandId, 'tendv2_content', String(item.id), d);
                }}
                onThreadChange={(resolved) => {
                  setFeedbackEntry((e) => ({ count: e?.count ?? 0, resolved }));
                  feedbackCounts.setResolved(brandId, 'tendv2_content', String(item.id), resolved);
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Secondary analytics band — lifetime metrics for published items.
          In-flow, so it pushes the content area down like a header row. */}
      {showMetricsRow && (
        <div className="tend-card-body-frost">
          <TendCardMetricsRow
            metricsRow={metricsRow}
            publishedAt={publishedAt}
            queued={!!isItemQueued}
          />
        </div>
      )}

      {/* Content Area */}
      <div className="p-4 flex-1 flex flex-col tend-card-body-frost">
        {/* Per-channel ordered slots (media / post title / primary text) */}
        {orderedSlots}
        {/* Recording stage — image carousel + overlay + camera + slide nav +
            controls. Always mounted at this JSX position; portals into
            either the in-place host above (default) or the PIP container
            (when teleprompter is open). Container swap preserves state.
            Gated off entirely for published items — one gate removes the
            picker, media strip, overlay editor and every edit control. */}
        {showEditorStage && stagePortalTarget && createPortal(
          <>
        {/* Empty state - no images on this slide. Lives inside the portal
            so the in-situ recording slot follows the rest of the stage
            into the PIP for older content whose slides have no media. */}
        {localImages.length === 0 && (
          <div className="mb-3 relative">
            {isRecordingActive ? (
              /* Recording in-situ inside the empty slot */
              <div
                className="relative w-full overflow-hidden rounded-lg bg-black"
                style={
                  platformConfig.imageMode === 'fixed' && platformConfig.aspectRatio
                    ? { aspectRatio: platformConfig.aspectRatio }
                    : { height: '12rem' }
                }
              >
                {recorder.permissionError ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-4">
                    <div className="text-base mb-2 font-medium">Camera unavailable</div>
                    <div className="text-white/70 text-sm max-w-xs">{recorder.permissionError}</div>
                  </div>
                ) : recorder.phase === 'review' && recorder.recordedUrl ? (
                  <video
                    src={recorder.recordedUrl}
                    className="w-full h-full object-cover select-none"
                    autoPlay
                    muted
                    loop
                    playsInline
                    draggable={false}
                  />
                ) : (
                  <canvas
                    ref={recorder.canvasRef}
                    className="w-full h-full select-none"
                    style={{ objectFit: 'cover', objectPosition: 'center' }}
                  />
                )}
              </div>
            ) : (
              <button
                onClick={() => setShowPexelsPanel(true)}
                className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg
                  flex flex-col items-center justify-center gap-2 text-gray-400
                  hover:border-gray-400 hover:text-gray-500 hover:bg-gray-50 transition-colors
                  cursor-pointer"
                disabled={isUploading}
              >
                {isUploading ? (
                  <svg className="w-8 h-8 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <>
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="text-sm">Add image or video</span>
                  </>
                )}
              </button>
            )}
            {/* Secondary record entry-point for the empty state — small chip
                in the top-right of the dashed-border area so users can start
                a recording without first opening the picker. */}
            {!isRecordingActive && !isUploading && recorder.isSupported && (
              <button
                onClick={(e) => { e.stopPropagation(); recorder.beginPreview(); }}
                className="absolute top-2 right-2 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 hover:bg-black/80 text-white text-xs font-medium transition-colors"
                title="Record video"
              >
                <span className="w-2 h-2 rounded-full bg-red-500" />
                Record
              </button>
            )}
            {/* Pexels Media Picker Panel - for empty state */}
            {showPexelsPanel && !isRecordingActive && (
              <div className="absolute inset-0 z-50">
                <PexelsMediaPicker
                  onSelectMedia={handlePexelsMediaSelect}
                  onLocalUpload={() => {
                    setShowPexelsPanel(false);
                    fileInputRef.current?.click();
                  }}
                  onClose={() => setShowPexelsPanel(false)}
                  isUploading={isUploading}
                  aspectRatio={platformConfig.aspectRatio}
                  instanceId={`card-${item.id}-empty`}
                />
              </div>
            )}
          </div>
        )}

        {/* Image Carousel with Scroll Snap */}
        {localImages.length > 0 && (
          <div className="mb-3 group relative">
            {/* Wrapper for carousel mode - adds side peek cards */}
            <div className={`relative ${isCarouselMode && slides.length > 1 ? 'px-6' : ''}`}>
              {/* Left peek card - previous slide (carousel mode only).
                  Hidden during recording — the user is locked to the
                  active recording slot. */}
              {!isRecordingActive && isCarouselMode && slides.length > 1 && currentSlideIndex > 0 && (
                <div
                  onClick={() => navigateToSlide(currentSlideIndex - 1)}
                  className="absolute left-0 top-0 bottom-0 w-8 cursor-pointer z-0
                             overflow-hidden rounded-l-lg
                             transition-all duration-300 ease-out hover:w-10"
                >
                  <div className="absolute inset-0 bg-gray-800 rounded-l-lg overflow-hidden">
                    {slides[currentSlideIndex - 1] && (slides[currentSlideIndex - 1].media_url || slides[currentSlideIndex - 1].primary_image_url) && (() => {
                      const peekUrl = slides[currentSlideIndex - 1].media_url || slides[currentSlideIndex - 1].primary_image_url || '';
                      return isVideoUrl(peekUrl) ? (
                        <video src={peekUrl} className="absolute right-0 top-0 h-full w-auto min-w-[200%] object-cover opacity-70" autoPlay muted loop playsInline />
                      ) : (
                        <img src={peekUrl} alt={`Slide ${currentSlideIndex}`} className="absolute right-0 top-0 h-full w-auto min-w-[200%] object-cover opacity-70" />
                      );
                    })()}
                    <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              )}

              {/* Main overlay container - same for both modes */}
              <div ref={setOverlayContainer} className="relative z-10">
                {/* Media corner (upper-right, opens left) — the shared control,
                    on the SLIDE's box (inside the carousel peek padding), so in
                    carousel mode it sits on the current slide it represents. */}
                <TendMediaControl
                  pool={localImages}
                  selectedIndex={selectedImageIndex}
                  previewUrl={currentImageUrl || undefined}
                  previewIsVideo={!!currentImageUrl && isVideoUrl(currentImageUrl)}
                  visible={isCardHovered}
                  hidden={isRecordingActive || showPublishedView || elementMode}
                  busy={isUploading}
                  error={uploadError}
                  onSelect={navigateToImage}
                  onAdd={() => setShowPexelsPanel(true)}
                  onDelete={handleDeleteImage}
                onPreviewClick={() => setGalleryOpen(true)}
                />

              {/* Upload error message */}
              {uploadError && (
                <div className="absolute top-2 left-1/2 -translate-x-1/2 z-50 bg-red-600 text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-lg">
                  {uploadError}
                </div>
              )}

              {/* Media strip — a translated track, a pure projection of
                  selectedImageIndex. Nothing scrolls (swipe/wheel were
                  removed because horizontal pans through the content row
                  hijacked into the strip); the dots drive navigateToImage.
                  `left` rather than `transform` so the track creates no
                  stacking context: the per-slot controls (z-30) must paint
                  above the title/logo overlay (z-10) that follows. */}
              <div
                className="rounded-lg border border-gray-200 transition-[height] duration-300 overflow-hidden"
                data-testid="tend-media-strip"
              >
                <div
                  className="relative flex items-start w-full transition-[left] duration-200 ease-out"
                  style={{ left: `${-selectedImageIndex * 100}%` }}
                  data-testid="tend-media-track"
                >
                {localImages.map((imageUrl, index) => {
                  // Window: only the selected slot and its two neighbours mount their
                  // media. The pool can run to dozens of items (every sibling tend's
                  // additions) and a page mounts ~46 cards — eager slots meant thousands
                  // of <img>/<video> elements fetching at once. Other slots keep their
                  // width so the track still slides; they fill in as they become a
                  // neighbour.
                  const slotIsLive = Math.abs(index - selectedImageIndex) <= 1;
                  return (
                  <div
                    key={`${imageUrl}-${index}`}
                    className="flex-shrink-0 relative overflow-hidden"
                    style={{
                      width: '100%',
                      minWidth: '100%',
                      ...(platformConfig.imageMode === 'fixed' && platformConfig.aspectRatio
                        ? { aspectRatio: platformConfig.aspectRatio }
                        : {})
                    }}
                    onMouseEnter={() => setHoveredImageIndex(index)}
                    onMouseLeave={() => setHoveredImageIndex(null)}
                  >
                    {slotIsLive && (<>
                    {/* In-situ recording surface: takes over the active slot's
                        media area when the recorder is running. Live preview /
                        recording renders to a <canvas>; review uses the actual
                        recorded blob via <video src=...> so the full fit/fill
                        structure (including blurred background) applies. */}
                    {isRecordingActive && index === selectedImageIndex ? (
                      recorder.permissionError ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black text-white text-center px-4 z-[2]">
                          <div className="text-base mb-2 font-medium">Camera unavailable</div>
                          <div className="text-white/70 text-sm max-w-xs">{recorder.permissionError}</div>
                        </div>
                      ) : recorder.phase === 'review' && recorder.recordedUrl ? (
                        imageFitMode === 'fit' && platformConfig.imageMode === 'fixed' ? (
                          <>
                            <video src={recorder.recordedUrl} className="absolute inset-0 w-full h-full object-cover select-none" style={{ filter: backdropBlurCss(overlayScale), transform: 'scale(1.1)' }} autoPlay muted loop playsInline draggable={false} />
                            <video src={recorder.recordedUrl} className="absolute z-[1] select-none" style={{ width: `${imageFitWidth}%`, height: '100%', objectFit: 'contain', objectPosition: `center ${imageFitAnchor}`, left: '50%', transform: 'translateX(-50%)' }} autoPlay muted loop playsInline draggable={false} />
                          </>
                        ) : (
                          <video
                            src={recorder.recordedUrl}
                            className={`select-none ${platformConfig.imageMode === 'fixed' ? 'w-full h-full object-cover' : 'w-full h-auto'}`}
                            style={platformConfig.imageMode === 'fixed' && imageFitMode === 'fill'
                              ? { objectPosition: `${imageFillPositionX}% ${imageFillPositionY}%` }
                              : undefined}
                            autoPlay
                            muted
                            loop
                            playsInline
                            draggable={false}
                          />
                        )
                      ) : (
                        // Preview / recording: live canvas. fit mode letterboxes
                        // on a black background and honors the vertical anchor
                        // (top/center/bottom); fill mode covers + honors the
                        // current objectPosition. The fit-mode width slider is
                        // still skipped here (it only shapes the saved clip).
                        <div className="absolute inset-0 bg-black flex items-center justify-center">
                          <canvas
                            ref={recorder.canvasRef}
                            className="w-full h-full select-none"
                            style={{
                              objectFit: imageFitMode === 'fit' && platformConfig.imageMode === 'fixed' ? 'contain' : 'cover',
                              objectPosition: platformConfig.imageMode === 'fixed' && imageFitMode === 'fill'
                                ? `${imageFillPositionX}% ${imageFillPositionY}%`
                                : platformConfig.imageMode === 'fixed' && imageFitMode === 'fit'
                                  ? `center ${imageFitAnchor || 'center'}`
                                  : 'center',
                            }}
                          />
                        </div>
                      )
                    ) : imageFitMode === 'fit' && platformConfig.imageMode === 'fixed' ? (
                      <>
                        {/* Blurred background — key on imageUrl so React
                            remounts the <video> when the slot's URL changes
                            (e.g. after Save replaces the slide's source
                            with the just-uploaded server URL). Without the
                            key, the same element keeps trying to play the
                            previous source and flashes mid-swap. */}
                        {isVideoUrl(imageUrl) ? (
                          <video key={`bg-${imageUrl}`} ref={registerVideo(index, 'bg')} src={imageUrl} poster={posterUrlFor(imageUrl)} className="absolute inset-0 w-full h-full object-cover select-none" style={{ filter: backdropBlurCss(overlayScale), transform: 'scale(1.1)' }} muted loop playsInline preload="metadata" draggable={false} />
                        ) : (
                          <img src={imageUrl} alt="" className="absolute inset-0 w-full h-full object-cover select-none" style={{ filter: backdropBlurCss(overlayScale), transform: 'scale(1.1)' }} draggable={false} />
                        )}
                        {/* Contained foreground */}
                        {isVideoUrl(imageUrl) ? (
                          <video key={`fg-${imageUrl}`} ref={registerVideo(index, 'fg')} src={imageUrl} poster={posterUrlFor(imageUrl)} className="absolute z-[1] select-none" style={{ width: `${imageFitWidth}%`, height: '100%', objectFit: 'contain', objectPosition: `center ${imageFitAnchor}`, left: '50%', transform: 'translateX(-50%)', ...(imageFitWidth < 100 && imageFitAnchor === 'top' ? { paddingTop: 20 } : imageFitWidth < 100 && imageFitAnchor === 'bottom' ? { paddingBottom: 20 } : {}) }} muted loop playsInline preload="metadata" draggable={false} />
                        ) : (
                          <img src={imageUrl} alt={`${item.platform} preview ${index + 1}`} className="absolute z-[1] select-none" style={{ width: `${imageFitWidth}%`, height: '100%', objectFit: 'contain', objectPosition: `center ${imageFitAnchor}`, left: '50%', transform: 'translateX(-50%)', ...(imageFitWidth < 100 && imageFitAnchor === 'top' ? { paddingTop: 20 } : imageFitWidth < 100 && imageFitAnchor === 'bottom' ? { paddingBottom: 20 } : {}) }} draggable={false} onLoad={(e) => handleImageLoad(index, e)} />
                        )}
                      </>
                    ) : isVideoUrl(imageUrl) ? (
                      <video
                        key={`solo-${imageUrl}`}
                        ref={registerVideo(index, 'solo')}
                        src={imageUrl}
                        poster={posterUrlFor(imageUrl)}
                        className={`select-none ${platformConfig.imageMode === 'fixed'
                          ? 'w-full h-full object-cover'
                          : 'w-full h-auto'
                          }`}
                        style={platformConfig.imageMode === 'fixed' && imageFitMode === 'fill'
                          ? { objectPosition: `${imageFillPositionX}% ${imageFillPositionY}%` }
                          : undefined}
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        draggable={false}
                        onLoadedMetadata={(e) => handleVideoMetadata(index, e)}
                        onError={(e) => {
                          console.error('Video playback error:', imageUrl, e);
                          setUploadError('Video format not supported by browser');
                          setTimeout(() => setUploadError(null), 5000);
                        }}
                      />
                    ) : (
                      <img
                        src={imageUrl}
                        alt={`${item.platform} preview ${index + 1}`}
                        className={`select-none ${platformConfig.imageMode === 'fixed'
                          ? 'w-full h-full object-cover'
                          : 'w-full h-auto'
                          }`}
                        style={platformConfig.imageMode === 'fixed' && imageFitMode === 'fill'
                          ? { objectPosition: `${imageFillPositionX}% ${imageFillPositionY}%` }
                          : undefined}
                        draggable={false}
                        onLoad={(e) => handleImageLoad(index, e)}
                      />
                    )}
                    {/* Play/Pause toggle — visible before hover (a card must read
                        as video at a glance), gone while an element is selected
                        like every other control. Play starts WITH sound for this
                        slide; pause keeps the sound choice for re-play. */}
                    {!isRecordingActive && !elementMode && isVideoUrl(imageUrl) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isPlaying) setUnmutedIndex(index);
                          setIsPlaying(prev => !prev);
                        }}
                        className="absolute top-2 left-2 z-30 p-1.5 rounded-full transition-all duration-200
                          bg-black/60 hover:bg-purple-600 text-white opacity-100 scale-100"
                        title={isPlaying ? "Pause" : "Play"}
                      >
                        {isPlaying ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        )}
                      </button>
                    )}
                    {/* Sound toggle - shows on hover for video items */}
                    {!isRecordingActive && !elementMode && isVideoUrl(imageUrl) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Per-slide: unmute THIS slide only (or re-mute it)
                          setUnmutedIndex(prev => (prev === index ? null : index));
                        }}
                        className={`absolute top-2 left-10 z-30 p-1.5 rounded-full transition-all duration-200
                          bg-black/60 hover:bg-blue-600 text-white
                          ${hoveredImageIndex === index ? 'opacity-100 scale-100' :
                            selectedImageIndex === index ? 'opacity-70 scale-90 md:opacity-0 md:scale-75' : 'opacity-0 scale-75'}
                        `}
                        title={unmutedIndex !== index ? "Unmute" : "Mute"}
                      >
                        {unmutedIndex !== index ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                          </svg>
                        )}
                      </button>
                    )}
                    {/* Record video button — peer to Add Media, kicks off
                        in-situ recording in this slot. Only visible when
                        recording is idle and the platform supports it. */}
                    {!isRecordingActive && !elementMode && recorder.isSupported && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // The canvas only renders inside the selected
                          // slot; make sure the camera lands on the slot
                          // whose Record button was clicked (a UI focus,
                          // not a media choice — no save).
                          selectMedia(index);
                          recorder.beginPreview();
                        }}
                        className={`absolute top-2 ${isVideoUrl(imageUrl) ? 'left-[4.5rem]' : 'left-2'} z-30 p-1.5 rounded-full transition-all duration-200
                          bg-black/60 hover:bg-red-600 text-white
                          ${pipStageContainer ? 'opacity-100 scale-100' :
                            hoveredImageIndex === index ? 'opacity-100 scale-100' :
                            selectedImageIndex === index ? 'opacity-70 scale-90 md:opacity-0 md:scale-75' : 'opacity-0 scale-75'}
                        `}
                        title="Record video"
                        aria-label="Record video"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <circle cx="12" cy="12" r="6" fill="#ef4444" />
                        </svg>
                      </button>
                    )}
                    {/* Fit/Fill toggle - next to add/record buttons on left
                        side. Gone while an element is selected (element mode),
                        except during a recording session, when it stays so the
                        user can adjust framing live — the recorder's rules win. */}
                    {platformConfig.imageMode === 'fixed' && (!elementMode || isRecordingActive) && (
                      <div className={`absolute top-2 z-30 transition-all duration-200
                        ${isRecordingActive && index === selectedImageIndex ? 'left-2' : (isVideoUrl(imageUrl) ? 'left-[6.5rem]' : 'left-10')}
                        ${isRecordingActive && index === selectedImageIndex
                          ? 'opacity-100 scale-100'
                          : hoveredImageIndex === index ? 'opacity-100 scale-100'
                          : selectedImageIndex === index ? 'opacity-70 scale-90 md:opacity-0 md:scale-75'
                          : 'opacity-0 scale-75'}
                      `} onClick={(e) => e.stopPropagation()}
                        onMouseLeave={() => { if (showFitControls) setShowFitControls(false); }}>
                        {showFitControls ? (
                          /* Expanded: panel wraps around the icon so it stays in place */
                          <div className="bg-black/80 backdrop-blur-sm rounded-xl shadow-xl flex flex-col">
                            {(() => {
                              const saveFit = (updates: Record<string, any>) => {
                                if (isCarouselMode && slides[currentSlideIndex]) {
                                  const slideId = slides[currentSlideIndex].id;
                                  // Remembered for the local slide (landed when the
                                  // user leaves the slide — see commitPendingFit);
                                  // never a `slides` write per slider tick.
                                  const pending = pendingFitRef.current;
                                  pendingFitRef.current = pending && pending.slideId === slideId
                                    ? { slideId, patch: { ...pending.patch, ...(updates as Partial<CarouselSlide>) } }
                                    : { slideId, patch: { ...(updates as Partial<CarouselSlide>) } };
                                  carouselApi.updateSlide(slideId, updates);
                                } else {
                                  tendApi.updateTendV2Content(item.id, updates);
                                }
                              };
                              return (imageFitMode === 'fit' ? ['fit', 'fill'] as const : ['fill', 'fit'] as const).map(mode =>
                                mode === 'fill' ? (
                                  <div key="fill"
                                    onClick={() => { if (imageFitMode !== 'fill') { setImageFitMode('fill'); saveFit({ image_fit_mode: 'fill' }); } }}
                                    className={`flex items-center gap-1 px-1.5 first:rounded-t-xl last:rounded-b-xl transition-colors ${imageFitMode === 'fill' ? 'bg-white/10 text-white' : 'text-white/50 hover:bg-white/5 cursor-pointer'}`}
                                    style={{ height: 28 }}>
                                    <ArrowsPointingOutIcon className="w-4 h-4 flex-shrink-0" />
                                    <span className="text-sm font-medium">Fill</span>
                                    {/* Position slider — only meaningful in fill mode when an axis is cropped */}
                                    {imageFitMode === 'fill' && fillCroppedAxis && (
                                      <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={fillCroppedAxis === 'x' ? imageFillPositionX : imageFillPositionY}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                          const val = parseInt(e.target.value);
                                          if (fillCroppedAxis === 'x') {
                                            setImageFillPositionX(val);
                                            saveFit({ image_fill_position_x: val });
                                          } else {
                                            setImageFillPositionY(val);
                                            saveFit({ image_fill_position_y: val });
                                          }
                                        }}
                                        className="w-16 h-1.5 accent-blue-500 cursor-pointer ml-0.5"
                                        title={fillCroppedAxis === 'x' ? 'Horizontal position' : 'Vertical position'}
                                      />
                                    )}
                                  </div>
                                ) : (
                                  <div key="fit"
                                    onClick={() => { if (imageFitMode !== 'fit') { setImageFitMode('fit'); saveFit({ image_fit_mode: 'fit' }); } }}
                                    className={`flex items-center gap-1 px-1.5 first:rounded-t-xl last:rounded-b-xl transition-colors ${imageFitMode === 'fit' ? 'bg-white/10 text-white' : 'text-white/50 hover:bg-white/5 cursor-pointer'}`}
                                    style={{ height: 28 }}>
                                    <ArrowsPointingInIcon className="w-4 h-4 flex-shrink-0" />
                                    <span className="text-sm font-medium">Fit</span>
                                    {/* 3-point vertical anchor */}
                                    <div className="flex flex-col gap-0.5 bg-black/40 rounded p-0.5 ml-0.5">
                                      {(['top', 'center', 'bottom'] as const).map(pos => (
                                        <button key={pos} type="button" onMouseDown={(e) => e.preventDefault()}
                                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setImageFitAnchor(pos); saveFit({ image_fit_anchor: pos }); }}
                                          className={`w-1.5 h-1.5 rounded-[1px] transition-colors ${imageFitAnchor === pos ? 'bg-blue-400 shadow-[0_0_2px_rgba(96,165,250,0.8)]' : 'bg-white/30 hover:bg-white/60'}`}
                                          title={pos} />
                                      ))}
                                    </div>
                                    {/* Width slider */}
                                    <input type="range" min="50" max="100" value={imageFitWidth}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => { const val = parseInt(e.target.value); setImageFitWidth(val); saveFit({ image_fit_width: val }); }}
                                      className="w-16 h-1.5 accent-blue-500 cursor-pointer" />
                                  </div>
                                )
                              );
                            })()}
                          </div>
                        ) : (
                          /* Collapsed: just the icon button */
                          <button
                            onClick={() => setShowFitControls(true)}
                            className={`p-1.5 rounded-full transition-all duration-200 text-white ${imageFitMode === 'fit' ? 'bg-blue-600' : 'bg-black/60 hover:bg-black/80'}`}
                            title={`${imageFitMode === 'fill' ? 'Fill' : 'Fit'} mode`}
                          >
                            {imageFitMode === 'fit' ? <ArrowsPointingInIcon className="w-4 h-4" /> : <ArrowsPointingOutIcon className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                    )}
                    </>)}
                  </div>
                  );
                })}
                </div>
              </div>



              {/* Title and Logo Overlay - fixed position over the media strip */}
              {showImageOverlay && (
                <>
                  <div className="absolute inset-0 rounded-lg pointer-events-none z-10 overflow-hidden">
                    {/* Border layer (above gradient, below content) */}
                    {borderEnabled && (
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          border: `${borderThickness}px solid ${borderColor}`,
                          borderRadius: 'inherit',
                        }}
                      />
                    )}
                    {showOverlayGradient && (
                      <div
                        className="absolute inset-0"
                        style={{
                          background: gradientDirection === 'full'
                            ? `${gradientColor}${Math.round(gradientOpacity * 2.55).toString(16).padStart(2, '0')}`
                            : gradientDirection === 'down'
                            ? `linear-gradient(to bottom, ${gradientColor}${Math.round(gradientOpacity * 2.55).toString(16).padStart(2, '0')} 0%, ${gradientColor}${Math.round(gradientOpacity * 0.5 * 2.55).toString(16).padStart(2, '0')} 50%, transparent 100%)`
                            : `linear-gradient(to top, ${gradientColor}${Math.round(gradientOpacity * 2.55).toString(16).padStart(2, '0')} 0%, ${gradientColor}${Math.round(gradientOpacity * 0.5 * 2.55).toString(16).padStart(2, '0')} 50%, transparent 100%)`,
                        }}
                      />
                    )}
                  </div>

                  {/* Element mode: the bottom-left pair and the aspect menu are
                      gone while an element is selected (useElementMode also
                      closes the menu). */}
                  {!elementMode && (<>
                  {/* Template Management Button - bottom left */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowTemplateModal(true);
                    }}
                    className="absolute bottom-2 left-2 z-30 p-2 bg-black/60 hover:bg-blue-600
                              text-white rounded-full transition-all duration-200
                              opacity-0 group-hover:opacity-100"
                    title="Manage overlay templates"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                    </svg>
                  </button>

                  {/* Aspect ratio — glyph of the current bucket, next to the
                      template button; one content-level pick governs all slides */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setAspectMenuAnchor(
                        aspectMenuAnchor ? null : e.currentTarget.getBoundingClientRect()
                      );
                    }}
                    className="absolute bottom-2 left-11 z-30 p-2 bg-black/60 hover:bg-blue-600
                              text-white rounded-full transition-all duration-200
                              opacity-0 group-hover:opacity-100"
                    title={`Aspect ratio (${effectiveBucket})`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <AspectGlyph bucket={effectiveBucket} />
                    </span>
                  </button>
                  {aspectMenuAnchor && (
                    <AspectRatioMenuPopover
                      anchorRect={aspectMenuAnchor}
                      onClose={() => setAspectMenuAnchor(null)}
                      current={effectiveBucket}
                      recommended={resolveChannelAspect(brandAspectMap, universalAspectMap, item.platform)}
                      onPick={(bucket) => {
                        setAspectState(bucket);
                        setAspectMenuAnchor(null);
                        tendApi.updateTendV2Content(item.id, { aspect_ratio: bucket });
                      }}
                    />
                  )}
                  </>)}

                  {/* Overlay toggles panel - bottom right. Element-visibility
                      toggles stack UPWARD from the settings puck; the
                      panel-bearing controls (design/background/border) open
                      left — the old single row overflowed the card edge.
                      Gone while an element is selected (element mode). */}
                  {!elementMode && (
                  <div className="absolute bottom-2 right-2 z-30 flex flex-col items-end gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none [&_button]:pointer-events-auto [&_input]:pointer-events-auto">
                    {/* Element visibility toggles - vertical, above the puck */}
                    {showOverlayToggles && (
                      <div className="flex flex-col items-center gap-1.5">
                        {/* Category */}
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowOverlayCategory(!showOverlayCategory); }}
                          className={`p-1.5 rounded-full transition-all duration-200 text-white
                            ${showOverlayCategory ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600'}`}
                          title={showOverlayCategory ? "Hide category" : "Show category"}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 7.994V3h2.01" />
                          </svg>
                        </button>
                        {/* Title */}
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowOverlayTitle(!showOverlayTitle); }}
                          className={`p-1.5 rounded-full transition-all duration-200 text-white
                            ${showOverlayTitle ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600'}`}
                          title={showOverlayTitle ? "Hide title" : "Show title"}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M12 6v13" />
                          </svg>
                        </button>
                        {/* Subheader */}
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowOverlaySubheader(!showOverlaySubheader); }}
                          className={`p-1.5 rounded-full transition-all duration-200 text-white
                            ${showOverlaySubheader ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600'}`}
                          title={showOverlaySubheader ? "Hide subheader" : "Show subheader"}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10" />
                          </svg>
                        </button>
                        {/* Body */}
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowOverlayBody(!showOverlayBody); }}
                          className={`p-1.5 rounded-full transition-all duration-200 text-white
                            ${showOverlayBody ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600'}`}
                          title={showOverlayBody ? "Hide body" : "Show body"}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h12" />
                          </svg>
                        </button>
                        {/* Logo - shows the brand's actual mark so it's clear
                            WHICH logo the toggle controls */}
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowOverlayLogo(!showOverlayLogo); }}
                          className={`p-1.5 rounded-full transition-all duration-200 text-white
                            ${showOverlayLogo ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600'}`}
                          title={showOverlayLogo ? "Hide logo" : "Show logo"}
                        >
                          {(selectedLogo?.url || brandDefaultLogo?.url) ? (
                            <img
                              src={selectedLogo?.url || brandDefaultLogo?.url}
                              alt=""
                              draggable={false}
                              className={`w-4 h-4 object-contain ${showOverlayLogo ? '' : 'opacity-50 grayscale'}`}
                            />
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth={2} />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12h9v9h-9z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    )}
                    {/* Toggle buttons row */}
                    <div className="flex items-end gap-1.5">
                      {/* Panel-bearing controls - open left of the puck */}
                      {showOverlayToggles && (
                        <div className="flex items-center gap-1.5">
                          {/* Design elements - opens picker over canvas */}
                          <button
                            onClick={(e) => { e.stopPropagation(); setShowDesignElementsPicker((v) => !v); }}
                            className={`p-1.5 rounded-full transition-all duration-200 text-white
                              ${showDesignElementsPicker ? 'bg-blue-600 ring-2 ring-blue-400' : (designElements.length > 0 ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600')}`}
                            title="Design elements"
                          >
                            {/* Shapes glyph — "elements you place on a canvas";
                                the old arrows-out icon read as expand/fullscreen */}
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <rect x="3" y="3" width="11" height="11" rx="1.5" strokeWidth={2} />
                              <circle cx="16" cy="16" r="5" strokeWidth={2} />
                            </svg>
                          </button>
                          {/* Safe zone guideline (per card, off by default; off again when the card loses selection) */}
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleSafeZones(); }}
                            className={`p-1.5 rounded-full transition-all duration-200 text-white
                              ${showSafeZones ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600'}`}
                            title={showSafeZones ? 'Hide safe zone' : 'Show safe zone'}
                            data-testid="safe-zone-toggle"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} />
                              <rect x="7" y="8" width="10" height="8" rx="1" strokeWidth={2} strokeDasharray="2 2" />
                            </svg>
                          </button>
                          {/* Gradient - expandable controls */}
                          <button
                            ref={gradientBtnRef}
                            onClick={(e) => { e.stopPropagation(); setShowGradientControls(!showGradientControls); }}
                              className={`p-1.5 rounded-full transition-all duration-200 text-white
                                ${showGradientControls ? 'bg-blue-600 ring-2 ring-blue-400' : showOverlayGradient ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600'}`}
                              title="Background settings"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 9h2v2h-2zm-2 2h2v2H9zm4 0h2v2h-2zm2-2h2v2h-2zM7 9h2v2H7zm12-6H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 18H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2zm2-7h-2v2h2v2h-2v-2h-2v2h-2v-2h-2v2H5v-2h2v-2H5V5h14v6z" />
                              </svg>
                            </button>
                          {/* Gradient controls panel - portaled so overflow-hidden ancestors can't clip it */}
                          {showGradientControls && (
                            <AnchoredStripPopover
                              anchorRef={gradientBtnRef}
                              onClose={() => setShowGradientControls(false)}
                              suspendOutsideClose={isColorPickerOpen}
                            >
                                {/* Hide/Show toggle */}
                                <button
                                  type="button"
                                  onClick={() => updateShowGradient(!showOverlayGradient)}
                                  className="p-0.5 text-white/70 hover:text-white transition-colors"
                                  title={showOverlayGradient ? "Hide gradient" : "Show gradient"}
                                >
                                  {showOverlayGradient ? (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                                    </svg>
                                  )}
                                </button>
                                {/* Direction buttons */}
                                {(['up', 'down', 'full'] as const).map((dir) => (
                                  <button
                                    key={dir}
                                    onClick={() => updateGradientDirection(dir)}
                                    className={`p-0.5 text-sm transition-colors ${gradientDirection === dir ? 'text-white' : 'text-white/50 hover:text-white/80'}`}
                                    title={dir === 'up' ? 'Bottom to top' : dir === 'down' ? 'Top to bottom' : 'Full overlay'}
                                  >
                                    {dir === 'up' ? '↑' : dir === 'down' ? '↓' : '▣'}
                                  </button>
                                ))}
                                {/* Color swatch */}
                                <ColorPickerPopover
                                  color={gradientColor}
                                  onChange={updateGradientColor}
                                  triggerSize="md"
                                  position="above"
                                  title="Gradient color"
                                  onOpenChange={setIsColorPickerOpen}
                                />
                                {/* Opacity slider */}
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  value={gradientOpacity}
                                  onChange={(e) => updateGradientOpacity(parseInt(e.target.value))}
                                  className="w-16 h-1.5 accent-blue-500 cursor-pointer"
                                  title="Opacity"
                                />
                            </AnchoredStripPopover>
                          )}
                          {/* Border - expandable controls */}
                          <button
                            ref={borderBtnRef}
                            onClick={(e) => { e.stopPropagation(); setShowBorderControls(!showBorderControls); }}
                              className={`p-1.5 rounded-full transition-all duration-200 text-white
                                ${showBorderControls ? 'bg-blue-600 ring-2 ring-blue-400' : borderEnabled ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600'}`}
                              title="Border settings"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} />
                              </svg>
                            </button>
                          {/* Border controls panel - portaled so overflow-hidden ancestors can't clip it */}
                          {showBorderControls && (
                            <AnchoredStripPopover
                              anchorRef={borderBtnRef}
                              onClose={() => setShowBorderControls(false)}
                              suspendOutsideClose={isColorPickerOpen}
                            >
                                {/* Hide/Show toggle */}
                                <button
                                  type="button"
                                  onClick={() => updateBorderEnabled(!borderEnabled)}
                                  className="p-0.5 text-white/70 hover:text-white transition-colors"
                                  title={borderEnabled ? "Hide border" : "Show border"}
                                >
                                  {borderEnabled ? (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                                    </svg>
                                  )}
                                </button>
                                {/* Color swatch */}
                                <ColorPickerPopover
                                  color={borderColor}
                                  onChange={updateBorderColor}
                                  triggerSize="md"
                                  position="above"
                                  title="Border color"
                                  onOpenChange={setIsColorPickerOpen}
                                />
                                {/* Thickness slider */}
                                <input
                                  type="range"
                                  min="1"
                                  max="12"
                                  value={borderThickness}
                                  onChange={(e) => updateBorderThickness(parseInt(e.target.value))}
                                  className="w-16 h-1.5 accent-blue-500 cursor-pointer"
                                  title={`Thickness: ${borderThickness}px`}
                                />
                            </AnchoredStripPopover>
                          )}
                          {/* Caption Mode toggle - only show if AI captions exist */}
                          {hasAICaptions && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const newMode = !captionModeEnabled;

                                // Use the EDITED values (preserves user edits), not the original item.* values
                                const newTitle = newMode ? editedGeneratedTitle : editedSourceTitle;
                                const newSubheader = newMode ? editedGeneratedSubheader : editedSourceSubheader;

                                // Force DOM update directly
                                if (titleRef.current) {
                                  titleRef.current.innerHTML = newTitle;
                                }
                                if (subheaderRef.current) {
                                  subheaderRef.current.innerHTML = newSubheader;
                                }

                                // Update React state
                                setEditedTitle(newTitle);
                                setEditedSubheader(newSubheader);
                                setCaptionModeEnabled(newMode);

                                // Persist to dedicated DB column (independent of overlay_config)
                                tendApi.updateTendV2Content(item.id, { caption_mode_enabled: newMode });
                              }}
                              className={`p-1.5 rounded-full transition-all duration-200 text-white
                                ${captionModeEnabled ? 'bg-purple-600 hover:bg-purple-700' : 'bg-black/60 hover:bg-purple-600'}`}
                              title={captionModeEnabled ? "Using AI captions (click for source text)" : "Using source text (click for AI captions)"}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      )}
                      {/* Settings button - opens/closes toggle panel */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowOverlayToggles(!showOverlayToggles);
                          if (showOverlayToggles) {
                            setShowGradientControls(false);
                            setShowBorderControls(false);
                          }
                        }}
                        className={`p-1.5 rounded-full transition-all duration-200 text-white
                          ${showOverlayToggles ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-white/30'}
                        `}
                        title={showOverlayToggles ? "Close overlay settings" : "Overlay settings"}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  )}

                  {/* Dynamic Overlay Container with 3 Row Containers */}
                  <div className="absolute inset-0 z-20 pointer-events-none">
                    {/* ONE column on the safe rectangle: top stack, flexible middle, bottom stack.
                        Nothing can overlap (utils/overlayStack.ts; the image render and the video
                        composition lay out the same way). */}
                    <div data-overlay-column="" style={{ ...stackColumnStyle(safeZone), pointerEvents: 'none' }}>
                    {(['top', 'middle', 'bottom'] as const).map((row) => {
                        const rowKinds = stackKindsForRow(row, stackInput);
                        const rowItems = rowKinds.map((type) => ({ type, zone: stackInput.positions[type] }));
                        const rowMargins = stackMargins(rowKinds, stackGaps, overlayScale);

                        // Derive horizontal alignment from item's column suffix
                        const getAlignClass = (zone: string) => {
                          if (zone.endsWith('left')) return 'self-start text-left';
                          if (zone.endsWith('right')) return 'self-end text-right';
                          return 'self-center text-center';
                        };

                        // Derive justify for flex children from column suffix
                        const getJustifyClass = (zone: string) => {
                          if (zone.endsWith('left')) return 'justify-start';
                          if (zone.endsWith('right')) return 'justify-end';
                          return 'justify-center';
                        };

                        // Row position style (scaled)
                        const getRowStyle = (): React.CSSProperties => ({ ...stackBoxStyle(row), pointerEvents: 'none' });

                        return (
                          <div key={row} data-overlay-row={row} style={getRowStyle()}>
                            {/* Render Items in Stack Order. The row takes no pointer events of its
                                own — each ElementFrame does — so blank row space falls through to a
                                design element behind the text instead of being swallowed row-wide. */}
                            <div style={{ pointerEvents: 'none', width: '100%', display: 'flex', flexDirection: 'column' }}>
                              {rowItems.map(({ type: itemType, zone: itemZone }, itemIndex) => {
                                const itemMargin = rowMargins[itemIndex];
                                if (itemType === 'logo') {
                                  if (showOverlayLogo) {
                                    return (
                                        <div
                                          key="logo"
                                          className={`w-full ${getAlignClass(itemZone)} relative flex ${getJustifyClass(itemZone)}`}
                                          style={{ marginBottom: itemMargin }}
                                        >
                                          <ElementFrame
                                            elementKey="logo"
                                            selected={isElementSelected('logo')}
                                            editable
                                            onSelect={() => selectElement({ kind: 'logo' })}
                                            onDismiss={() => { setShowOverlayLogo(false); clearSelection(); }}
                                            dismissTitle="Hide logo"
                                            boundsRef={overlayContainerRef}
                                            handles={(
                                              <CornerScaleHandle
                                                scale={logoScale}
                                                onChange={(s) => { overlayUserTouchedRef.current = true; setLogoScale(s); }}
                                                boundsRef={overlayContainerRef}
                                                insets={safeZone}
                                                title="Drag to resize the logo"
                                              />
                                            )}
                                            style={{ display: 'inline-flex' }}
                                            toolbar={(placement) => (
                                              <ElementToolbar
                                                kind="logo"
                                                placement={placement}
                                                position={logoPosition}
                                                onPositionChange={setLogoPosition}
                                                gap={effectiveGapFor('logo')}
                                                onGapChange={(n) => { overlayUserTouchedRef.current = true; setLogoGap(n); }}
                                                onChangeLogo={() => logoSelectorRef.current?.open()}
                                              />
                                            )}
                                          >
                                        <div style={{ zoom: logoScale * overlayScale }}>
                                          <LogoSelector
                                            ref={logoSelectorRef}
                                            brandId={brandId}
                                            brandName={brandName}
                                            defaultLogo={selectedLogo}
                                            logoDisplayMode={logoDisplayMode}
                                            canEdit={true}
                                            openOnClick={false}
                                            onChange={(logo, displayMode) => {
                                              if (logo) setSelectedLogo(logo);
                                              if (displayMode) setLogoDisplayMode(displayMode);
                                              // Track explicit logo selection for render/publish
                                              effectiveLogoUrlRef.current = logo?.url || null;
                                              // Persist logo selection to dedicated DB columns
                                              tendApi.updateTendV2Content(item.id, {
                                                overlay_logo_url: logo?.url || null,
                                                overlay_logo_key: logo?.logoKey || null,
                                                overlay_logo_display_mode: displayMode || logoDisplayMode,
                                              });
                                            }}
                                            skipBrandUpdate={true}
                                          />
                                        </div>
                                          </ElementFrame>
                                        </div>
                                    );
                                  } else {
                                    return null;
                                  }
                                }
                                if (itemType === 'title') {
                                  const isTitlePlaceholder = !hasVisibleText(editedTitle);
                                  const titleEmphasis = {
                                    ...buildTextStyle({
                                      bold: titleStyle.bold,
                                      italic: titleStyle.italic,
                                      underline: titleStyle.underline,
                                      strikethrough: titleStyle.strikethrough,
                                      shadow: titleStyle.shadow,
                                      shadowVariant: 'title',
                                      textCase: titleStyle.textCase,
                                      letterSpacing: titleStyle.letterSpacing,
                                      lineHeight: titleStyle.lineHeight,
                                    }, overlayScale),
                                    fontFamily: effectiveFontFamilies.title,
                                  };
                                  const titleBox = buildBorderStyle({
                                    borderEnabled: titleStyle.borderEnabled,
                                    borderColor: titleStyle.borderColor,
                                    borderThickness: titleStyle.borderThickness,
                                    borderRadius: titleStyle.borderRadius,
                                  }, overlayScale);
                                  if (showOverlayTitle) {
                                    const titleAnchor: 'left' | 'center' | 'right' = itemZone.endsWith('left') ? 'left' : itemZone.endsWith('right') ? 'right' : 'center';
                                    const titleSelected = isElementSelected('title');
                                    const titleToolbar = textToolbarProps('title');
                                    return (
                                        <ElementFrame
                                          key="title"
                                          elementKey="title"
                                          selected={titleSelected}
                                          editable
                                          onSelect={() => selectElement({ kind: 'title' })}
                                          onDismiss={() => { setShowOverlayTitle(false); setIsTitleFocused(false); titleRef.current?.blur(); clearSelection(); }}
                                          dismissTitle="Hide title"
                                          boundsRef={overlayContainerRef}
                                          className="relative"
                                          style={{
                                            width: `${titleStyle.width}%`,
                                            alignSelf: titleAnchor === 'left' ? 'flex-start' : titleAnchor === 'right' ? 'flex-end' : 'center',
                                            textAlign: titleAnchor,
                                            marginLeft: titleAnchor === 'left' ? `${titleStyle.widthOffset}%` : undefined,
                                            marginRight: titleAnchor === 'right' ? `${titleStyle.widthOffset}%` : undefined,
                                            marginBottom: itemMargin,
                                          }}
                                          handles={(
                                            <TextWidthHandles
                                              widthPct={titleStyle.width}
                                              widthOffsetPct={titleStyle.widthOffset}
                                              anchor={titleAnchor}
                                              onChange={({ width, widthOffset }) => setTitleStyle({ ...titleStyle, width, widthOffset })}
                                              visible
                                            />
                                          )}
                                          toolbar={(placement) => (
                                            <ElementToolbar kind="title" placement={placement} value={titleToolbar.value} onChange={titleToolbar.onChange} />
                                          )}
                                        >
                                        {isTitlePlaceholder && !(titleSelected && isTitleFocused) ? (
                                          <div
                                            onClick={() => {
                                              if (!titleSelected) return; // first click selects the box; the second places the cursor
                                              setIsTitleFocused(true);
                                              setTimeout(() => titleRef.current?.focus(), 0);
                                            }}
                                            className={`leading-tight italic opacity-50 rounded ${titleSelected ? 'cursor-text' : 'cursor-default'}`}
                                            style={{
                                              fontSize: `${scaledOverlayStyles.titleFont}px`,
                                              color: titleTextColor,
                                              backgroundColor: titleBgEnabled ? `${titleBgColor}${Math.round(titleBgOpacity * 2.55).toString(16).padStart(2, '0')}` : 'transparent',
                                              borderRadius: titleBgEnabled ? scaledOverlayStyles.titleBgRadius : undefined,
                                              padding: `${scaledOverlayStyles.textPadding}px`,
                                              ...titleEmphasis,
                                              ...titleBox,
                                            }}
                                          >
                                            Add a title
                                          </div>
                                        ) : (
                                          <div
                                            ref={(el) => {
                                              // Use type assertion for callback ref assignment
                                              (titleRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                                              // Set initial content when ref attaches
                                              if (el && !el.textContent) {
                                                el.innerHTML = editedTitle;
                                              }
                                            }}
                                            contentEditable={titleSelected}
                                            suppressContentEditableWarning
                                            onInput={handleTitleInput}
                                            onKeyDown={handleTextElementKeyDown}
                                            onFocus={() => setIsTitleFocused(true)}
                                            onBlur={handleTitleBlur}
                                            className={`leading-tight focus:outline-none focus:ring-2 focus:ring-white/50 rounded transition-colors ${titleSelected ? 'cursor-text' : 'cursor-default'}`}
                                            style={{
                                              fontSize: `${scaledOverlayStyles.titleFont}px`,
                                              color: titleTextColor,
                                              backgroundColor: titleBgEnabled ? `${titleBgColor}${Math.round(titleBgOpacity * 2.55).toString(16).padStart(2, '0')}` : 'transparent',
                                              borderRadius: titleBgEnabled ? scaledOverlayStyles.titleBgRadius : undefined,
                                              padding: `${scaledOverlayStyles.textPadding}px`,
                                              whiteSpace: 'pre-wrap',
                                              ...titleEmphasis,
                                              ...titleBox,
                                            }}
                                          />
                                        )}
                                        </ElementFrame>
                                    );
                                  } else {
                                    return null;
                                  }
                                }
                                if (itemType === 'subheader') {
                                  const subheaderDisplayText = editedSubheader || 'This is subheader text of this length';
                                  const isSubheaderPlaceholder = !hasVisibleText(editedSubheader);
                                  const subheaderEmphasis = {
                                    ...buildTextStyle({
                                      bold: subheaderStyle.bold,
                                      italic: subheaderStyle.italic,
                                      underline: subheaderStyle.underline,
                                      strikethrough: subheaderStyle.strikethrough,
                                      shadow: subheaderStyle.shadow,
                                      textCase: subheaderStyle.textCase,
                                      letterSpacing: subheaderStyle.letterSpacing,
                                      lineHeight: subheaderStyle.lineHeight,
                                    }, overlayScale),
                                    fontFamily: effectiveFontFamilies.subheader,
                                  };
                                  const subheaderBox = buildBorderStyle({
                                    borderEnabled: subheaderStyle.borderEnabled,
                                    borderColor: subheaderStyle.borderColor,
                                    borderThickness: subheaderStyle.borderThickness,
                                    borderRadius: subheaderStyle.borderRadius,
                                  }, overlayScale);
                                  if (showOverlaySubheader) {
                                    const subheaderAnchor: 'left' | 'center' | 'right' = itemZone.endsWith('left') ? 'left' : itemZone.endsWith('right') ? 'right' : 'center';
                                    const subheaderSelected = isElementSelected('subheader');
                                    const subheaderToolbar = textToolbarProps('subheader');
                                    return (
                                        <ElementFrame
                                          key="subheader"
                                          elementKey="subheader"
                                          selected={subheaderSelected}
                                          editable
                                          onSelect={() => selectElement({ kind: 'subheader' })}
                                          onDismiss={() => { setShowOverlaySubheader(false); setIsSubheaderFocused(false); subheaderRef.current?.blur(); clearSelection(); }}
                                          dismissTitle="Hide subheader"
                                          boundsRef={overlayContainerRef}
                                          className="relative"
                                          style={{
                                            width: `${subheaderStyle.width}%`,
                                            alignSelf: subheaderAnchor === 'left' ? 'flex-start' : subheaderAnchor === 'right' ? 'flex-end' : 'center',
                                            textAlign: subheaderAnchor,
                                            marginLeft: subheaderAnchor === 'left' ? `${subheaderStyle.widthOffset}%` : undefined,
                                            marginRight: subheaderAnchor === 'right' ? `${subheaderStyle.widthOffset}%` : undefined,
                                            marginBottom: itemMargin,
                                          }}
                                          handles={(
                                            <TextWidthHandles
                                              widthPct={subheaderStyle.width}
                                              widthOffsetPct={subheaderStyle.widthOffset}
                                              anchor={subheaderAnchor}
                                              onChange={({ width, widthOffset }) => setSubheaderStyle({ ...subheaderStyle, width, widthOffset })}
                                              visible
                                            />
                                          )}
                                          toolbar={(placement) => (
                                            <ElementToolbar kind="subheader" placement={placement} value={subheaderToolbar.value} onChange={subheaderToolbar.onChange} />
                                          )}
                                        >
                                        {isSubheaderPlaceholder && !(subheaderSelected && isSubheaderFocused) ? (
                                          <div
                                            onClick={() => {
                                              if (!subheaderSelected) return; // first click selects the box; the second places the cursor
                                              setIsSubheaderFocused(true);
                                              setTimeout(() => subheaderRef.current?.focus(), 0);
                                            }}
                                            className={`leading-snug italic opacity-50 rounded ${subheaderSelected ? 'cursor-text' : 'cursor-default'}`}
                                            style={{
                                              fontSize: `${scaledOverlayStyles.subheaderFont}px`,
                                              color: subheaderTextColor,
                                              backgroundColor: subheaderBgEnabled ? `${subheaderBgColor}${Math.round(subheaderBgOpacity * 2.55).toString(16).padStart(2, '0')}` : 'transparent',
                                              borderRadius: subheaderBgEnabled ? scaledOverlayStyles.subheaderBgRadius : undefined,
                                              padding: `${scaledOverlayStyles.textPadding}px`,
                                              ...subheaderEmphasis,
                                              ...subheaderBox,
                                            }}
                                          >
                                            Add a subheader
                                          </div>
                                        ) : (
                                          <div
                                            ref={(el) => {
                                              // Use type assertion for callback ref assignment
                                              (subheaderRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                                              // Set initial content when ref attaches
                                              if (el && !el.textContent) {
                                                el.innerHTML = editedSubheader;
                                              }
                                            }}
                                            contentEditable={subheaderSelected}
                                            suppressContentEditableWarning
                                            onInput={handleSubheaderInput}
                                            onKeyDown={handleTextElementKeyDown}
                                            onFocus={() => setIsSubheaderFocused(true)}
                                            onBlur={handleSubheaderBlur}
                                            className={`leading-snug focus:outline-none focus:ring-2 focus:ring-white/50 rounded transition-colors min-h-[1.25rem] ${subheaderSelected ? 'cursor-text' : 'cursor-default'}`}
                                            style={{
                                              fontSize: `${scaledOverlayStyles.subheaderFont}px`,
                                              color: subheaderTextColor,
                                              backgroundColor: subheaderBgEnabled ? `${subheaderBgColor}${Math.round(subheaderBgOpacity * 2.55).toString(16).padStart(2, '0')}` : 'transparent',
                                              borderRadius: subheaderBgEnabled ? scaledOverlayStyles.subheaderBgRadius : undefined,
                                              padding: `${scaledOverlayStyles.textPadding}px`,
                                              whiteSpace: 'pre-wrap',
                                              ...subheaderEmphasis,
                                              ...subheaderBox,
                                            }}
                                          />
                                        )}
                                        </ElementFrame>
                                    );
                                  } else {
                                    return null;
                                  }
                                }
                                if (itemType === 'body') {
                                  const bodyEmphasis = {
                                    ...buildTextStyle({
                                      bold: bodyStyle.bold,
                                      italic: bodyStyle.italic,
                                      underline: bodyStyle.underline,
                                      strikethrough: bodyStyle.strikethrough,
                                      shadow: bodyStyle.shadow,
                                      textCase: bodyStyle.textCase,
                                      letterSpacing: bodyStyle.letterSpacing,
                                      lineHeight: bodyStyle.lineHeight,
                                    }, overlayScale),
                                    fontFamily: effectiveFontFamilies.body,
                                  };
                                  const bodyBox = buildBorderStyle({
                                    borderEnabled: bodyStyle.borderEnabled,
                                    borderColor: bodyStyle.borderColor,
                                    borderThickness: bodyStyle.borderThickness,
                                    borderRadius: bodyStyle.borderRadius,
                                  }, overlayScale);
                                  if (showOverlayBody) {
                                    const bodyAnchor: 'left' | 'center' | 'right' = itemZone.endsWith('left') ? 'left' : itemZone.endsWith('right') ? 'right' : 'center';
                                    const bodySelected = isElementSelected('body');
                                    // Same italic placeholder the title and subheader show: an element that is
                                    // on but empty is visible (no silent blank box), and the renders drop it.
                                    const isBodyPlaceholder = !hasVisibleText(editedBody);
                                    const bodyToolbar = textToolbarProps('body');
                                    return (
                                        <ElementFrame
                                          key="body"
                                          elementKey="body"
                                          selected={bodySelected}
                                          editable
                                          onSelect={() => selectElement({ kind: 'body' })}
                                          onDismiss={() => { setShowOverlayBody(false); setIsBodyFocused(false); bodyRef.current?.blur(); clearSelection(); }}
                                          dismissTitle="Hide body"
                                          boundsRef={overlayContainerRef}
                                          className="relative block"
                                          style={{
                                            width: `${bodyStyle.width}%`,
                                            alignSelf: bodyAnchor === 'left' ? 'flex-start' : bodyAnchor === 'right' ? 'flex-end' : 'center',
                                            textAlign: bodyAnchor,
                                            marginLeft: bodyAnchor === 'left' ? `${bodyStyle.widthOffset}%` : undefined,
                                            marginRight: bodyAnchor === 'right' ? `${bodyStyle.widthOffset}%` : undefined,
                                            marginBottom: itemMargin,
                                            flexShrink: 0,
                                          }}
                                          handles={(
                                            <TextWidthHandles
                                              widthPct={bodyStyle.width}
                                              widthOffsetPct={bodyStyle.widthOffset}
                                              anchor={bodyAnchor}
                                              onChange={({ width, widthOffset }) => setBodyStyle({ ...bodyStyle, width, widthOffset })}
                                              visible
                                            />
                                          )}
                                          toolbar={(placement) => (
                                            <ElementToolbar kind="body" placement={placement} value={bodyToolbar.value} onChange={bodyToolbar.onChange} />
                                          )}
                                        >
                                        {isBodyPlaceholder && !(bodySelected && isBodyFocused) ? (
                                          <div
                                            onClick={() => {
                                              if (!bodySelected) return; // first click selects the box; the second places the cursor
                                              setIsBodyFocused(true);
                                              setTimeout(() => bodyRef.current?.focus(), 0);
                                            }}
                                            className={`leading-snug italic opacity-50 rounded ${bodySelected ? 'cursor-text' : 'cursor-default'}`}
                                            style={{
                                              fontSize: `${bodyFontSize * overlayScale}px`,
                                              color: bodyTextColor,
                                              backgroundColor: bodyBgEnabled ? `${bodyBgColor}${Math.round(bodyBgOpacity * 2.55).toString(16).padStart(2, '0')}` : 'transparent',
                                              borderRadius: bodyBgEnabled ? scaledOverlayStyles.subheaderBgRadius : undefined,
                                              padding: `${scaledOverlayStyles.textPadding}px`,
                                              ...bodyEmphasis,
                                              ...bodyBox,
                                            }}
                                          >
                                            Add body text
                                          </div>
                                        ) : (
                                        <div
                                          ref={(el) => {
                                            (bodyRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                                            if (el && !el.textContent) {
                                              el.innerHTML = editedBody;
                                            }
                                          }}
                                          contentEditable={bodySelected}
                                          suppressContentEditableWarning
                                          onInput={handleBodyInput}
                                          onKeyDown={handleTextElementKeyDown}
                                          onFocus={() => setIsBodyFocused(true)}
                                          onBlur={handleBodyBlur}
                                          className={`leading-snug focus:outline-none focus:ring-2 focus:ring-white/50 rounded transition-colors min-h-[1.25rem] ${bodySelected ? 'cursor-text' : 'cursor-default'}`}
                                          style={{
                                            fontSize: `${bodyFontSize * overlayScale}px`,
                                            color: bodyTextColor,
                                            backgroundColor: bodyBgEnabled ? `${bodyBgColor}${Math.round(bodyBgOpacity * 2.55).toString(16).padStart(2, '0')}` : 'transparent',
                                            borderRadius: bodyBgEnabled ? scaledOverlayStyles.subheaderBgRadius : undefined,
                                            padding: `${scaledOverlayStyles.textPadding}px`,
                                            whiteSpace: 'pre-wrap',
                                            ...bodyEmphasis,
                                            ...bodyBox,
                                          }}
                                        />
                                        )}
                                        </ElementFrame>
                                    );
                                  } else {
                                    return null;
                                  }
                                }
                                if (itemType === 'category') {
                                  const isCategoryPlaceholder = !hasVisibleText(editedCategory) && !categoryFallback;
                                  // category_border_radius: 0=square, 100=pill. Scale by 0.1 for px, then by overlayScale
                                  const dynamicCategoryRadius = Math.round((categoryBorderRadius / 10) * overlayScale);
                                  if (showOverlayCategory) {
                                    const categorySelected = isElementSelected('category');
                                    return (
                                        <div
                                          key="category"
                                          className={`w-full ${getAlignClass(itemZone)} relative flex ${getJustifyClass(itemZone)}`}
                                          style={{ marginBottom: itemMargin }}
                                        >
                                          <ElementFrame
                                            elementKey="category"
                                            selected={categorySelected}
                                            editable
                                            onSelect={() => selectElement({ kind: 'category' })}
                                            onDismiss={() => { setShowOverlayCategory(false); setIsCategoryFocused(false); categoryRef.current?.blur(); clearSelection(); }}
                                            dismissTitle="Hide category"
                                            outlineRadius={dynamicCategoryRadius}
                                            outlineOffset={1}
                                            boundsRef={overlayContainerRef}
                                            handles={(
                                              <CornerScaleHandle
                                                scale={categoryScale}
                                                onChange={(s) => { overlayUserTouchedRef.current = true; setCategoryScale(s); }}
                                                boundsRef={overlayContainerRef}
                                                insets={safeZone}
                                                title="Drag to resize the category"
                                              />
                                            )}
                                            style={{ display: 'inline-flex' }}
                                            toolbar={(placement) => (
                                              <ElementToolbar
                                                kind="category"
                                                placement={placement}
                                                borderRadius={categoryBorderRadius}
                                                bgColor={categoryBgColor}
                                                fontFamily={categoryFontFamily}
                                                position={categoryPosition}
                                                onBorderRadiusPreview={previewCategoryBorderRadius}
                                                onBorderRadiusCommit={updateCategoryBorderRadius}
                                                onBgColorPreview={previewCategoryBgColor}
                                                onBgColorCommit={updateCategoryBgColor}
                                                onFontFamilyChange={setCategoryFontFamily}
                                                onPositionChange={setCategoryPosition}
                                                gap={effectiveGapFor('category')}
                                                onGapChange={(n) => { overlayUserTouchedRef.current = true; setCategoryGap(n); }}
                                              />
                                            )}
                                          >
                                        <div style={{ zoom: categoryScale }}>
                                        {isCategoryPlaceholder && !(categorySelected && isCategoryFocused) ? (
                                          <span
                                            onClick={() => {
                                              if (!categorySelected) return; // first click selects the pill; the second places the cursor
                                              setIsCategoryFocused(true);
                                              setTimeout(() => categoryRef.current?.focus(), 0);
                                            }}
                                            className={`backdrop-blur-sm text-white font-bold uppercase tracking-wider shadow-sm italic opacity-50 inline-block ${categorySelected ? 'cursor-text' : 'cursor-default'}`}
                                            style={{
                                              backgroundColor: `${categoryBgColor}e6`,
                                              borderRadius: `${dynamicCategoryRadius}px`,
                                              fontSize: `${scaledOverlayStyles.categoryFont}px`,
                                              padding: `${scaledOverlayStyles.categoryPadV}px ${scaledOverlayStyles.categoryPadH}px`,
                                              fontFamily: effectiveFontFamilies.category,
                                            }}
                                          >
                                            category
                                          </span>
                                        ) : (
                                          <div
                                            ref={attachCategoryRef}
                                            contentEditable={categorySelected}
                                            suppressContentEditableWarning
                                            onInput={handleCategoryInput}
                                            onFocus={() => setIsCategoryFocused(true)}
                                            onBlur={handleCategoryBlur}
                                            className={`backdrop-blur-sm text-white font-bold uppercase tracking-wider shadow-sm focus:outline-none focus:ring-2 focus:ring-white/50 transition-colors inline-block min-w-[40px] ${categorySelected ? 'cursor-text' : 'cursor-default'}`}
                                            style={{
                                              backgroundColor: `${categoryBgColor}e6`,
                                              borderRadius: `${dynamicCategoryRadius}px`,
                                              fontSize: `${scaledOverlayStyles.categoryFont}px`,
                                              padding: `${scaledOverlayStyles.categoryPadV}px ${scaledOverlayStyles.categoryPadH}px`,
                                              fontFamily: effectiveFontFamilies.category,
                                            }}
                                          />
                                        )}
                                        </div>
                                          </ElementFrame>
                                        </div>
                                    );
                                  } else {
                                    return null;
                                  }
                                }
                                return null;
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {/* Safe zone: the keep-out bands of this channel + placement (view for everyone; SA / CS drag the edges). */}
              <SafeZoneLayer
                insets={safeZone}
                visible={showSafeZones}
                editable={isSuperAdmin}
                label={safeZoneLabel}
                publishSize={BUCKET_DIMENSIONS[safeBucket]}
                onSave={(insets) => setUniversalSafeZones({ [item.platform]: { [contentPlacement ?? 'default']: { ...insets, bucket: safeBucket } } })}
                onEditingChange={(editing) => { if (editing) clearSelection(); }}
              />
              {/* Design Elements — two layers by depth (utils/designElementLayers.ts), both
                  siblings of the gradient layer (z-10) and the set elements (z-20) so z-index
                  alone orders them: BEHIND is z-[15], above gradient + border and under the
                  text; IN FRONT is z-20 after the text — today's look for every placement made
                  before `depth` existed. Wrappers are pointer-events:none so clicks on
                  transparent areas pass through; each PlaceableDesignElement re-enables
                  pointer events on itself. inset-[1px] aligns the clip with the inside of the
                  scroll container's 1px gray border. */}
              {designLayers.back.length > 0 && (
                <div data-design-layer="back" className="absolute inset-[1px] pointer-events-none z-[15] overflow-hidden rounded-lg">
                  {designLayers.back.map(renderDesignElement)}
                </div>
              )}
              {designLayers.front.length > 0 && (
                <div data-design-layer="front" className="absolute inset-[1px] pointer-events-none z-20 overflow-hidden rounded-lg">
                  {designLayers.front.map(renderDesignElement)}
                </div>
              )}
              {/* Chrome host: the selection chrome of a BEHIND design element (same box as the layers). */}
              <div ref={setDesignChromeHost} data-design-chrome-host="" className="absolute inset-[1px] pointer-events-none z-30 overflow-hidden rounded-lg" />

              {/* Design Elements picker — full-overlay popup, similar to PexelsMediaPicker */}
              {showDesignElementsPicker && (
                <DesignElementsPicker
                  brandId={brandId ?? null}
                  isSuperAdmin={isSuperAdmin}
                  canEditBrand={canEditCurrentBrand}
                  currentPlacementCount={designElements.length}
                  onSelect={(item) => {
                    if (designElements.length >= MAX_DESIGN_ELEMENTS_PER_OVERLAY) return;
                    const instanceId =
                      typeof crypto !== 'undefined' && (crypto as any).randomUUID
                        ? (crypto as any).randomUUID()
                        : `de-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
                    // New placement lands at canvas center → anchor 'center' / 'middle'
                    // with 0/0 offsets, which holds the visual position across aspect changes.
                    const placement: PlacedDesignElement = {
                      instanceId,
                      elementId: item.id,
                      url: item.s3_url,
                      xAnchor: 'center',
                      yAnchor: 'middle',
                      xPct: 0,
                      yPct: 0,
                      widthPct: 30,
                      rotation: 0,
                      aspectRatio: item.aspect_ratio || 1,
                      depth: 'back', // new placements start behind the text; the pill brings them in front
                    };
                    setDesignElements((prev) => [...prev, placement]);
                    selectElement({ kind: 'design', id: instanceId });
                    setShowDesignElementsPicker(false);
                  }}
                  onClose={() => setShowDesignElementsPicker(false)}
                />
              )}
            </div>
            {/* End of main overlay container */}

              {/* Right peek card - next slide (carousel mode only).
                  Hidden during recording. */}
              {!isRecordingActive && isCarouselMode && slides.length > 1 && currentSlideIndex < slides.length - 1 && (
                <div
                  onClick={() => navigateToSlide(currentSlideIndex + 1)}
                  className="absolute right-0 top-0 bottom-0 w-8 cursor-pointer z-0
                             overflow-hidden rounded-r-lg
                             transition-all duration-300 ease-out hover:w-10"
                >
                  <div className="absolute inset-0 bg-gray-800 rounded-r-lg overflow-hidden">
                    {slides[currentSlideIndex + 1] && (slides[currentSlideIndex + 1].media_url || slides[currentSlideIndex + 1].primary_image_url) && (() => {
                      const peekUrl = slides[currentSlideIndex + 1].media_url || slides[currentSlideIndex + 1].primary_image_url || '';
                      return isVideoUrl(peekUrl) ? (
                        <video src={peekUrl} className="absolute left-0 top-0 h-full w-auto min-w-[200%] object-cover opacity-70" autoPlay muted loop playsInline />
                      ) : (
                        <img src={peekUrl} alt={`Slide ${currentSlideIndex + 2}`} className="absolute left-0 top-0 h-full w-auto min-w-[200%] object-cover opacity-70" />
                      );
                    })()}
                    <div className="absolute inset-0 bg-gradient-to-l from-black/60 to-black/30" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* End of wrapper div for carousel mode padding */}


            {/* TikTok mixed-media warning — fires the moment the carousel
                holds both photos and video (and again at publish via the
                validation endpoint). Platform truth per Ayrshare: "TikTok
                does not support a combination of video and images" — one
                video OR up to 35 images. Warn, never block (IG/Threads
                hybrids are legitimate and publish fine). */}
            {isCarouselMode && item.platform.toLowerCase() === 'tiktok' && (() => {
              const isVideoSlide = (s: CarouselSlide) =>
                (s.media_type || '').toLowerCase() === 'video' || isVideoUrl(s.media_url || '');
              return slides.some(isVideoSlide) && slides.some((s) => !isVideoSlide(s));
            })() && (
              <div
                className="mt-2 px-2.5 py-1.5 rounded-lg text-xs font-medium"
                style={{ background: 'rgba(255,162,62,.16)', color: '#92400e' }}
              >
                TikTok can’t mix photos and videos in one post — this carousel will be
                rejected at publish. Keep it all images, or a single video.
              </div>
            )}

            {/* Carousel Control Bar - shows for carousel mode.
                Hidden by default, fades in on card hover to match the rest
                of the overlay controls. */}
            {isCarouselMode && slides.length > 0 && (
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <CarouselControlBar
                  slides={slides}
                  currentIndex={currentSlideIndex}
                  onNavigate={navigateToSlide}
                  onDelete={handleDeleteSlide}
                  onAdd={handleAddSlide}
                  onReorder={handleReorderSlides}
                />
              </div>
            )}

            {/* Pexels Media Picker Panel */}
            {showPexelsPanel && (
              <PexelsMediaPicker
                onSelectMedia={handlePexelsMediaSelect}
                onLocalUpload={() => {
                  setShowPexelsPanel(false);
                  fileInputRef.current?.click();
                }}
                onClose={() => setShowPexelsPanel(false)}
                isUploading={isUploading}
                aspectRatio={platformConfig.aspectRatio}
                instanceId={`card-${item.id}-carousel`}
              />
            )}
          </div>
        )}
        {/* End of Image Carousel section */}

        {/* In-situ recording controls — appear directly under the slot when
            a recording session is active (preview / recording / review).
            Phase determines which controls show; Save reuses the same
            handleMediaUpload path as desktop upload + Pexels selection. */}
        {isRecordingActive && (
          <div className="mb-3 flex items-center justify-center gap-3 px-2">
            {recorder.permissionError ? (
              <button
                onClick={() => recorder.cancel()}
                className="px-4 py-2 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-medium transition-colors"
              >
                Cancel
              </button>
            ) : recorder.phase === 'preview' ? (
              <>
                <button
                  onClick={() => recorder.cancel()}
                  className="px-4 py-2 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => recorder.startRecording()}
                  className="w-14 h-14 rounded-full bg-white border-4 border-red-500 flex items-center justify-center hover:border-red-600 transition-colors"
                  title="Start recording"
                  aria-label="Start recording"
                >
                  <span className="w-8 h-8 rounded-full bg-red-500" />
                </button>
                <span className="text-xs text-gray-500 select-none">Max 60s</span>
              </>
            ) : recorder.phase === 'recording' ? (
              <>
                <button
                  onClick={() => recorder.cancel()}
                  className="px-4 py-2 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => recorder.stopRecording()}
                  className="w-14 h-14 rounded-full bg-white border-4 border-red-500 flex items-center justify-center hover:border-red-600 transition-colors"
                  title="Stop recording"
                  aria-label="Stop recording"
                >
                  <span className="w-6 h-6 rounded-sm bg-red-500" />
                </button>
                <span className="flex items-center gap-2 text-sm text-gray-700 tabular-nums">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  {String(Math.floor(recorder.elapsedSeconds / 60)).padStart(2, '0')}:
                  {String(Math.floor(recorder.elapsedSeconds % 60)).padStart(2, '0')}
                  <span className="text-gray-400">/ 01:00</span>
                </span>
              </>
            ) : recorder.phase === 'review' ? (
              <>
                <button
                  onClick={() => { recorder.retake(); }}
                  disabled={isUploading}
                  className="px-4 py-2 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Retake
                </button>
                <button
                  onClick={() => recorder.cancel()}
                  disabled={isUploading}
                  className="px-4 py-2 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    const file = recorder.recordedFile;
                    if (!file) return;
                    try {
                      await handleMediaUpload(file, 'recording');
                      recorder.cancel();
                      // Intentionally NOT closing the teleprompter here:
                      // doing so triggered a portal swap (PIP → in-place
                      // remount) which flashed between slides during the
                      // remount. Saving in-place never had this because
                      // no portal swap happens. Let the user close the
                      // PIP manually when they're done reviewing.
                    } catch {
                      // handleMediaUpload already surfaces uploadError
                    }
                  }}
                  disabled={isUploading || !recorder.recordedFile}
                  className="px-5 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isUploading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Saving…
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
              </>
            ) : null}
            {/* Teleprompter toggle — visible during preview/recording/review
                regardless of whether a script exists, so users with older
                content (no LLM-generated script) can still open the
                teleprompter and paste their own. Hidden once the PIP is
                already open. Auto-open on tab switch is wired separately
                via the visibilitychange effect above. */}
            {!teleprompterOpen && (recorder.phase === 'preview' || recorder.phase === 'recording' || recorder.phase === 'review') && (
              <button
                type="button"
                onClick={() => {
                  teleprompterDismissedRef.current = false;
                  setTeleprompterOpen(true);
                }}
                className="px-3 py-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-medium transition-colors flex items-center gap-1.5"
                title="Open teleprompter"
                aria-label="Open teleprompter"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h10" />
                </svg>
                Teleprompter
              </button>
            )}
          </div>
        )}
          </>,
          stagePortalTarget
        )}

        {/* (Primary text renders via orderedSlots above — position is
            per-channel; see PLATFORM_LAYOUT) */}

        {/* Secondary Text */}
        {item.secondary_text && (
          <p className="text-xs text-gray-600 italic mt-3">
            {item.secondary_text}
          </p>
        )}

        {/* Link + Tags control row — always rendered (constant tail height).
            Toggles act in place (nothing relocates to the details strip);
            the pencil opens the matching editor in the white space below. */}
        <div className="mt-3 flex items-stretch gap-2">
          {/* Link half */}
          <div className="flex-1 min-w-0 flex items-center gap-1 tend-card-pill-frost rounded-lg px-2 py-1.5">
            <span className={`flex items-center gap-1.5 min-w-0 ${linkEnabled ? '' : 'opacity-40'}`}>
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <span className="text-xs font-medium text-gray-600">Link</span>
            </span>
            {!showPublishedView && <button
              onClick={() => setOpenTailEditor(openTailEditor === 'link' ? null : 'link')}
              className={`p-1 rounded transition-colors ${openTailEditor === 'link' ? 'text-blue-500 bg-blue-100' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'}`}
              title="Edit link"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>}
            <span className="flex-1" />
            <Toggle checked={linkEnabled} onChange={setLinkEnabled} size="sm" disabled={showPublishedView} />
          </div>
          {/* Tags half */}
          <div className="flex-1 min-w-0 flex items-center gap-1 tend-card-pill-frost rounded-lg px-2 py-1.5">
            <span className={`flex items-center gap-1.5 min-w-0 ${hashtagsEnabled ? '' : 'opacity-40'}`}>
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
              </svg>
              <span className="text-xs font-medium text-gray-600">Tags</span>
            </span>
            {!showPublishedView && <button
              onClick={() => setOpenTailEditor(openTailEditor === 'tags' ? null : 'tags')}
              className={`p-1 rounded transition-colors ${openTailEditor === 'tags' ? 'text-blue-500 bg-blue-100' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'}`}
              title="Edit hashtags"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>}
            <span className="flex-1" />
            <Toggle checked={hashtagsEnabled} onChange={setHashtagsEnabled} size="sm" disabled={showPublishedView} />
          </div>
        </div>

        {/* Placement + 1st-comment row — one line, shared. Placement shows
            for channels with more than one surface; 1st Comment for
            channels verified to honor it; reddit adds its destination (+
            flair) pickers. The 'Placement' word yields whenever another
            control shares the line (the values speak for themselves).
            Once published the row stays, locked: what went out is the
            record, and it reads the same as it did in the queue. */}
        {(placementsFor(item.platform) || FIRST_COMMENT_CHANNELS.has(item.platform)) && (
          <div className="mt-2 flex items-stretch gap-2">
            {placementsFor(item.platform) && (
              <div className="flex-1 min-w-0 flex items-center gap-1.5 tend-card-pill-frost rounded-lg px-2 py-1.5">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {!FIRST_COMMENT_CHANNELS.has(item.platform) && item.platform !== 'reddit' && (
                  <span className="text-xs font-medium text-gray-600">Placement</span>
                )}
                <span className="flex-1" />
                <PlacementControl
                  channel={item.platform}
                  value={contentPlacement}
                  onChange={handlePlacementChange}
                  disabled={showPublishedView}
                />
                {item.platform === 'reddit' && brandId != null && (
                  <RedditDestinationControl
                    brandId={brandId}
                    value={redditDestination}
                    flairId={redditFlairId}
                    onChange={handleRedditDestinationChange}
                    onFlairChange={handleRedditFlairChange}
                    disabled={showPublishedView}
                  />
                )}
              </div>
            )}
            {FIRST_COMMENT_CHANNELS.has(item.platform) && (
              <div
                className="flex-1 min-w-0 flex items-center gap-1.5 tend-card-pill-frost rounded-lg px-2 py-1.5"
                title={linkEnabled
                  ? 'The link publishes as the post’s first comment'
                  : 'Needs Link on — the first comment carries the link'}
              >
                <span className={`flex items-center gap-1.5 min-w-0 ${firstCommentEnabled && linkEnabled ? '' : 'opacity-40'}`}>
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span className="text-xs font-medium text-gray-600 whitespace-nowrap">1st Comment</span>
                </span>
                <span className="flex-1" />
                <Toggle checked={firstCommentEnabled} onChange={setFirstCommentEnabled} size="sm" disabled={showPublishedView} />
              </div>
            )}
          </div>
        )}

        {/* Placement/media hints. Amber = the adapter will degrade or the
            platform will reject (warn, never block); neutral = what the
            chosen reddit format actually posts (text+image can't share a
            Reddit post, so the format decides what goes). */}
        {!showPublishedView && (() => {
          const hint = (() => {
            if (item.platform === 'reddit') {
              if (currentMediaIsVideo) {
                return { tone: 'warn' as const, text: 'Reddit does not accept video through its API — swap the media or drop the Reddit channel.' };
              }
              if (contentPlacement === 'link_post') {
                return { tone: 'info' as const, text: 'Posts as a link post — title + link; the image is not included.' };
              }
              return { tone: 'info' as const, text: 'Posts as an image post — the design is the post; the link stays out of the body.' };
            }
            if (!contentPlacement) return null;
            const surface = item.platform === 'instagram' ? 'grid' : 'feed';
            if ((contentPlacement === 'reel' || contentPlacement === 'reel_off_grid') && !currentMediaIsVideo) {
              return { tone: 'warn' as const, text: `Reels are video — this will publish to the ${surface} instead.` };
            }
            if (contentPlacement === 'story' && isCarouselMode && (item.platform === 'instagram' || item.platform === 'facebook')) {
              return { tone: 'warn' as const, text: `Stories take a single photo or video — this will publish as a carousel to the ${surface} instead.` };
            }
            if (contentPlacement === 'spotlight' && !currentMediaIsVideo) {
              return { tone: 'warn' as const, text: 'Spotlight is video — this will publish as a Story instead.' };
            }
            return null;
          })();
          if (!hint) return null;
          return hint.tone === 'warn' ? (
            <div
              className="mt-2 px-2.5 py-1.5 rounded-lg text-xs font-medium"
              style={{ background: 'rgba(255,162,62,.16)', color: '#92400e' }}
            >
              {hint.text}
            </div>
          ) : (
            <div className="mt-1.5 px-1 text-xs text-gray-500">
              {hint.text}
            </div>
          );
        })()}

        {/* Link editor — opens under the control row */}
        {openTailEditor === 'link' && (
          <div className="mt-2 tend-card-pill-frost rounded-lg p-2 flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <input
              type="text"
              value={editedUrl || ''}
              onChange={(e) => setEditedUrl(e.target.value)}
              onBlur={flushUrl}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setOpenTailEditor(null); }}
              placeholder="https://…"
              autoFocus
              className="flex-1 min-w-0 text-sm text-gray-800 bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <span className="text-[10px] text-gray-400 flex-shrink-0">{(editedUrl || '').length}</span>
          </div>
        )}

        {/* Tags editor — the familiar pills box; the toggle stays in the
            control row above */}
        {openTailEditor === 'tags' && (
          <div className="mt-2">
            <HashtagsList
              hashtags={editedHashtags || []}
              enabled={hashtagsEnabled}
              onToggle={setHashtagsEnabled}
              onHashtagsChange={setEditedHashtags}
              hideToggle
            />
          </div>
        )}

        {/* Photo Attribution - in post area when enabled */}
        {photoAttributionEnabled && editedPhotoAttribution && (
          <div className="mt-3">
            <PhotoAttributionCallout
              attribution={editedPhotoAttribution}
              enabled={photoAttributionEnabled}
              onToggle={setPhotoAttributionEnabled}
              onAttributionChange={handleAttributionChange}
            />
          </div>
        )}
      </div>

      {/* Curator Attribution (for curated content) */}
      {is_curated && curatorBrandName && (
        <div className="tend-card-chrome px-4 py-2.5 flex items-center gap-2" style={{ borderTop: '1px solid rgba(255,255,255,.10)' }}>
          <svg className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--signal-scribe)' }} fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          <span className="tend-micro" style={{ textTransform: 'none', letterSpacing: 0.4 }}>
            Curated by
          </span>
          {curatorBrandLogo?.url ? (
            <img
              src={curatorBrandLogo.url}
              alt={curatorBrandName}
              className="w-5 h-5 rounded object-contain"
            />
          ) : null}
          <span className="text-xs font-medium" style={{ color: 'var(--text-2)' }}>
            {curatorBrandName}
          </span>
        </div>
      )}

      {/* Details Footer - Expandable (TEND ink chrome) */}
      <div className="tend-card-chrome" style={{ borderTop: '1px solid rgba(255,255,255,.10)', boxShadow: 'none' }}>
        {/* Toggle Bar — utility actions at left; the rest toggles details */}
        <div className="flex items-center" style={isOverLimit ? { background: 'rgba(218,41,28,.14)' } : undefined}>
          {/* Copy text + render preview — moved down from the header. The
              relative wrapper anchors the video error toast; the fixed
              preview panels are viewport-anchored and simply live here. */}
          <div className="relative flex items-center gap-1 pl-3">
            {/* Copy Button */}
            <button
              onClick={handleCopy}
              className={`tend-card-action ${isCopied ? 'is-ok' : ''}`}
              title="Copy to clipboard"
            >
              {isCopied ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
            {/* Preview Overlay Button — only visible when overlay is active and media is NOT video.
                Hidden for published items: it renders a NEW overlay, meaningless once published. */}
            {showImageOverlay && !currentMediaIsVideo && showEditorStage && (
              <button
                ref={previewButtonRef}
                onClick={async () => {
                  // Always re-render to capture latest changes (title, subheader, overlay config).
                  // In carousel mode, render the active slide via the slide-render endpoint
                  // so the preview shows what slide N will actually look like when published.
                  // In single mode, render the parent content as before.
                  const anchorRect = overlayContainerRef.current?.getBoundingClientRect();
                  setPreviewAnchor(anchorRect && anchorRect.width > 40
                    ? { width: anchorRect.width, top: anchorRect.top, left: anchorRect.left, right: anchorRect.right }
                    : null);
                  setIsRenderingPreview(true);
                  try {
                    const activeSlide = isCarouselMode ? slides[currentSlideIndex] : null;
                    let renderedUrl: string | undefined;

                    if (activeSlide) {
                      const slideResult = await tendApi.renderSlideOverlay(activeSlide.id, true);
                      if (slideResult.success) {
                        renderedUrl = slideResult.rendered_overlay_url;
                      } else if (slideResult.error) {
                        console.error('Slide render failed:', slideResult.error);
                      }
                    } else {
                      const config = currentOverlayConfig;
                      // Use the effective logo for rendering: if the user explicitly
                      // selected a logo it's stored in overlay_logo_url; otherwise
                      // the selectedLogo is just the auto-seeded brand default which
                      // shouldn't override display mode (icon/name fallback).
                      const hasExplicitLogo = !!effectiveLogoUrlRef.current;
                      const renderLogoUrl = hasExplicitLogo ? (selectedLogo?.url || '') : '';
                      const renderLogoKey = hasExplicitLogo ? (selectedLogo?.logoKey || 'icon_white') : 'icon_white';
                      const result = await tendApi.renderOverlay(
                        item.id,
                        config,
                        renderLogoUrl,
                        renderLogoKey,
                        logoDisplayMode,
                        brandName || '',
                        true, // sync
                        { title: editedTitle, subheader: editedSubheader, body: editedBody, category: editedCategory || categoryFallback },
                        { categoryBorderRadius, categoryBgColor },
                      );
                      if (result.success && result.rendered_url) {
                        renderedUrl = result.rendered_url;
                      }
                    }

                    if (renderedUrl) {
                      setPreviewUrl(renderedUrl);
                      setShowPreviewPopover(true);
                    }
                  } catch (err) {
                    console.error('Preview render failed:', err);
                  } finally {
                    setIsRenderingPreview(false);
                  }
                }}
                className="tend-card-action relative"
                title="Preview rendered overlay"
                disabled={isRenderingPreview}
              >
                {isRenderingPreview ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            )}
            {/* Video Render Button — only visible when media is video; hidden once published */}
            {showVideoOverlay && showEditorStage && (
              <button
                onClick={() => handleRenderVideo()}
                className={`tend-card-action relative ${renderedVideoUrl && !isRenderingVideo ? 'is-ok' : ''}`}
                title={isRenderingVideo ? "Rendering video..." : renderedVideoUrl ? "View rendered video" : "Render video with overlay"}
                disabled={isRenderingVideo}
              >
                {isRenderingVideo ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : renderedVideoUrl ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            )}
            {/* Video Render Error Toast */}
            {videoRenderError && (
              <div className="absolute bottom-full left-0 mb-1 bg-red-50 border border-red-200 text-red-700 text-xs px-2 py-1 rounded shadow-lg z-50 whitespace-nowrap">
                {videoRenderError}
              </div>
            )}
            {/* Preview Panel — floating TEND-ink card, draggable for
                side-by-side comparison against the source card */}
            {showPreviewPopover && previewUrl && (
              <DraggablePreviewPanel
                title="Rendered Overlay Preview"
                caption="This is the image that will be published"
                anchor={previewAnchor}
                onClose={() => setShowPreviewPopover(false)}
              >
                <img
                  src={previewUrl}
                  alt="Rendered overlay preview"
                  style={{ width: '100%', borderRadius: 12 }}
                />
              </DraggablePreviewPanel>
            )}
            {/* Video Preview Panel — same draggable ink card */}
            {showVideoPreview && renderedVideoUrl && (
              <DraggablePreviewPanel
                title="Rendered Video Preview"
                caption="This is the video that will be published"
                anchor={previewAnchor}
                onClose={() => setShowVideoPreview(false)}
                actions={
                  <button
                    type="button"
                    className="tend-btn tend-btn--icon tend-btn--icon-sm"
                    aria-label="Re-render video"
                    title="Re-render video"
                    onClick={() => {
                      setShowVideoPreview(false);
                      handleRenderVideo(true);
                    }}
                    style={{ flexShrink: 0 }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                }
              >
                <video
                  src={renderedVideoUrl.startsWith('http') ? renderedVideoUrl : `${REMOTION_SERVICE_URL}${renderedVideoUrl}`}
                  controls
                  autoPlay
                  loop
                  style={{ width: '100%', borderRadius: 12 }}
                />
              </DraggablePreviewPanel>
            )}
          </div>
          <button
            onClick={() => setDetailsOpen(!detailsOpen)}
            className="flex-1 min-w-0 px-3 py-2.5 flex items-center justify-between transition-colors hover:bg-white/[.06]"
          >
            <span className="tend-micro flex items-center gap-2" style={isOverLimit ? { color: 'var(--signal-rec-soft)' } : undefined}>
              <span>{fullPostCharCount}/{platformLimit} CHARS</span>
              {isOverLimit && (
                <span style={{ color: 'var(--signal-rec-soft)' }}>
                  OVER BY {fullPostCharCount - platformLimit}
                </span>
              )}
              <span style={{ opacity: .4 }}>·</span>
              <span>{localWordCount} WORDS</span>
            </span>
            <span className="tend-micro flex items-center gap-1">
              <svg
                className="w-4 h-4"
                style={{ transform: detailsOpen ? 'rotate(180deg)' : 'none', transition: 'transform .25s var(--ease-settle)' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </button>
        </div>

        {/* Expandable Content */}
        {detailsOpen && (
          <div className="px-4 py-3 space-y-3" style={{ borderTop: '1px solid rgba(255,255,255,.10)' }}>
            {/* Tactic — moved down from the header; carries the old Tactic
                Info sub-lines */}
            {item.tactic_id && (
              <div className="space-y-0.5">
                <div className="flex items-center justify-between">
                  <div className="tend-micro">TACTIC</div>
                  {/* Traceability for tactic tuning — CS/superadmin only
                      (the gateway hard-gates the endpoint too) */}
                  {isSuperAdmin && (
                    <button
                      type="button"
                      className="tend-micro"
                      style={{ opacity: .7 }}
                      onClick={openPromptModal}
                    >
                      View Prompt
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="text-sm font-medium" style={{ color: 'var(--text-2)' }}>
                    {isAgentHandOff ? 'Agent hand-off' : formatTacticName(item.tactic_id)}
                  </div>
                  {/* Tactic-first editing: edits apply to all FUTURE runs of
                      this tactic — this card's generated text doesn't change.
                      An agent hand-off (tactic_id "mcp:<format>") ran no
                      tactic; there is nothing to edit. */}
                  {brandSlug && !isAgentHandOff && (
                    <button
                      type="button"
                      onClick={() => setTacticEditOpen(true)}
                      className="p-0.5 rounded text-white/40 hover:text-white/80 transition-colors"
                      title="Edit tactic (applies to future runs)"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  )}
                </div>
                <div className="text-xs text-white/[.55]">
                  {isAgentHandOff ? `format ${(item.tactic_id || '').slice(4)}` : item.tactic_id}
                  {item.tactic_readiness && <> · {item.tactic_readiness}</>}
                  {item.tactic_image_mode && <> · {item.tactic_image_mode} image</>}
                </div>
              </div>
            )}

            {tacticEditOpen && brandSlug && item.tactic_id && !isAgentHandOff && (
              <TacticEditModal
                isOpen={true}
                onClose={() => setTacticEditOpen(false)}
                tacticId={item.tactic_id}
                platform={item.platform}
                brandSlug={brandSlug}
              />
            )}

            {promptModalOpen && (
              <TendModal
                title="Input prompt"
                subtitle={`${formatTacticName(item.tactic_id || '')} · ${item.platform}`}
                onClose={() => setPromptModalOpen(false)}
                width={680}
              >
                {promptData === 'loading' ? (
                  <span className="tend-micro" style={{ padding: '16px 0', textAlign: 'center', textTransform: 'none' }}>
                    Loading prompt…
                  </span>
                ) : !promptData || !promptData.available ? (
                  <span className="tend-micro" style={{ padding: '12px 0', textTransform: 'none', letterSpacing: 0.4 }}>
                    No prompt on record for this item — it wasn't generated by an
                    LLM run (video clips and pre-tracking content have none).
                  </span>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="tend-micro" style={{ textTransform: 'none', letterSpacing: 0.4 }}>
                        {promptData.prompt_tokens != null && <>{promptData.prompt_tokens} prompt tokens · </>}
                        {promptData.completion_tokens != null && <>{promptData.completion_tokens} out · </>}
                        {promptData.execution_time_ms != null && <>{(promptData.execution_time_ms / 1000).toFixed(1)}s · </>}
                        {promptData.created_at && new Date(promptData.created_at).toLocaleString()}
                      </span>
                      <button
                        type="button"
                        className="tend-btn tend-btn--glass"
                        style={{ padding: '4px 10px', fontSize: 12 }}
                        onClick={() => navigator.clipboard?.writeText(promptData.prompt || '')}
                      >
                        Copy
                      </button>
                    </div>
                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontSize: 12,
                        lineHeight: 1.55,
                        color: 'var(--text-2)',
                        background: 'rgba(255,255,255,.05)',
                        border: '1px solid rgba(255,255,255,.10)',
                        borderRadius: 12,
                        padding: 12,
                        maxHeight: '60vh',
                        overflowY: 'auto',
                        margin: 0,
                      }}
                    >
                      {promptData.prompt}
                    </pre>
                  </>
                )}
              </TendModal>
            )}

            {/* Publish attempts — the platform's actual error next to the
                content it rejected. State-tinted (amber = retrying/paused,
                green = a retry healed it, red = final): the transient ones
                don't scream, but every attempt stays on record. */}
            {publishFailures && publishFailures.length > 0 && (() => {
              const head = PUBLISH_FAILURE_STYLE[publishFailureHeadState(publishFailures)];
              return (
              <div className="p-3 rounded-xl" style={{ background: `${head.color}1A`, border: `1px solid ${head.color}59` }}>
                <div className="flex items-center gap-2 font-medium text-sm mb-2" style={{ color: head.color }}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {head.heading}
                </div>
                <ul className="text-sm space-y-1.5" style={{ color: 'var(--text-2)' }}>
                  {publishFailures.map((f, i) => {
                    // Rows arrive newest first; only the newest earns the
                    // "failed after N attempts" tail (attempts is ticket-level).
                    const view = describePublishFailure({ ...f, is_final_attempt: i === 0 });
                    return (
                    <li key={f.history_id ?? `held-${i}`} className="flex items-start gap-2">
                      <span className="mt-0.5" style={{ color: view.color }}>•</span>
                      <span className="min-w-0">
                        <span className="text-white/[.55]">
                          {f.created_at
                            ? new Date(f.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                            : '—'}
                          {' · '}{f.platforms.join(', ')} —{' '}
                        </span>
                        <span style={view.muteError ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}>
                          {f.error_message || 'No error detail recorded'}{publishFailureCodeTag(f)}
                        </span>
                        {view.suffix && <span style={{ color: view.color }}>{view.suffix}</span>}
                        {/* The network's own details, or our note for a bare
                            code it sent without one (Threads 379). */}
                        {(() => {
                          const detail = publishFailureDetailLine(f);
                          return detail ? <span className="block text-xs text-white/[.45]">{detail}</span> : null;
                        })()}
                      </span>
                    </li>
                    );
                  })}
                </ul>
              </div>
              );
            })()}

            {/* Publishing Notes from AI */}
            {item.publish_warnings && (() => {
              const warnings = typeof item.publish_warnings === 'string'
                ? JSON.parse(item.publish_warnings)
                : item.publish_warnings;
              return warnings.length > 0 ? (
                <div className="p-3 rounded-xl" style={{ background: 'rgba(255,162,62,.10)', border: '1px solid rgba(255,162,62,.35)' }}>
                  <div className="flex items-center gap-2 font-medium text-sm mb-2" style={{ color: 'var(--signal-feed)' }}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Publishing Notes
                  </div>
                  <ul className="text-sm space-y-1" style={{ color: 'var(--text-2)' }}>
                    {warnings.map((warning: string, idx: number) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="mt-0.5" style={{ color: 'var(--signal-feed)' }}>•</span>
                        <span>{warning}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null;
            })()}

            {/* Spoken script — used by the recording teleprompter and the
                future voiceover / faceless-video pipeline. Single-post
                edits write to item.script; carousel edits write to the
                current slide's script. */}
            <div className="pt-1">
              <div className="flex items-center justify-between mb-1">
                <div className="tend-micro">
                  Script
                  {isCarouselMode && slides[currentSlideIndex] && (
                    <span className="ml-1 text-white/[.45]">
                      (slide {slides[currentSlideIndex].slide_number})
                    </span>
                  )}
                </div>
                {editedScript && (
                  <span className="text-[10px] text-white/[.45]">
                    {editedScript.trim().split(/\s+/).filter(Boolean).length} words
                  </span>
                )}
              </div>
              <div
                ref={(el) => {
                  (scriptRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                  // Hydrate once on mount — after that, the user owns the
                  // DOM (contentEditable) and the slide-sync effect handles
                  // programmatic updates imperatively.
                  if (el && !el.textContent && editedScript) {
                    el.innerText = editedScript;
                  }
                }}
                contentEditable
                suppressContentEditableWarning
                onInput={handleScriptInput}
                data-placeholder="Optional spoken script — read this on camera in the teleprompter."
                className="text-sm text-white/[.92] whitespace-pre-wrap leading-relaxed
                           min-h-[3.5rem] focus:outline-none focus:ring-2 focus:ring-[rgba(25,144,255,.45)]
                           rounded p-2 cursor-text transition-colors
                           bg-[rgba(20,26,40,.55)] border border-white/[.22]
                           empty:before:content-[attr(data-placeholder)]
                           empty:before:text-white/[.4] empty:before:italic"
              />
            </div>

            {/* (Toggled-off link/hashtags no longer relocate here — they
                stay in the card's Link/Tags control row, just dimmed.) */}

            {/* Hidden Photo Attribution - in details when disabled */}
            {!photoAttributionEnabled && editedPhotoAttribution && (
              <PhotoAttributionCallout
                attribution={editedPhotoAttribution}
                enabled={photoAttributionEnabled}
                onToggle={setPhotoAttributionEnabled}
                onAttributionChange={handleAttributionChange}
              />
            )}

            {/* Source Info */}
            {item.source_author && (
              <div className="pt-2 border-t border-white/[.12] space-y-1">
                <div className="tend-micro">Source Info</div>
                <div className="text-xs text-white/[.72]">Author: {item.source_author}</div>
              </div>
            )}

            {/* Content Classification */}
            {(item.source_section || item.source_category) && (
              <div className="pt-2 border-t border-white/[.12] space-y-1">
                <div className="tend-micro">Content Classification</div>
                {item.source_section && (
                  <div className="text-xs text-white/[.72]">Section: {item.source_section}</div>
                )}
                {item.source_category && (
                  <div className="text-xs text-white/[.72]">Category: {item.source_category}</div>
                )}
              </div>
            )}

            {/* Video Clip Info (Vizard) */}
            {item.content_format === 'video_clip' && (
              <div className="pt-2 border-t border-white/[.12] space-y-2">
                <div className="tend-micro flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Video Clip Info
                </div>

                {/* Duration */}
                {item.estimated_read_time_seconds && (
                  <div className="text-xs text-white/[.72]">
                    Duration: {item.estimated_read_time_seconds}s
                  </div>
                )}

                {/* Viral Score */}
                {item.engagement_score && (
                  <div className="text-xs text-white/[.72] flex items-center gap-1">
                    Viral Score:
                    <span className={`font-medium ${
                      item.engagement_score >= 9.5 ? 'text-[#44d36a]' :
                      item.engagement_score >= 9 ? 'text-[#6FB6FF]' :
                      'text-white/[.72]'
                    }`}>
                      {item.engagement_score}/10
                    </span>
                  </div>
                )}

                {/* Vizard Metadata */}
                {item.platform_metadata?.vizard && (
                  <>
                    {item.platform_metadata.vizard.viralReason && (
                      <div className="text-xs text-white/[.72]">
                        <span className="font-medium">Why it's viral:</span>
                        <p className="mt-1 text-white/[.55] italic">
                          {item.platform_metadata.vizard.viralReason}
                        </p>
                      </div>
                    )}
                    {item.platform_metadata.vizard.relatedTopic && (
                      <div className="text-xs text-white/[.72]">
                        <span className="font-medium">Topics:</span>{' '}
                        {(() => {
                          try {
                            const topics = JSON.parse(item.platform_metadata.vizard.relatedTopic);
                            return Array.isArray(topics) ? topics.join(', ') : item.platform_metadata.vizard.relatedTopic;
                          } catch {
                            return item.platform_metadata.vizard.relatedTopic;
                          }
                        })()}
                      </div>
                    )}
                    {item.platform_metadata.vizard.clipEditorUrl && (
                      <a
                        href={item.platform_metadata.vizard.clipEditorUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[#6FB6FF] hover:underline flex items-center gap-1"
                      >
                        Edit in Vizard
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    )}
                  </>
                )}

                {/* Transcript */}
                {item.transcript && (
                  <div className="text-xs">
                    <div className="font-medium text-white/[.55] mb-1">Transcript:</div>
                    <div className="max-h-32 overflow-y-auto p-2 bg-[rgba(20,26,40,.55)] rounded border border-white/[.22] text-white/[.72] whitespace-pre-wrap">
                      {item.transcript}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Archive Button - SuperAdmin only */}
            {isSuperAdmin && (onArchive || onUnarchive) && (
              <div className="mt-4 pt-4 border-t border-white/[.12]">
                {item.is_archived ? (
                  <button
                    onClick={() => onUnarchive?.(item.id)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2
                      bg-green-500/20 border border-green-500/50 rounded-lg text-[#44d36a]
                      hover:bg-green-500/30 transition-colors text-sm font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Restore
                  </button>
                ) : (
                  <button
                    onClick={() => onArchive?.(item.id)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2
                      bg-yellow-500/20 border border-yellow-500/50 rounded-lg text-[#FFA23E]
                      hover:bg-yellow-500/30 transition-colors text-sm font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    Archive
                  </button>
                )}
              </div>
            )}

            {/* Publish Now moved into the unified Smart Publishing button's
                dropdown (Phase 4) — available from every non-published state */}
          </div>
        )}
      </div>

      {/* Template Management Modal */}
      {showTemplateModal && brandId && (
        <TemplateManagementModal
          isOpen={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          contentItem={{
            ...item,
            // Use the currently selected image from the carousel
            primary_image_url: localImages[selectedImageIndex] || item.primary_image_url
          }}
          brandId={brandId}
          brandName={brandName || 'Brand'}
          brandDefaultLogo={selectedLogo || brandDefaultLogo || null}
          brandLogoDisplayMode={brandLogoDisplayMode}
          currentTemplateId={activeTemplateId}
          defaultTemplateId={defaultTemplateId}
          isSuperAdmin={isSuperAdmin}
          categoryBorderRadius={categoryBorderRadius}
          categoryBgColor={categoryBgColor}
          currentOverlayConfig={
            // The complete config snapshot — never an inline field list. Every
            // key missing here is silently dropped from saved templates (this
            // literal once lagged the memo by 59 keys: line height, shadow,
            // fonts, design elements...). Extend the memo, not this prop.
            currentOverlayConfig
          }
          currentTitle={editedTitle}
          currentSubheader={editedSubheader}
          currentBody={editedBody}
          onSelectTemplate={(templateId, config) => {
            // Apply the selected template config to current card
            // -1 means "Current Settings" - no saved template active
            // Mark that we're applying a template to prevent auto-save interference
            if (templateId !== -1) {
              isApplyingTemplateRef.current = true;
              setTimeout(() => { isApplyingTemplateRef.current = false; }, 600);
            }
            // The apply path persists explicitly — the applied state is not a
            // user overlay edit. Disarm so auto-save adopts it as baseline.
            overlayUserTouchedRef.current = false;
            // Template-carried logo copies down on apply (explicit act —
            // overwrites the content's current selection; the card's
            // LogoSelector can re-override afterwards). "Current Settings"
            // (-1) participates too: its config mirrors the card's own logo
            // (no-op normally) but carries picks made on its preview in the
            // template modal — excluding -1 silently dropped those.
            // A display-mode-only config (universal templates carry
            // logoDisplayMode but never a logo file) restyles the logo
            // WITHOUT touching which logo the content shows — treating it
            // as "has a logo" used to null the content's columns and drop
            // renders to the initial-letter fallback.
            const templateCarriesLogoFile = !!config.logoUrl;
            const appliedLogo: Logo | null = templateCarriesLogoFile
              ? ({ url: config.logoUrl, logoKey: config.logoKey || 'icon_white', displayName: '' } as Logo)
              : null;
            const appliedDisplayMode: LogoDisplayMode = isLogoDisplayMode(config.logoDisplayMode)
              ? config.logoDisplayMode
              : logoDisplayMode;
            if (templateCarriesLogoFile) {
              setSelectedLogo(appliedLogo);
              setLogoDisplayMode(appliedDisplayMode);
              effectiveLogoUrlRef.current = appliedLogo?.url || null;
              hasPersistedInitialLogo.current = true;
              tendApi.updateTendV2Content(item.id, {
                overlay_logo_url: appliedLogo?.url || null,
                overlay_logo_key: appliedLogo?.logoKey || null,
                overlay_logo_display_mode: appliedDisplayMode,
              });
            } else if (isLogoDisplayMode(config.logoDisplayMode)) {
              setLogoDisplayMode(appliedDisplayMode);
              tendApi.updateTendV2Content(item.id, {
                overlay_logo_display_mode: appliedDisplayMode,
              });
            }
            setActiveTemplateId(templateId === -1 ? null : templateId);
            setShowOverlayLogo(config.showOverlayLogo ?? true);
            setShowOverlayTitle(config.showOverlayTitle ?? true);
            setShowOverlaySubheader(config.showOverlaySubheader ?? false);
            setShowOverlayBody(config.showOverlayBody ?? false);
            setShowOverlayCategory(config.showOverlayCategory ?? false);
            setShowOverlayGradient(config.showOverlayGradient ?? true);
            setLogoPosition(config.logoPosition || 'bottom-center');
            setTitlePosition(config.titlePosition || 'bottom-center');
            setSubheaderPosition(config.subheaderPosition || 'bottom-center');
            setBodyPosition(config.bodyPosition || 'bottom-center');
            setCategoryPosition(config.categoryPosition || 'top-left');
            setTitleFontSize(config.titleFontSize || 16);
            setSubheaderFontSize(config.subheaderFontSize || 12);
            setBodyFontSize(config.bodyFontSize || 12);
            setLogoScale(config.logoScale || 1.0);
            setCategoryBgColor(config.categoryBgColor || brandCategoryDefaultsRef.current.color);
            setCategoryBorderRadius(
              typeof config.categoryBorderRadius === 'number'
                ? config.categoryBorderRadius
                : brandCategoryDefaultsRef.current.radius
            );
            categoryConfigOverrideRef.current = {
              color: !!config.categoryBgColor,
              radius: typeof config.categoryBorderRadius === 'number',
            };
            setGradientDirection(config.gradientDirection || 'up');
            setGradientColor(config.gradientColor || '#000000');
            setGradientOpacity(config.gradientOpacity ?? 70);
            setTitleBgEnabled(config.titleBgEnabled ?? false);
            setTitleBgColor(config.titleBgColor || '#000000');
            setTitleBgOpacity(config.titleBgOpacity ?? 80);
            setTitleTextColor(config.titleTextColor || '#ffffff');
            setSubheaderBgEnabled(config.subheaderBgEnabled ?? false);
            setSubheaderBgColor(config.subheaderBgColor || '#000000');
            setSubheaderBgOpacity(config.subheaderBgOpacity ?? 80);
            setSubheaderTextColor(config.subheaderTextColor || '#ffffff');
            setBodyBgEnabled(config.bodyBgEnabled ?? false);
            setBodyBgColor(config.bodyBgColor || '#000000');
            setBodyBgOpacity(config.bodyBgOpacity ?? 80);
            setBodyTextColor(config.bodyTextColor || '#ffffff');
            setBorderEnabled(config.borderEnabled ?? false);
            setBorderColor(config.borderColor || '#ffffff');
            setBorderThickness(config.borderThickness ?? 4);

            // Per-element extended styling (bold, italic, underline, strike,
            // text case, per-box border, vertical offset, width, width-offset).
            const nextTitleStyleApplied = hydrateTextElementStyle(config, 'title');
            const nextSubheaderStyleApplied = hydrateTextElementStyle(config, 'subheader');
            const nextBodyStyleApplied = hydrateTextElementStyle(config, 'body');
            setTitleStyle(nextTitleStyleApplied);
            setSubheaderStyle(nextSubheaderStyleApplied);
            setBodyStyle(nextBodyStyleApplied);
            const nextCategoryFontApplied = config.categoryFontFamily ?? null;
            setCategoryFontFamily(nextCategoryFontApplied);

            // Design elements: deep-clone so subsequent edits on the applied
            // target don't mutate the cached template config.
            const appliedDesignElements: PlacedDesignElement[] = Array.isArray(config.designElements)
              ? config.designElements.map((el) => ({ ...el }))
              : [];
            setDesignElements(appliedDesignElements);

            // Save to backend (carousel mode saves to slide, single mode saves to content)
            const fullConfig: OverlayTemplateConfig = {
              showOverlayLogo: config.showOverlayLogo ?? true,
              showOverlayTitle: config.showOverlayTitle ?? true,
              showOverlaySubheader: config.showOverlaySubheader ?? false,
              showOverlayBody: config.showOverlayBody ?? false,
              showOverlayCategory: config.showOverlayCategory ?? false,
              showOverlayGradient: config.showOverlayGradient ?? true,
              logoPosition: config.logoPosition || 'bottom-center',
              titlePosition: config.titlePosition || 'bottom-center',
              subheaderPosition: config.subheaderPosition || 'bottom-center',
              bodyPosition: config.bodyPosition || 'bottom-center',
              categoryPosition: config.categoryPosition || 'top-left',
              titleFontSize: config.titleFontSize || 16,
              subheaderFontSize: config.subheaderFontSize || 12,
              bodyFontSize: config.bodyFontSize || 12,
              logoScale: config.logoScale || 1.0,
              categoryScale: config.categoryScale ?? 1.0,
              logoGap: config.logoGap ?? null,
              categoryGap: config.categoryGap ?? null,
              categoryBgColor: config.categoryBgColor || categoryBgColor,
              // Sparse: only persist a radius the template actually carried,
              // so brand-default resolution stays live otherwise (matches
              // categoryConfigOverrideRef semantics above)
              categoryBorderRadius: typeof config.categoryBorderRadius === 'number' ? config.categoryBorderRadius : undefined,
              gradientDirection: config.gradientDirection || 'up',
              gradientColor: config.gradientColor || '#000000',
              gradientOpacity: config.gradientOpacity ?? 70,
              titleBgEnabled: config.titleBgEnabled ?? false,
              titleBgColor: config.titleBgColor || '#000000',
              titleBgOpacity: config.titleBgOpacity ?? 80,
              titleTextColor: config.titleTextColor || '#ffffff',
              subheaderBgEnabled: config.subheaderBgEnabled ?? false,
              subheaderBgColor: config.subheaderBgColor || '#000000',
              subheaderBgOpacity: config.subheaderBgOpacity ?? 80,
              subheaderTextColor: config.subheaderTextColor || '#ffffff',
              bodyBgEnabled: config.bodyBgEnabled ?? false,
              bodyBgColor: config.bodyBgColor || '#000000',
              bodyBgOpacity: config.bodyBgOpacity ?? 80,
              bodyTextColor: config.bodyTextColor || '#ffffff',
              borderEnabled: config.borderEnabled ?? false,
              borderColor: config.borderColor || '#ffffff',
              borderThickness: config.borderThickness ?? 4,
              // Per-element extended styling (Title)
              titleBold: nextTitleStyleApplied.bold,
              titleItalic: nextTitleStyleApplied.italic,
              titleUnderline: nextTitleStyleApplied.underline,
              titleStrikethrough: nextTitleStyleApplied.strikethrough,
              titleShadow: nextTitleStyleApplied.shadow,
              titleTextCase: nextTitleStyleApplied.textCase,
              titleBorderEnabled: nextTitleStyleApplied.borderEnabled,
              titleBorderColor: nextTitleStyleApplied.borderColor,
              titleBorderThickness: nextTitleStyleApplied.borderThickness,
              titleBorderRadius: nextTitleStyleApplied.borderRadius,
              titleGap: nextTitleStyleApplied.gap ?? null,
              titleWidth: nextTitleStyleApplied.width,
              titleWidthOffset: nextTitleStyleApplied.widthOffset,
              // Per-element extended styling (Subheader)
              subheaderBold: nextSubheaderStyleApplied.bold,
              subheaderItalic: nextSubheaderStyleApplied.italic,
              subheaderUnderline: nextSubheaderStyleApplied.underline,
              subheaderStrikethrough: nextSubheaderStyleApplied.strikethrough,
              subheaderShadow: nextSubheaderStyleApplied.shadow,
              subheaderTextCase: nextSubheaderStyleApplied.textCase,
              subheaderBorderEnabled: nextSubheaderStyleApplied.borderEnabled,
              subheaderBorderColor: nextSubheaderStyleApplied.borderColor,
              subheaderBorderThickness: nextSubheaderStyleApplied.borderThickness,
              subheaderBorderRadius: nextSubheaderStyleApplied.borderRadius,
              subheaderGap: nextSubheaderStyleApplied.gap ?? null,
              subheaderWidth: nextSubheaderStyleApplied.width,
              subheaderWidthOffset: nextSubheaderStyleApplied.widthOffset,
              // Per-element extended styling (Body)
              bodyBold: nextBodyStyleApplied.bold,
              bodyItalic: nextBodyStyleApplied.italic,
              bodyUnderline: nextBodyStyleApplied.underline,
              bodyStrikethrough: nextBodyStyleApplied.strikethrough,
              bodyShadow: nextBodyStyleApplied.shadow,
              bodyTextCase: nextBodyStyleApplied.textCase,
              bodyBorderEnabled: nextBodyStyleApplied.borderEnabled,
              bodyBorderColor: nextBodyStyleApplied.borderColor,
              bodyBorderThickness: nextBodyStyleApplied.borderThickness,
              bodyBorderRadius: nextBodyStyleApplied.borderRadius,
              bodyGap: nextBodyStyleApplied.gap ?? null,
              bodyWidth: nextBodyStyleApplied.width,
              bodyWidthOffset: nextBodyStyleApplied.widthOffset,
              // Font Library — sparse keys preserve cascade inheritance
              titleFontFamily: nextTitleStyleApplied.fontFamily ?? undefined,
              subheaderFontFamily: nextSubheaderStyleApplied.fontFamily ?? undefined,
              bodyFontFamily: nextBodyStyleApplied.fontFamily ?? undefined,
              titleLetterSpacing: nextTitleStyleApplied.letterSpacing || undefined,
              subheaderLetterSpacing: nextSubheaderStyleApplied.letterSpacing || undefined,
              bodyLetterSpacing: nextBodyStyleApplied.letterSpacing || undefined,
              titleLineHeight: nextTitleStyleApplied.lineHeight ?? undefined,
              subheaderLineHeight: nextSubheaderStyleApplied.lineHeight ?? undefined,
              bodyLineHeight: nextBodyStyleApplied.lineHeight ?? undefined,
              categoryFontFamily: nextCategoryFontApplied ?? undefined,
              captionModeEnabled,
              // Image fit/crop is media-specific (focal point of THIS photo),
              // never adopted from a template. Mirror the card's live values —
              // the image_fit_* columns are the source of truth and loaders
              // fall back column → config, so config must agree with state.
              imageFitMode,
              imageFitAnchor,
              imageFitWidth,
              imageFillPositionX,
              imageFillPositionY,
              // Template logo wins on apply (setSelectedLogo above hasn't
              // committed yet, so read the applied values, not state)
              logoUrl: templateCarriesLogoFile ? appliedLogo?.url : selectedLogo?.url,
              logoKey: templateCarriesLogoFile ? appliedLogo?.logoKey : selectedLogo?.logoKey,
              logoDisplayMode: appliedDisplayMode,
              designElements: appliedDesignElements,
            };

            const effectiveTemplateId = templateId === -1 ? null : templateId;

            if (isCarouselMode && slides[currentSlideIndex]) {
              const slide = slides[currentSlideIndex];
              const slideIndex = currentSlideIndex;

              // Optimistic update
              setSlides(prev => prev.map((s, i) =>
                i === slideIndex ? { ...s, overlay_template_id: effectiveTemplateId ?? undefined, overlay_config: fullConfig } : s
              ));

              // Save to backend
              carouselApi.updateSlide(slide.id, {
                overlay_template_id: effectiveTemplateId,
                overlay_config: fullConfig
              }).catch(err => console.error('Failed to save template to slide:', err));
            } else {
              // Single mode - save to content
              tendApi.updateTendV2Content(item.id, {
                overlay_template_id: effectiveTemplateId,
                overlay_config: fullConfig
              });
            }
          }}
          onSetDefault={(templateId) => {
            setDefaultTemplateId(templateId);
          }}
        />
      )}
      {/* Picture-in-Picture teleprompter — only mounted while open so we
          don't request a PIP window until the user (or a tab switch)
          actually wants one. */}
      {teleprompterOpen && (
        <RecordingTeleprompter
          script={editedScript}
          onScriptChange={handleScriptTextChange}
          label={
            isCarouselMode && slides[currentSlideIndex]
              ? `Slide ${slides[currentSlideIndex].slide_number} of ${slides.length}`
              : undefined
          }
          onStageContainerReady={setPipStageContainer}
          onClose={closeTeleprompter}
        />
      )}
    </div>

    {/* The Content Media Gallery for this post (the current slide in carousel
        mode): one click selects (the post's choice), hold-and-drag reorders
        THIS post's media (it stops following the original's order), delete
        = remove from this post, add = the shared picker. */}
    {galleryOpen && (
      <MediaGalleryModal
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        anchorEl={cardRootRef.current}
        title={isCarouselMode ? `Slide ${currentSlideIndex + 1} Media` : 'Post Media'}
        pool={localImages}
        selectedUrl={currentImageUrl || null}
        onSelect={(url) => { const i = localImages.indexOf(url); if (i >= 0) navigateToImage(i); }}
        onReorder={async (urls) => {
          setLocalImages(urls);
          const keep = urls.indexOf(currentImageUrl);
          if (keep >= 0) selectMedia(keep, { images: urls });
          try {
            if (isCarouselMode && slides[currentSlideIndex]) {
              const slide = slides[currentSlideIndex];
              setSlides((prev) => prev.map((s, i) => (i === currentSlideIndex ? { ...s, media_urls: urls, media_order_custom: true } : s)));
              await carouselApi.updateSlide(slide.id, { media_urls: urls, media_order_custom: true });
            } else {
              await tendApi.updateTendV2Content(item.id, { media_urls: urls, media_order_custom: true });
            }
          } catch (error) {
            console.error('Error saving the media order:', error);
          }
        }}
        onDelete={(url) => { const i = localImages.indexOf(url); if (i >= 0) handleDeleteImage(i); }}
        deleteTitle="Remove from this post"
        onAddPexels={handlePexelsMediaSelect}
        onAddUpload={() => fileInputRef.current?.click()}
        isUploading={isUploading}
        instanceId={`gallery-tend-${item.id}`}
        tileAspect={platformConfig.imageMode === 'fixed' ? platformConfig.aspectRatio : undefined}
        tileFit={platformConfig.imageMode === 'fixed'
          ? { mode: imageFitMode, anchor: imageFitAnchor, width: imageFitWidth, positionX: imageFillPositionX, positionY: imageFillPositionY }
          : undefined}
      />
    )}
    </CornerControlsProvider>
  );
};

export default TendV2ContentCard;
